// Canvas — Ship 3 v1 (Apr 30, 2026)
// Mobile-first canvasser map. Mapbox GL JS via CDN. Knock pins rendered by lifecycle
// state. Pin tap → bottom sheet with 4 outcome buttons (No Answer / Contact Made /
// Rent / App). Manager-tier can drop pins via long-press / button.
//
// Visual reference: canvasser-mobile-prototype.html, canvasser-overview-prototype.html
// Spec: memory/features/canvassing-tool.md (Pin lifecycle colors + Activity Logging UX)
//
// Ship 4 (Apr 30, 2026): storm zone overlay added — last 90 days of NEXRAD hail
// signatures with Mapbox clustering, colored by aggregated max hail size.
//
// Jul 12, 2026: NWS LSR *wind* layer added to the storm panel — ground-truth
// measured/estimated gust reports (speed reports only; the ~1,850 no-speed
// "TSTM WND DMG" rows are deliberately excluded server-side per Greg) with a
// client-side mph floor slider (30–80, default 50). Blue/violet palette so wind
// never masquerades as hail. RPC: get_storm_lsr_wind_geojson.
//
// What's NOT in v1 yet (follow-ups):
// - Offline queue (IndexedDB)
// - Voice notes + photos
// - Address lookup search bar  (parcels + Mapbox geocoder)
// - Lasso assignment (manager tool)
// - GPS audit trail (Ship 6, after legal review)

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase, SUPABASE_URL } from "../lib/supabase";
import { fetchEdge } from "../lib/edgeError";
import { submitVisit, flushQueue, startBackgroundSync, newClientUuid } from "../lib/canvasOfflineQueue";
import { useCanvasShift } from "../lib/canvasShift";
import { useMyLocation } from "../lib/geo";
import { LocationBanner } from "../components/canvas/LocationHelp";
import { useVisualViewportPin } from "../lib/useVisualViewportPin";
import MediaExtrasRow, { type CapturedMedia, extForBlob, compressImage } from "../components/canvas/MediaExtrasRow";
import VisitMediaInline from "../components/canvas/VisitMediaInline";
import PinIntelSection, { titleCaseOwner } from "../components/canvas/PinIntelSection";
import { PinNeighborhoodInsurance } from "../components/claims/NeighborhoodInsurance";
import DropSessionReviewModal from "../components/canvas/DropSessionReviewModal";

// Mapbox GL is loaded via CDN in index.html — typed as `any` here to avoid a
// dependency on @types/mapbox-gl while we evaluate the right packaging path.
declare const mapboxgl: any;

// ─── Pin lifecycle colors (matches spec + Crew app palette) ──────────────
// 'lead' = drone report approved + RM pursuing HO to call the carrier.
// The "Claim called in" action button on the deal flips the deal into the
// claims phase (waiting_on_claims / assign_roof_specialist / reinspection_*),
// which moves the pin to claim_called.
//
// 'open' shifted from Bardin orange #f46530 to a lighter #fb923c so 'lead'
// (now Bardin orange) is the most visually-loud state on the map.
const STATE_STYLE: Record<string, { color: string; outline?: string; label: string }> = {
  open:           { color: "#fb923c",                       label: "Open" },
  // knocked was #fbbf24 amber-yellow — too close to the warm/orange spectrum
  // (open #fb923c, lead #f46530, appt_scheduled was #ff9e7a). Switched to blue-500
  // #3b82f6 (May 11, 2026 fix #6) — distinctly cool, separates "no answer
  // pending follow-up" cleanly from any orange/yellow state. Different enough
  // from cyan claim_called (#06b6d4) to read as its own color.
  knocked:        { color: "#3b82f6",                       label: "Knocked" },
  contacted:      { color: "#9333ea",                       label: "Contacted" },
  rent:           { color: "#94a3b8", outline: "#64748b",   label: "Rent" },
  // appt_scheduled was #ff9e7a peach — too close to open's #fb923c orange on
  // the map at a glance. Switched to pink-500 #ec4899 (May 11, 2026) — distinct
  // from every other state (open/lead orange, knocked blue, contacted purple,
  // claim cyan, built green) and contrasts cleanly with the gold warm-lead halo.
  appt_scheduled: { color: "#ec4899",                       label: "Appt set" },
  lead:           { color: "#f46530", outline: "#7c2d12",   label: "Lead" },
  claim_called:   { color: "#06b6d4",                       label: "Claim" },
  built:          { color: "#16a34a",                       label: "Built" },
  legacy_client:  { color: "#ffffff", outline: "#0d9488",   label: "Past client" },
  do_not_knock:   { color: "#475569",                       label: "Do not knock" },
  wrong_address:  { color: "#94a3b8",                       label: "Wrong addr" },
  not_home_3x:    { color: "#cbd5e1",                       label: "Not home 3×" },
};

// Below this zoom the map runs in OVERVIEW mode: pin visuals come from the
// server-aggregated canvas_pin_overview_cells RPC (one small call) instead of
// the row-level bbox fetch. Added Sep 9, 2026 after a metro-zoom map open was
// measured paging ~40 × 1,000-row knock_pins requests (the whole 37.5k-pin
// table) — the single heaviest PostgREST consumer on the project and a
// contributor to the Sep 8 disk-I/O starvation. At >= this zoom the existing
// row fetch + client clustering + DOM markers behave exactly as before.
const DETAIL_MIN_ZOOM = 11.5;

// ── Bubble density is decided in SCREEN pixels, not degrees (Oct 12, 2026) ──
// Greg (phone screenshots): below street zoom the map vanished under hundreds
// of overlapping count bubbles, while the same views were fine on a desktop.
// Cause: the overview grid split the viewport into a fixed ~22 cells across
// no matter the screen (17pt apart on a phone vs 62px on a 1400px desktop),
// the detail cluster radius was the same 50px everywhere, and bubbles ran to
// 64px wide. Now BOTH bubble layers are Mapbox clusters whose merge radius —
// which is the minimum spacing between bubbles — comes from the map's pixel
// width, and a phone's bubbles are drawn smaller than its merge radius so two
// can never overlap. Fewer, well-separated bubbles; the map shows through.
interface BubbleTuning {
  narrow: boolean;
  clusterRadius: number;   // detail clusters, DETAIL_MIN_ZOOM ≤ z < 14
  overviewRadius: number;  // overview bubbles, z < DETAIL_MIN_ZOOM
  radiusSteps: (countExpr: any[]) => any[]; // circle-radius by pin count
  textSize: number;
}
function bubbleTuning(map: any): BubbleTuning {
  const w = Number(map?.getContainer?.()?.clientWidth) || window.innerWidth || 1024;
  const narrow = w < 900; // phones + portrait tablets
  if (narrow) {
    return {
      narrow,
      clusterRadius: 72,
      overviewRadius: 96,
      // max diameter 48px (+2px stroke) sits under the 72px merge radius
      radiusSteps: (c) => ["step", c, 12, 10, 15, 50, 18, 200, 21, 1000, 24],
      textSize: 12,
    };
  }
  return {
    narrow,
    clusterRadius: 50, // unchanged — the desktop view was already fine
    overviewRadius: 72,
    radiusSteps: (c) => ["step", c, 16, 10, 20, 50, 24, 200, 28, 1000, 32],
    textSize: 13,
  };
}

// Most-advanced state present wins the bubble color. ONE expression shared by
// the detail clusters and the overview bubbles so a bubble reads the same at
// every zoom. Order matters — built beats claim beats lead beats appt… —
// because once a deal is built it's the strongest signal for the canvasser.
// Both sources carry the same n_* keys: the detail source derives them per
// pin, the overview cells emit them per cell, and each sums them per cluster
// through clusterProperties.
const BUBBLE_COLOR_EXPR: any[] = [
  "case",
  [">", ["get", "n_built"], 0],     STATE_STYLE.built.color,
  [">", ["get", "n_claim"], 0],     STATE_STYLE.claim_called.color,
  [">", ["get", "n_lead"], 0],      STATE_STYLE.lead.color,
  [">", ["get", "n_appt"], 0],      STATE_STYLE.appt_scheduled.color,
  [">", ["get", "n_contacted"], 0], STATE_STYLE.contacted.color,
  [">", ["get", "n_knocked"], 0],   STATE_STYLE.knocked.color,
  STATE_STYLE.open.color,
];

// "2.3k"-style count label, matching supercluster's point_count_abbreviated
// (which the detail clusters show natively) so the two layers read alike.
const bubbleCountLabel = (countExpr: any[]): any[] => [
  "case",
  [">=", countExpr, 10000],
  ["concat", ["number-format", ["/", countExpr, 1000], { "max-fraction-digits": 0 }], "k"],
  [">=", countExpr, 1000],
  ["concat", ["number-format", ["/", countExpr, 1000], { "max-fraction-digits": 1 }], "k"],
  ["to-string", countExpr],
];

// ── Storm-area pin filter helpers (Sep 28, 2026) ───────────────────────────
// "Inside a storm" = within the 1-mile halo the map draws around every storm
// feature (radar signature / NWS hail / NWS wind — see the storm-buffer layers'
// circle-radius calibration). Features are bucketed into a ~1-mile lat/lon
// grid so a pin only compares against the handful of features in its
// neighbourhood instead of all ~13k radar cells in a 365-day window.
const STORM_BUFFER_M = 1609.34;                    // 1 statute mile
const M_PER_DEG_LAT = 111194.9;                    // 2π·6371 km / 360
const STORM_GRID_DEG = STORM_BUFFER_M / M_PER_DEG_LAT; // ≈ 0.01447° (1 mi of latitude)

interface StormPointIndex {
  cells: Map<string, Array<[number, number]>>; // "row:col" → [lat, lon][]
  size: number;                                 // total features indexed
}

function buildStormPointIndex(collections: unknown[]): StormPointIndex {
  const cells = new Map<string, Array<[number, number]>>();
  let size = 0;
  for (const fc of collections) {
    const feats = (fc as any)?.features;
    if (!Array.isArray(feats)) continue;
    for (const f of feats) {
      const c = f?.geometry?.coordinates;
      if (!Array.isArray(c) || c.length < 2) continue;
      const lon = Number(c[0]);
      const lat = Number(c[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const key = `${Math.floor(lat / STORM_GRID_DEG)}:${Math.floor(lon / STORM_GRID_DEG)}`;
      let arr = cells.get(key);
      if (!arr) { arr = []; cells.set(key, arr); }
      arr.push([lat, lon]);
      size++;
    }
  }
  return { cells, size };
}

// True when at least one indexed storm feature lies within radiusM of
// (lat, lon). Equirectangular distance — exact enough at 1-mile scale.
// The lon-axis grid cells are shorter than a mile (1° lon ≈ 0.83 × 1° lat at
// Georgia latitudes), so the column span is widened by cos(lat) so no feature
// inside the radius can hide two columns away.
function stormIndexHasWithin(idx: StormPointIndex, lat: number, lon: number, radiusM: number): boolean {
  if (idx.size === 0) return false;
  const cosLat = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
  const spanR = Math.max(1, Math.ceil(radiusM / STORM_BUFFER_M));
  const spanC = Math.max(1, Math.ceil(radiusM / (STORM_BUFFER_M * cosLat)));
  const r0 = Math.floor(lat / STORM_GRID_DEG);
  const c0 = Math.floor(lon / STORM_GRID_DEG);
  const r2 = radiusM * radiusM;
  for (let r = r0 - spanR; r <= r0 + spanR; r++) {
    for (let c = c0 - spanC; c <= c0 + spanC; c++) {
      const arr = idx.cells.get(`${r}:${c}`);
      if (!arr) continue;
      for (const [plat, plon] of arr) {
        const dy = (plat - lat) * M_PER_DEG_LAT;
        const dx = (plon - lon) * M_PER_DEG_LAT * cosLat;
        if (dx * dx + dy * dy <= r2) return true;
      }
    }
  }
  return false;
}

// One grid cell from canvas_pin_overview_cells: weighted centroid + per-state
// pin counts (only states actually present are keyed).
interface OverviewCell {
  lat: number;
  lon: number;
  states: Record<string, number>;
}

interface KnockPin {
  id: string;
  lat: number;
  lon: number;
  property_street: string;
  property_city: string;
  property_state: string;
  property_zip: string | null;
  homeowner_name: string | null;
  current_state: string;
  attempt_count: number;
  cooldown_until: string | null;
  notes: string | null;
  deal_id: string | null;
  parcel_id: string | null;
  drop_session_id: string | null;
  prior_appointment_count: number;
  last_appointment_at: string | null;
}

interface CanvasVisit {
  id: string;
  outcome: string;
  notes: string | null;
  visited_at: string;
  visited_by: string;
  attempt_number: number | null;
  voice_note_url: string | null;
  photo_storage_path: string | null;
}

// Pin lifecycle stages where the door-knock outcome grid no longer applies —
// the deal/lifecycle has moved past the canvasser's hand-off. Hoisted to
// module scope so it isn't reallocated on every PinSheet render (Fix #1,
// May 11, 2026).
const LOCKED_PIN_STATES = new Set<string>([
  "appt_scheduled","lead","claim_called","built","legacy_client","do_not_knock",
]);

// Roles allowed to drop pins + run drop sessions. Per Greg (May 11, 2026):
// "admins, asm, tl. no rm's." RMs (`restore_manager`) intentionally NOT in this
// set — they're a canvasser tier for visits, not a manager tier for placing pins.
// Super-users (Greg / Chad / Jennifer / Jeremy) bypass this set via `isSuperUser`.
// Directors (`sales_director`, `roof_director`, `director_of_operations`) and the
// historical `roof_team_manager` role kept — none of those are RMs.
/** Today's date in Eastern time, YYYY-MM-DD (daily areas, Oct 23, 2026). */
function todayEastern(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}
/** "Tue, Oct 27" for a YYYY-MM-DD. */
function fmtDayShort(day: string): string {
  return new Date(day + "T12:00:00Z").toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });
}

const MANAGER_ROLES = new Set([
  "admin","sales_director","asm","team_lead",
  "roof_team_manager","roof_director","director_of_operations",
]);

// Non-manager roles that may submit an appointment (pending_canvas_appointments
// insert = canvas_can_canvass()). Managers get "Drop pin here" instead; other
// roles that can open the canvas (finance, office, qc, PM…) can't submit.
const LEAD_BY_ADDRESS_ROLES = new Set(["canvasser", "restore_manager", "roof_specialist"]);

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

// ─── sessionStorage helpers for map/toggle persistence (Fix #2+#3, May 11, 2026)
// Canvas remounts when the canvasser routes to /canvas/appt to submit an
// appointment — useState resets to defaults so the map kicks back to the
// initial zoom and the weather toggles flip off. We persist the relevant
// state to sessionStorage on every change and hydrate it on mount so the
// user lands back in the same neighborhood with the same layers visible.
// sessionStorage (not localStorage) so opening a fresh tab tomorrow starts
// from defaults — this is "preserve across this session", not "remember
// forever". Failures (Safari private mode, quota) silently fall back to
// defaults rather than blowing up.
type PersistedMapView = { center: [number, number]; zoom: number };
function readMapViewFromSession(): PersistedMapView | null {
  try {
    const raw = sessionStorage.getItem("bardin.canvas.mapView");
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (
      Array.isArray(j?.center) && j.center.length === 2 &&
      Number.isFinite(j.center[0]) && Number.isFinite(j.center[1]) &&
      Number.isFinite(j?.zoom)
    ) {
      return { center: [Number(j.center[0]), Number(j.center[1])], zoom: Number(j.zoom) };
    }
  } catch { /* ignore */ }
  return null;
}
function readSessionFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch { /* ignore */ }
  return fallback;
}
function readSessionString<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw && (allowed as readonly string[]).includes(raw)) return raw as T;
  } catch { /* ignore */ }
  return fallback;
}
function readSessionNumber(key: string, min: number, max: number, fallback: number): number {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw != null) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= min && n <= max) return n;
    }
  } catch { /* ignore */ }
  return fallback;
}
function writeSession(key: string, value: string | boolean): void {
  try { sessionStorage.setItem(key, typeof value === "string" ? value : (value ? "true" : "false")); } catch { /* ignore */ }
}

// ─── NWS wind layer constants (Jul 12, 2026) ─────────────────────────────
// Slider bounds for the mph floor. 30 = below this it's not roof-relevant;
// 80 = above the max observed gust in the dataset (89 exists but a floor of
// 80 already isolates the extreme tail). Default floor = 50 (Greg's pick:
// roof-damage-plausible without much noise; ~70 reports/yr at 50+).
const WIND_MPH_MIN = 30;
const WIND_MPH_MAX = 80;
const WIND_MPH_DEFAULT = 50;
// Severity tiers — blue → violet so wind reads as a different weather mode
// than hail's amber → red. 58 mph = NWS severe-thunderstorm wind criterion.
//   30–49  #38bdf8  (sky)     gusty, marginal
//   50–57  #3b82f6  (blue)    roof-damage plausible
//   58–69  #6366f1  (indigo)  NWS severe
//   70+    #7c3aed  (violet)  destructive
const WIND_STEP_EXPR = [
  "step", ["get", "speed_mph"],
  "#38bdf8", 50, "#3b82f6", 58, "#6366f1", 70, "#7c3aed",
];
function windColor(mph: number): string {
  return mph >= 70 ? "#7c3aed" : mph >= 58 ? "#6366f1" : mph >= 50 ? "#3b82f6" : "#38bdf8";
}

// ─────────────────────────────────────────────────────────────────────────
export default function Canvas() {
  const { member, role, isSuperUser } = useAuth();
  const navigate = useNavigate();
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());
  // Top-bar pin (May 11, 2026): when iOS Safari ignores the page-level
  // user-scalable=no (Accessibility override, or in response to a Mapbox
  // gesture), pinch-zoom + pan can carry the top bar off-screen. The
  // VisualViewport hook applies an inverse transform so the bar always
  // stays parked at the visible viewport's top, at original pixel size.
  const topBarRef = useRef<HTMLDivElement | null>(null);
  useVisualViewportPin(topBarRef);

  const [pins, setPins] = useState<KnockPin[]>([]);
  // mapInited flips true at the end of the map-init effect. The bbox-fetch
  // effect depends on this so it runs AFTER the map is created (effects run
  // in declaration order; the bbox effect is positioned before map-init in
  // this file, so without the flag, mapRef.current would be null on the
  // bbox effect's first pass and the initial fetch would never fire).
  const [mapInited, setMapInited] = useState(false);
  // True when the map opened on the north-Atlanta default (no saved view or
  // deep link) — the rep's first GPS fix then centers the map on them.
  const startedOnDefaultViewRef = useRef(false);
  const [activePin, setActivePin] = useState<KnockPin | null>(null);
  const [visits, setVisits] = useState<CanvasVisit[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [pinStormReport, setPinStormReport] = useState<{
    short_id: string;
    pdf_storage_path: string | null;
    storm_date: string;
    property_max_in: number | null;
    data_snapshot: any;
  } | null>(null);
  const [reportRendering, setReportRendering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mapStyle, setMapStyle] = useState<"satellite" | "streets">(
    () => readSessionString<"satellite" | "streets">("bardin.canvas.mapStyle", ["satellite","streets"] as const, "satellite")
  );
  const [dropPinMode, setDropPinMode] = useState(false);
  // Drop session — manager-only mode; instant pin drops, no prompts.
  // dropSession is the active row (null when not in a session).
  const [dropSession, setDropSession] = useState<{ id: string; pin_count: number; started_at: string; label: string | null } | null>(null);
  const [reviewSession, setReviewSession] = useState<{
    id: string;
    pin_count: number;
    // Sep 9, 2026 — explicit save feedback so a slow End never reads as "didn't
    // save" (the Sep 8 incident). savedNow/skipped describe THIS End run;
    // dedupeUnavailable = the batched parcel lookup failed, so already-pinned
    // houses couldn't be screened out this run.
    savedNow?: number;
    skipped?: number;
    dedupeUnavailable?: boolean;
  } | null>(null);
  // Pending drops queued during a session — pushed to DB only on End.
  // Each entry = { tempId, lat, lon, droppedAt }. Persisted to localStorage
  // so accidental refreshes don't lose work.
  type PendingDrop = { tempId: string; lat: number; lon: number; droppedAt: string };
  const [pendingDrops, setPendingDrops] = useState<PendingDrop[]>([]);
  const pendingMarkersRef = useRef<Map<string, any>>(new Map());
  const [processing, setProcessing] = useState<{ done: number; total: number } | null>(null);
  // Overview-mode data: server-aggregated pin cells for the current low-zoom
  // viewport (null until the first overview fetch lands). See DETAIL_MIN_ZOOM.
  const [overviewCells, setOverviewCells] = useState<OverviewCell[] | null>(null);
  // Grid size (degrees) the current overviewCells were aggregated at — the
  // storm-area pin filter needs it to decide whether a cell can touch a halo.
  const overviewCellDegRef = useRef<number>(0.02);
  // Storm deep link (Oct 12, 2026): /canvas?storm=YYYY-MM-DD&lat=..&lon=..
  // opens the map centered on one storm with ONLY that date's hail drawn.
  // Used by the manager audit board's "Top active storm dates" cards (each
  // opens in a new tab). Ephemeral on purpose — it never touches the
  // manager's saved excludedStormDates; ✕ on the banner clears it.
  const stormLink = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const d = q.get("storm");
      const date = d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
      const lat = Number(q.get("lat")), lon = Number(q.get("lon"));
      const hasLL = Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
      return { date, center: hasLL ? ([lon, lat] as [number, number]) : null };
    } catch { return { date: null, center: null }; }
  }, []);
  // Area deep links (Oct 19, 2026):
  //   /canvas?area=<area_group_id>         → map fitted to that assigned area
  //     (home "My canvas areas" widget, Today tab area cards)
  //   /canvas?assign=1[&rep=<roster id>]   → draw-an-area mode, person pre-picked
  //     (Today tab "Draw a new area for …"; managers only, like the 📐 lasso)
  // Read once on mount; both act once and never touch saved map state.
  const areaLink = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const area = q.get("area"), rep = q.get("rep");
      // Daily areas (Oct 23, 2026): the Today tab's "Area here" adds where to
      // land (lat/lon/z = the build or adjuster meeting), the deal it is drawn
      // around (anchor + kind), the day it is for and a prefilled name.
      const anchor = q.get("anchor"), kind = q.get("kind"), forDate = q.get("for"), label = q.get("label");
      const lat = Number(q.get("lat")), lon = Number(q.get("lon")), z = Number(q.get("z"));
      const hasLL = Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
      const kinds = ["build", "adjuster", "reinspection", "other"] as const;
      return {
        area: area && uuid.test(area) ? area : null,
        assign: q.get("assign") === "1",
        rep: rep && uuid.test(rep) ? rep : null,
        center: hasLL && q.get("assign") === "1" ? ([lon, lat] as [number, number]) : null,
        zoom: Number.isFinite(z) && z >= 8 && z <= 19 ? z : 16,
        anchor: anchor && uuid.test(anchor) ? anchor : null,
        kind: kind && (kinds as readonly string[]).includes(kind) ? (kind as (typeof kinds)[number]) : null,
        forDate: forDate && /^\d{4}-\d{2}-\d{2}$/.test(forDate) ? forDate : null,
        label: label ? label.slice(0, 80) : null,
      };
    } catch { return { area: null, assign: false, rep: null, center: null, zoom: 16, anchor: null, kind: null, forDate: null, label: null }; }
  }, []);
  const [focusStormDate, setFocusStormDate] = useState<string | null>(stormLink.date);
  const [showStorms, setShowStorms] = useState<boolean>(() => stormLink.date ? true : readSessionFlag("bardin.canvas.showStorms", true));
  const [stormDaysWindow, setStormDaysWindow] = useState<number>(365);   // default 1 year
  // 0 = off (use stormDaysWindow). 30 or 60 = filter to storms 335–365 / 305–365 days old.
  const [stormExpiringWindow, setStormExpiringWindow] = useState<0 | 30 | 60>(0);
  const [stormPanelOpen, setStormPanelOpen] = useState(true);            // collapse the legend panel for full-screen canvassing
  const [stormGeoJson, setStormGeoJson] = useState<any>(null);
  // Show the lower-confidence 0.75–0.99" tier? Default OFF so canvassers see the
  // high-confidence dots first; toggling ON adds the borderline cells (rendered
  // in muted slate so they're visually distinguishable). See May 11, 2026 work.
  const [stormIncludeSub1, setStormIncludeSub1] = useState<boolean>(() => readSessionFlag("bardin.canvas.stormIncludeSub1", false));
  // Per-layer toggles within the storm overlay (gated by master `showStorms`):
  //   showRadar       — NEXRAD radar signatures from storm_signatures (default ON)
  //   showLsrHail     — NWS LSR ground-truth hail reports (default OFF)
  //   showLsrWind     — NWS LSR wind gust reports, speed reports only (default OFF).
  //                     Filtered by the windMinMph slider — applied CLIENT-SIDE so
  //                     dragging is instant (the fetch pulls everything ≥ WIND_MPH_MIN
  //                     once per time window; ~150 features/yr, trivially small).
  //   radarHighConf   — when true, radar filtered to POH≥50 AND POSH≥30 (default ON).
  //                     POSH≥30 (not ≥50) chosen so NEXRAD-undercalled cells like
  //                     the Mar 16, 2026 Milton storm (POH 80 / POSH 30 / 0.75")
  //                     still show. When false, every nx3hail_all cell shows —
  //                     includes graupel/noise/sub-confident echoes. May 11, 2026.
  const [showRadar, setShowRadar] = useState<boolean>(() => stormLink.date ? true : readSessionFlag("bardin.canvas.showRadar", true));
  const [showLsrHail, setShowLsrHail] = useState<boolean>(() => readSessionFlag("bardin.canvas.showLsrHail", false));
  const [radarHighConf, setRadarHighConf] = useState<boolean>(() => readSessionFlag("bardin.canvas.radarHighConf", true));
  const [lsrGeoJson, setLsrGeoJson] = useState<any>(null);
  const [showLsrWind, setShowLsrWind] = useState<boolean>(() => readSessionFlag("bardin.canvas.showLsrWind", false));
  const [windMinMph, setWindMinMph] = useState<number>(() => readSessionNumber("bardin.canvas.windMinMph", WIND_MPH_MIN, WIND_MPH_MAX, WIND_MPH_DEFAULT));
  const [lsrWindGeoJson, setLsrWindGeoJson] = useState<any>(null);

  // Manager-only storm-date exclusion filter (May 11, 2026).
  // Each entry = "YYYY-MM-DD" of an occurred_at date the manager has hidden
  // from BOTH the radar and NWS layers. Backed by localStorage (NOT
  // sessionStorage) — selections persist across sessions per-device so a
  // manager who hid the May/June dates last week doesn't have to redo it
  // every morning. Applied by pre-filtering the GeoJSON before it hits the
  // Mapbox sources (so cluster_count aggregation reflects the filter too).
  // UI is gated on `isManager` further down so canvassers never see it.
  const [excludedStormDates, setExcludedStormDates] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("bardin.canvas.excludedStormDates");
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr.filter((x: unknown) => typeof x === "string")) : new Set();
    } catch { return new Set(); }
  });
  // Local UI state — picker is collapsed by default. Independent of the
  // overall stormPanelOpen state so opening the picker doesn't reopen the
  // whole panel if the manager had it tucked away.
  const [stormDatePickerOpen, setStormDatePickerOpen] = useState<boolean>(false);
  // Persist excludedStormDates to localStorage on every change.
  useEffect(() => {
    try {
      localStorage.setItem(
        "bardin.canvas.excludedStormDates",
        JSON.stringify([...excludedStormDates]),
      );
    } catch { /* private mode / quota — silently drop, in-memory state still wins */ }
  }, [excludedStormDates]);
  // Pins-in-selected-storms opt-out (Sep 28, 2026). Default ON: narrowing the
  // storm dates ALSO narrows the knock pins to those storm areas (see the
  // stormPinFilterActive derivation below). localStorage like
  // excludedStormDates so a manager's choice sticks per device.
  const [pinsInStormsOnly, setPinsInStormsOnly] = useState<boolean>(() => {
    try { return localStorage.getItem("bardin.canvas.pinsInStormsOnly") !== "false"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("bardin.canvas.pinsInStormsOnly", pinsInStormsOnly ? "true" : "false"); } catch { /* ignore */ }
  }, [pinsInStormsOnly]);

  // UI state — overflow menu for manager actions, filter sheet, address search
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Array<{
    label: string; sublabel: string; lat: number; lon: number; source: "parcel" | "deal" | "pin" | "mapbox";
    pinId?: string; parcelId?: string;
    street?: string | null; city?: string | null; state?: string | null; zip?: string | null;
  }>>([]);
  const [showSearch, setShowSearch] = useState(false);
  const searchedMarkerRef = useRef<any>(null);
  // Action card shown after picking a search result: open the existing pin
  // at that house, or drop a fresh one there.
  const [searchAction, setSearchAction] = useState<{
    lat: number; lon: number; label: string; sublabel: string;
    resolving: boolean; existingPin: KnockPin | null;
    street?: string | null; city?: string | null; state?: string | null; zip?: string | null;
  } | null>(null);
  const lastSearchActionRef = useRef<typeof searchAction>(null);
  // Offline queue state
  const [queuedCount, setQueuedCount] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== "undefined" ? navigator.onLine : true);
  // Assigned-area overlay (visible to ASM/TL/manager + assignees per RLS).
  // Persisted to sessionStorage (May 13, 2026) so the 📐 toggle survives
  // remounts when the canvasser routes through /canvas/appt and back —
  // matches the pattern used by mapStyle/showStorms/etc. above.
  const [showAreas, setShowAreas] = useState<boolean>(() => readSessionFlag("bardin.canvas.showAreas", true));
  const [assignedAreasGeoJson, setAssignedAreasGeoJson] = useState<any>(null);

  // Ship 10: area assignment state — freehand polygon draw
  const [assignMode, setAssignMode] = useState(false);
  // Array of [lon, lat] points the user draws. Last point == first point when closed.
  const [polygonPoints, setPolygonPoints] = useState<Array<[number, number]>>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showAssignSheet, setShowAssignSheet] = useState(false);
  const [activeRoster, setActiveRoster] = useState<Array<{ id: string; full_name: string; role: string }>>([]);
  const [selectedReps, setSelectedReps] = useState<Set<string>>(new Set());
  const [assignNote, setAssignNote] = useState("");
  const [areaLabel, setAreaLabel] = useState("");
  const [shareMode, setShareMode] = useState<"split" | "share">("share");
  // Daily areas (Oct 23, 2026): the day this area is for (null = a standing
  // area, as before) and the stop it was drawn around — both set by the Today
  // tab's deep link; the sheet lets the manager flip Today / Standing.
  const [areaForDate, setAreaForDate] = useState<string | null>(null);
  const [areaAnchor, setAreaAnchor] = useState<{ dealId: string; kind: "build" | "adjuster" | "reinspection" | "other" } | null>(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [filterStates, setFilterStates] = useState<Set<string>>(
    () => new Set(["open","knocked","contacted","appt_scheduled","lead","claim_called","built","legacy_client","not_home_3x"])
  );

  // Owner-scope filter for the canvas pin map.
  //   "all"   — no filter (default)
  //   "mine"  — only deals I own (or split with) — works for any role
  //   "team"  — TL: my RMs.   ASM: my whole area (TLs + their RMs reporting to me).
  //   "tl"    — ASM only: pick a specific TL → show that TL + their RMs' deals.
  // scopedDealIds: null = no filter; Set<dealId> = only those deal IDs visible.
  type ScopeMode = "all" | "mine" | "team" | "tl";
  const [scopeMode, setScopeMode] = useState<ScopeMode>("all");
  const [pickedTlId, setPickedTlId] = useState<string | null>(null);
  const [scopedDealIds, setScopedDealIds] = useState<Set<string> | null>(null);

  // ASMs need the list of TLs (themselves + RMs reporting to them under role=team_lead)
  // so they can drill into a specific TL's team.
  const [asmTlList, setAsmTlList] = useState<Array<{ id: string; full_name: string }>>([]);
  useEffect(() => {
    if (!member || member.role !== "asm") { setAsmTlList([]); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("team_roster")
        .select("id, full_name")
        .eq("asm_id", member.id)
        .eq("role", "team_lead")
        .eq("is_active", true)
        .order("full_name");
      if (!cancelled) setAsmTlList(((data ?? []) as Array<{ id: string; full_name: string }>));
    })();
    return () => { cancelled = true; };
  }, [member?.id, member?.role]);

  // When scopeMode (or pickedTlId, or member) changes, recompute the deal-id set.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!member || scopeMode === "all") {
        setScopedDealIds(null);
        return;
      }

      // Figure out the universe of user_ids whose deals count as "in scope".
      let userIds: string[] = [];

      if (scopeMode === "mine") {
        // Just me — every role can have personal deals.
        userIds = [member.id];

      } else if (scopeMode === "team") {
        if (member.role === "team_lead") {
          // TL: me + my RMs
          userIds = [member.id];
          const { data } = await supabase.from("team_roster")
            .select("id").eq("team_lead_id", member.id).eq("is_active", true);
          userIds.push(...((data ?? []) as Array<{ id: string }>).map((r) => r.id));
        } else if (member.role === "asm") {
          // ASM "My area": me + every TL under me + every RM under those TLs
          // (and any RMs that report directly to me, no TL).
          userIds = [member.id];
          const { data: directs } = await supabase.from("team_roster")
            .select("id").eq("asm_id", member.id).eq("is_active", true);
          userIds.push(...((directs ?? []) as Array<{ id: string }>).map((r) => r.id));
          // Also include RMs whose TL is one of my direct TLs
          const tlIds = ((directs ?? []) as Array<{ id: string }>).map((r) => r.id);
          if (tlIds.length > 0) {
            const list = `(${tlIds.join(",")})`;
            const { data: subs } = await supabase.from("team_roster")
              .select("id").or(`team_lead_id.in.${list}`).eq("is_active", true);
            userIds.push(...((subs ?? []) as Array<{ id: string }>).map((r) => r.id));
          }
        } else {
          // RMs hitting "team" gracefully fall back to just themselves
          userIds = [member.id];
        }

      } else if (scopeMode === "tl" && pickedTlId) {
        // ASM picked a specific TL — show that TL's deals + their RMs' deals.
        userIds = [pickedTlId];
        const { data } = await supabase.from("team_roster")
          .select("id").eq("team_lead_id", pickedTlId).eq("is_active", true);
        userIds.push(...((data ?? []) as Array<{ id: string }>).map((r) => r.id));
      } else {
        setScopedDealIds(null);
        return;
      }

      // De-dupe + chunk for PostgREST URL-length safety
      const uniq = Array.from(new Set(userIds));
      const chunks: string[][] = [];
      for (let i = 0; i < uniq.length; i += 50) chunks.push(uniq.slice(i, i + 50));
      const ids = new Set<string>();
      for (const c of chunks) {
        const list = `(${c.join(",")})`;
        const { data } = await supabase.from("deals")
          .select("id")
          .or(`owner_id.in.${list},split_rm_id.in.${list}`);
        for (const row of (data ?? []) as Array<{ id: string }>) ids.add(row.id);
      }
      if (!cancelled) setScopedDealIds(ids);
    })();
    return () => { cancelled = true; };
  }, [member?.id, member?.role, scopeMode, pickedTlId]);

  const isManager = useMemo(
    () => isSuperUser || (role && MANAGER_ROLES.has(role)),
    [role, isSuperUser]
  );

  // Ship 6 — on-shift session + GPS pings + day-stats counter
  const { shift, starting, ending, start: startShiftRaw, end: endShift, err: shiftErr, geoProblem: shiftGeoProblem } = useCanvasShift(supabase);
  const onShift = shift !== null && !shift.ended_at;
  // Canvassers are paid by the hour on shift, so their shift needs location
  // (Oct 27, 2026). Everyone else can still start without it.
  const isCanvasser = role === "canvasser";
  const startShift = () => startShiftRaw({ requireLocation: isCanvasser });

  // Shift-gated pins (Jun 8, 2026): for field reps (RMs + canvassers), the
  // on-shift / off-shift toggle doubles as an on/off switch for knock pins —
  // off shift, pins are hidden; on shift, they show. Per Greg: applies to
  // `restore_manager` + `canvasser` only. Managers/ASM/TL/admins and
  // super-users always see pins (oversight). Assigned-area polygons are NOT
  // gated — they render from get_active_canvas_areas_geojson and stay visible
  // regardless of shift state.
  const pinsGatedByShift = !isSuperUser && !!role && ["restore_manager", "canvasser"].includes(role);
  const pinsHiddenOffShift = pinsGatedByShift && !onShift;

  // Ship 6 v2 — tap a day-stats tile to identify matching pins on the map
  // and zoom to fit them. 'miles' draws the GPS breadcrumb line instead of
  // selecting pins. null = nothing selected.
  type TileKind = "knocks" | "contacts" | "appts" | "claims" | "miles";
  const [tileSel, setTileSel] = useState<TileKind | null>(null);

  // claim_count lives on canvas_shifts and is incremented by a DB trigger
  // (trg_canvas_bump_claim_count) whenever a deal linked to one of this
  // canvasser's knock pins transitions into a claims-phase stage while
  // their shift is open. The realtime subscription on the shift row
  // (in useCanvasShift) propagates the update to the UI automatically.

  // Quick win #3 — undo toast for the most-recently-logged visit. 5-sec window.
  const [undoToast, setUndoToast] = useState<{ clientUuid: string; pinId: string; label: string } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Quick win #4 — other reps currently on shift. Shown as small grey-blue
  // dots on the map + a "N reps live" badge.
  //
  // Refetch is debounced: 12 active canvassers pinging every 5 min would
  // otherwise fire ~144 RPC calls/hr per connected client through the
  // realtime sub. The 2-sec debounce collapses bursts into one fetch.
  interface ActiveRep { user_id: string; full_name: string; initials: string; lat: number | null; lon: number | null; visits: number; appts: number; }
  const [activeReps, setActiveReps] = useState<ActiveRep[]>([]);
  const repMarkersRef = useRef<Map<string, any>>(new Map());
  useEffect(() => {
    let cancelled = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    async function loadNow() {
      const { data, error } = await supabase.rpc("canvas_active_reps");
      if (cancelled) return;
      if (!error && data) setActiveReps(data as ActiveRep[]);
    }
    function scheduleLoad() {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { void loadNow(); }, 2_000);
    }

    loadNow(); // initial probe — no debounce
    const ch = supabase
      .channel("canvas-active-reps")
      .on("postgres_changes", { event: "*", schema: "public", table: "canvas_shifts" }, scheduleLoad)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "canvas_gps_pings" }, scheduleLoad)
      .subscribe();
    // 60s safety net for any missed events (network blip, channel reconnect)
    const tick = setInterval(loadNow, 60_000);
    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      clearInterval(tick);
      supabase.removeChannel(ch);
    };
  }, []);

  // Quick win #1 — staged voice/photo media to attach to the next visit log
  const [pendingMedia, setPendingMedia] = useState<CapturedMedia>({ voiceBlob: null, voiceMs: 0, photoFile: null });
  // Clear staged media whenever the active pin changes — moving to a new
  // pin shouldn't carry forward whatever was captured at the last one.
  useEffect(() => {
    setPendingMedia({ voiceBlob: null, voiceMs: 0, photoFile: null });
  }, [activePin?.id]);

  // Quick win #2 — current GPS for "↑ N ft to <pin>" distance hint, the
  // "you are here" dot, and (via lib/geo's shared fix) the shift pings.
  // Oct 27, 2026: a canvasser's phone is followed whenever the canvas is open
  // (it's what makes the phone ask for location before the shift starts);
  // anyone else only while on shift. Errors are no longer swallowed: the
  // banner under the top bar says what's wrong and how to fix it.
  const myLoc = useMyLocation(onShift || isCanvasser);
  const myPos = useMemo(
    () => (myLoc.fix ? { lat: myLoc.fix.lat, lon: myLoc.fix.lon } : null),
    [myLoc.fix],
  );
  // A live fix wins over an older failure (e.g. a shift start refused before
  // the rep turned location on).
  const locProblem = myLoc.fix ? null : (myLoc.problem ?? (onShift || isCanvasser ? shiftGeoProblem : null));

  // Recover any open drop session on mount (manager refreshed mid-session)
  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("canvas_active_drop_session");
      if (cancelled) return;
      const row = (data && typeof data === "object" && (data as any).id) ? data : null;
      if (row) {
        setDropSession({ id: row.id, pin_count: row.pin_count, started_at: row.started_at, label: row.label });
        // Restore any unprocessed pending drops for this session
        try {
          const stored = window.localStorage.getItem(`bardin.canvas.pendingDrops.${row.id}`);
          if (stored) {
            const arr = JSON.parse(stored) as PendingDrop[];
            if (Array.isArray(arr)) setPendingDrops(arr);
          }
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [isManager]);

  // Persist pendingDrops to localStorage on every change (keyed by session id)
  useEffect(() => {
    if (!dropSession) return;
    try {
      window.localStorage.setItem(`bardin.canvas.pendingDrops.${dropSession.id}`, JSON.stringify(pendingDrops));
    } catch { /* ignore */ }
  }, [pendingDrops, dropSession?.id]);

  // ── Pin fetching: viewport bbox + buffer, debounced refetch on moveend ─
  // History: v1 fetched the 1000 most-recently-created pins for the whole
  // map (Apr 30, 2026). Once knock_pins crossed ~1000 rows (May 18, 2026
  // at 31,068 with the Regrid backfill + months of canvassing), the oldest
  // hoods silently went dark — newer pins won the `.limit(1000)` race.
  // PostgREST has its own ~1000 row cap per query independent of the client
  // hint, so this is the proper fix: query only what's in the viewport
  // (plus a 50% buffer on each side for cache locality), paginate within
  // the bbox via .range() for dense zones, and refetch on moveend when the
  // visible viewport leaves the cached area. Initial fetch is gated on the
  // map's first `idle` event so we know getBounds() is valid.
  const loadedBboxRef = useRef<{ s: number; n: number; w: number; e: number } | null>(null);
  const pinFetchInFlightRef = useRef(false);
  const pinFetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!mapInited) return;
    const map = mapRef.current;
    if (!map) return;

    const PIN_SELECT = "id, lat, lon, property_street, property_city, property_state, property_zip, homeowner_name, current_state, attempt_count, cooldown_until, notes, deal_id, parcel_id, drop_session_id, prior_appointment_count, last_appointment_at";
    const CHUNK = 1000;
    const SAFETY_CAP = 50000;

    async function fetchInBbox(forceRefetch: boolean): Promise<void> {
      const bounds = map.getBounds?.();
      if (!bounds) return;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const latSpan = ne.lat - sw.lat;
      const lonSpan = ne.lng - sw.lng;
      const latPad = latSpan * 0.5;
      const lonPad = lonSpan * 0.5;
      const south = sw.lat - latPad;
      const north = ne.lat + latPad;
      const west  = sw.lng - lonPad;
      const east  = ne.lng + lonPad;

      // ── OVERVIEW MODE (zoom < DETAIL_MIN_ZOOM) ─────────────────────────
      // Wide views used to page the ENTIRE pin table through PostgREST
      // (~40 × 1,000-row requests at metro zoom — Sep 8, 2026 incident).
      // Instead: one small aggregate RPC (~60KB metro-wide, ~85ms) returning
      // grid cells with per-state counts. The overview render effect below
      // draws them as cluster-style bubbles. Row-level pins, client
      // clustering, and DOM markers are untouched at >= DETAIL_MIN_ZOOM.
      // loadedBboxRef is deliberately NOT set here, so zooming into detail
      // range always row-fetches fresh.
      const zoomNow = map.getZoom?.() ?? 0;
      if (zoomNow < DETAIL_MIN_ZOOM) {
        if (pinFetchInFlightRef.current) return;
        pinFetchInFlightRef.current = true;
        try {
          // Adaptive grid: keep the cell count bounded (~45×45) at any zoom.
          const cell = Math.min(0.08, Math.max(0.004, (east - west) / 45));
          overviewCellDegRef.current = cell;
          const { data, error } = await supabase.rpc("canvas_pin_overview_cells", {
            p_min_lat: south, p_min_lon: west,
            p_max_lat: north, p_max_lon: east,
            p_cell: cell,
          });
          if (error) {
            if (!isNetworkErr(error)) setErr(error.message);
          } else if (Array.isArray(data)) {
            setOverviewCells(data as OverviewCell[]);
          }
        } catch (e) {
          if (!isNetworkErr(e)) setErr(String((e as any)?.message ?? e));
        } finally {
          pinFetchInFlightRef.current = false;
        }
        return;
      }

      // Skip refetch if the visible viewport is comfortably inside what
      // we last loaded. The 50% buffer means small pans hit the cache.
      if (!forceRefetch && loadedBboxRef.current) {
        const c = loadedBboxRef.current;
        if (sw.lat >= c.s && ne.lat <= c.n && sw.lng >= c.w && ne.lng <= c.e) return;
      }

      if (pinFetchInFlightRef.current) return;
      pinFetchInFlightRef.current = true;

      try {
        const all: KnockPin[] = [];
        for (let from = 0; from < SAFETY_CAP; from += CHUNK) {
          const { data, error } = await supabase
            .from("knock_pins")
            .select(PIN_SELECT)
            .gte("lat", south).lte("lat", north)
            .gte("lon", west).lte("lon", east)
            .order("created_at", { ascending: false })
            .range(from, from + CHUNK - 1);
          if (error) {
            if (!isNetworkErr(error)) setErr(error.message);
            break;
          }
          const chunk = (data ?? []) as KnockPin[];
          all.push(...chunk);
          if (chunk.length < CHUNK) break;
        }
        setPins(all);
        loadedBboxRef.current = { s: south, n: north, w: west, e: east };
      } catch (e) {
        if (!isNetworkErr(e)) setErr(String((e as any)?.message ?? e));
      } finally {
        pinFetchInFlightRef.current = false;
      }
    }

    // Initial fetch: wait for the map's first `idle` event (style loaded,
    // first frame painted) so getBounds() is real, then force a fetch.
    let firstIdleDone = false;
    const onFirstIdle = () => {
      if (firstIdleDone) return;
      firstIdleDone = true;
      void fetchInBbox(true);
    };
    map.on("idle", onFirstIdle);

    // Subsequent fetches: debounced 400ms after moveend. Sits alongside the
    // existing onMoveEnd in the map-init effect (which persists camera to
    // session) — Mapbox supports multiple listeners per event.
    const onMoveEndForPins = () => {
      if (pinFetchDebounceRef.current) clearTimeout(pinFetchDebounceRef.current);
      pinFetchDebounceRef.current = setTimeout(() => void fetchInBbox(false), 400);
    };
    map.on("moveend", onMoveEndForPins);

    return () => {
      try { map.off("idle", onFirstIdle); } catch { /* map may already be torn down */ }
      try { map.off("moveend", onMoveEndForPins); } catch { /* ignore */ }
      if (pinFetchDebounceRef.current) {
        clearTimeout(pinFetchDebounceRef.current);
        pinFetchDebounceRef.current = null;
      }
    };
  }, [mapInited]);

  // ── Realtime sub on knock_pins for cross-rep visibility ─────────────────
  // Before this sub, if another rep dropped a pin in a hood you were viewing,
  // you wouldn't see it until you panned away and back. Now: subscribe to all
  // knock_pins changes, filter client-side by the currently-loaded bbox so we
  // only push state updates for pins that the user can actually see.
  //
  // Idempotency: own-drop + own-outcome flows already prepend/replace pins in
  // local state synchronously. The realtime event fires shortly after; the
  // `prev.some(p => p.id === newRow.id)` check de-dupes INSERTs, .map/.filter
  // are naturally idempotent for UPDATE/DELETE.
  //
  // Supabase realtime can't bbox-filter server-side (only `eq` filters), so
  // we receive every knock_pins event in the project and drop the ones that
  // fall outside our cached bbox. Volume is bounded — at peak canvassing this
  // is maybe a few events per minute project-wide.
  useEffect(() => {
    if (!mapInited) return;
    const channel = supabase
      .channel("canvas-knock-pins-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "knock_pins" },
        (payload: any) => {
          const newRow = (payload?.new ?? null) as KnockPin | null;
          const oldRow = (payload?.old ?? null) as { id?: string; lat?: number; lon?: number } | null;
          const eventType = payload?.eventType as "INSERT" | "UPDATE" | "DELETE" | undefined;

          // DELETE: only old row is present; remove if we have it in state.
          if (eventType === "DELETE") {
            const id = oldRow?.id;
            if (!id) return;
            setPins((prev) => prev.filter((p) => p.id !== id));
            return;
          }

          if (!newRow || newRow.lat == null || newRow.lon == null) return;
          const c = loadedBboxRef.current;
          // If we haven't loaded a bbox yet, ignore the event — the initial
          // fetch will pick up the row when the bbox is established.
          if (!c) return;
          const inBbox =
            newRow.lat >= c.s && newRow.lat <= c.n &&
            newRow.lon >= c.w && newRow.lon <= c.e;
          if (!inBbox) return;

          if (eventType === "INSERT") {
            setPins((prev) => prev.some((p) => p.id === newRow.id) ? prev : [newRow, ...prev]);
          } else if (eventType === "UPDATE") {
            setPins((prev) => {
              const idx = prev.findIndex((p) => p.id === newRow.id);
              if (idx < 0) {
                // Pin updated to a state that's now in our bbox but wasn't
                // before (e.g., parcel_id linked, lat/lon corrected). Add it.
                return [newRow, ...prev];
              }
              const next = prev.slice();
              next[idx] = { ...next[idx], ...newRow };
              return next;
            });
          }
        },
      )
      .subscribe();
    return () => {
      try { supabase.removeChannel(channel); } catch { /* ignore */ }
    };
  }, [mapInited]);

  // ── Offline queue background sync — flushes every 30s + on online event ──
  useEffect(() => {
    const stop = startBackgroundSync(supabase, (count) => setQueuedCount(count));
    return () => stop();
  }, []);

  // ── Track online/offline so we can suppress noisy fetch errors ─────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => { setIsOnline(true); setErr(null); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Helper: recognize transient network errors so we don't pollute the err
  // banner. Safari iOS uses "Load failed", Chrome uses "Failed to fetch",
  // various HTTP timeout phrases, "NetworkError", etc.
  function isNetworkErr(e: any): boolean {
    if (!e) return false;
    const msg = String(e.message ?? e).toLowerCase();
    return !navigator.onLine
      || msg.includes("load failed")
      || msg.includes("failed to fetch")
      || msg.includes("networkerror")
      || msg.includes("timeout")
      || msg.includes("network request failed");
  }

  // ── Load storm signatures for overlay (window controlled by stormDaysWindow) ─
  // Uses get_storm_sigs_geojson which returns the entire FeatureCollection as a
  // single jsonb scalar. Critical: PostgREST silently caps RPC table-row responses
  // at 1000, which truncated history older than ~5 months at the 365d window
  // (dropped the May 25, 2025 Marietta storm). Returning a single jsonb value
  // bypasses the row cap entirely.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // "Expiring soon" mode = storms (365 - N) to 365 days old, where N is the
      // window in days before the 1-yr claim filing deadline. e.g. N=30 → 335–365d.
      // Size floor toggles between 1.0" (strict, default) and 0.75" (include
      // borderline cells). High-confidence toggle applies POH ≥ 50 AND POSH ≥ 30
      // when ON (default). Chosen by the Mar 16, 2026 Milton case: NEXRAD reported
      // POH 80 / POSH 30 / MEHS 0.75 for cells 6–12 mi north of Milton against
      // ground-truth 1.25" hail. Pushing POSH to ≥50 would have hidden them.
      // POSH ≥ 30 = "moderate severe-hail probability" — drops pure-graupel noise,
      // keeps the undercalled real cells.
      const minSize = stormIncludeSub1 ? 0.75 : 1.0;
      const minProb = radarHighConf ? 50 : 0;
      const minSevProb = radarHighConf ? 30 : 0;
      const args = stormExpiringWindow > 0
        ? { p_days: 365, p_min_size_in: minSize, p_min_age_days: 365 - stormExpiringWindow, p_min_prob: minProb, p_min_sev_prob: minSevProb }
        : { p_days: stormDaysWindow, p_min_size_in: minSize, p_min_age_days: 0, p_min_prob: minProb, p_min_sev_prob: minSevProb };
      try {
        const { data, error } = await supabase.rpc("get_storm_sigs_geojson", args);
        if (cancelled) return;
        if (error) {
          if (!isNetworkErr(error)) setErr(`storm_signatures: ${error.message}`);
          return;
        }
        setStormGeoJson(data ?? { type: "FeatureCollection", features: [] });
      } catch (e) {
        if (!isNetworkErr(e)) setErr(`storm_signatures: ${(e as any)?.message ?? e}`);
      }
    })();
    return () => { cancelled = true; };
  }, [stormDaysWindow, stormExpiringWindow, stormIncludeSub1, radarHighConf]);

  // ── Load NWS LSR hail reports (ground-truth spotter/public/trained reports) ─
  // Only fetches when the LSR layer toggle is on. NO SIZE FLOOR: every NWS hail
  // report is a vetted ground-truth observation, so we surface them all
  // regardless of the size toggle (which is radar-only as of May 11, 2026).
  useEffect(() => {
    if (!showLsrHail) return;
    let cancelled = false;
    (async () => {
      const args = stormExpiringWindow > 0
        ? { p_days: 365, p_min_size_in: 0, p_min_age_days: 365 - stormExpiringWindow }
        : { p_days: stormDaysWindow, p_min_size_in: 0, p_min_age_days: 0 };
      try {
        const { data, error } = await supabase.rpc("get_storm_lsr_hail_geojson", args);
        if (cancelled) return;
        if (error) {
          if (!isNetworkErr(error)) setErr(`lsr_hail: ${error.message}`);
          return;
        }
        setLsrGeoJson(data ?? { type: "FeatureCollection", features: [] });
      } catch (e) {
        if (!isNetworkErr(e)) setErr(`lsr_hail: ${(e as any)?.message ?? e}`);
      }
    })();
    return () => { cancelled = true; };
  }, [showLsrHail, stormDaysWindow, stormExpiringWindow]);

  // ── Load NWS LSR wind reports (measured/estimated gusts, Jul 12, 2026) ──
  // Only fetches when the wind layer toggle is on. Server floor = WIND_MPH_MIN
  // (the slider's bottom end) so the mph slider filters CLIENT-SIDE with zero
  // refetch latency. Speed reports only — the RPC excludes no-magnitude damage
  // reports, WIND CHILL, and MARINE rows by design.
  useEffect(() => {
    if (!showLsrWind) return;
    let cancelled = false;
    (async () => {
      const args = stormExpiringWindow > 0
        ? { p_days: 365, p_min_mph: WIND_MPH_MIN, p_min_age_days: 365 - stormExpiringWindow }
        : { p_days: stormDaysWindow, p_min_mph: WIND_MPH_MIN, p_min_age_days: 0 };
      try {
        const { data, error } = await supabase.rpc("get_storm_lsr_wind_geojson", args);
        if (cancelled) return;
        if (error) {
          if (!isNetworkErr(error)) setErr(`lsr_wind: ${error.message}`);
          return;
        }
        setLsrWindGeoJson(data ?? { type: "FeatureCollection", features: [] });
      } catch (e) {
        if (!isNetworkErr(e)) setErr(`lsr_wind: ${(e as any)?.message ?? e}`);
      }
    })();
    return () => { cancelled = true; };
  }, [showLsrWind, stormDaysWindow, stormExpiringWindow]);

  // ── Storm-date filter derivations (Manager-only date exclusion, May 11, 2026)
  // Two outputs:
  //   1. stormDateOptions — sorted list of unique YYYY-MM-DD dates present in
  //      either radar or LSR feature sets, with per-date counts. Drives the
  //      picker UI. Built from the RAW fetched data so a manager can toggle
  //      a date back ON even when it's currently hidden.
  //   2. stormGeoJsonDisplayed / lsrGeoJsonDisplayed — filtered FeatureCollections
  //      that drop any feature whose occurred_at date is in excludedStormDates.
  //      Used as the source data for the Mapbox layers and for every count
  //      chip in the panel — so cluster aggregation and "13,445 sigs" line up.
  //      Important: Mapbox setFilter() on a clustered source doesn't affect
  //      cluster_count, so pre-filtering the source data is the only way to
  //      make the cluster bubble numbers reflect the manager's selection.
  // Wind slider floor applied CLIENT-SIDE (instant drag, no refetch). This
  // intermediate set — floor applied but NOT the date exclusions — feeds both
  // the date picker's 💨 counts (so they agree with what the slider allows,
  // while still letting a hidden date be re-enabled) and the displayed memo.
  const lsrWindSpeedFiltered = useMemo(() => {
    if (!lsrWindGeoJson) return lsrWindGeoJson;
    return {
      ...lsrWindGeoJson,
      features: (lsrWindGeoJson.features ?? []).filter(
        (f: any) => Number(f?.properties?.speed_mph ?? 0) >= windMinMph
      ),
    };
  }, [lsrWindGeoJson, windMinMph]);

  type StormDateOption = { date: string; radar: number; lsr: number; wind: number; total: number };
  const stormDateOptions: StormDateOption[] = useMemo(() => {
    const counts = new Map<string, { radar: number; lsr: number; wind: number }>();
    const ingest = (fc: any, key: "radar" | "lsr" | "wind") => {
      for (const f of (fc?.features ?? [])) {
        const t = f?.properties?.occurred_at;
        if (typeof t !== "string" || t.length < 10) continue;
        const d = t.slice(0, 10);
        let row = counts.get(d);
        if (!row) { row = { radar: 0, lsr: 0, wind: 0 }; counts.set(d, row); }
        row[key]++;
      }
    };
    ingest(stormGeoJson, "radar");
    ingest(lsrGeoJson, "lsr");
    ingest(lsrWindSpeedFiltered, "wind");
    return [...counts.entries()]
      .map(([date, c]) => ({ date, radar: c.radar, lsr: c.lsr, wind: c.wind, total: c.radar + c.lsr + c.wind }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [stormGeoJson, lsrGeoJson, lsrWindSpeedFiltered]);

  const stormGeoJsonDisplayed = useMemo(() => {
    if (!stormGeoJson) return stormGeoJson;
    if (excludedStormDates.size === 0 && !focusStormDate) return stormGeoJson;
    return {
      ...stormGeoJson,
      features: (stormGeoJson.features ?? []).filter((f: any) => {
        const t = f?.properties?.occurred_at;
        if (typeof t !== "string" || t.length < 10) return !focusStormDate;
        if (focusStormDate) return t.slice(0, 10) === focusStormDate;
        return !excludedStormDates.has(t.slice(0, 10));
      }),
    };
  }, [stormGeoJson, excludedStormDates, focusStormDate]);

  const lsrGeoJsonDisplayed = useMemo(() => {
    if (!lsrGeoJson) return lsrGeoJson;
    if (excludedStormDates.size === 0 && !focusStormDate) return lsrGeoJson;
    return {
      ...lsrGeoJson,
      features: (lsrGeoJson.features ?? []).filter((f: any) => {
        const t = f?.properties?.occurred_at;
        if (typeof t !== "string" || t.length < 10) return !focusStormDate;
        if (focusStormDate) return t.slice(0, 10) === focusStormDate;
        return !excludedStormDates.has(t.slice(0, 10));
      }),
    };
  }, [lsrGeoJson, excludedStormDates, focusStormDate]);

  // Wind displayed = slider floor (already applied above) minus excluded dates.
  const lsrWindGeoJsonDisplayed = useMemo(() => {
    if (!lsrWindSpeedFiltered) return lsrWindSpeedFiltered;
    if (excludedStormDates.size === 0 && !focusStormDate) return lsrWindSpeedFiltered;
    return {
      ...lsrWindSpeedFiltered,
      features: (lsrWindSpeedFiltered.features ?? []).filter((f: any) => {
        const t = f?.properties?.occurred_at;
        if (typeof t !== "string" || t.length < 10) return !focusStormDate;
        if (focusStormDate) return t.slice(0, 10) === focusStormDate;
        return !excludedStormDates.has(t.slice(0, 10));
      }),
    };
  }, [lsrWindSpeedFiltered, excludedStormDates, focusStormDate]);

  // ── Pins-in-storms filter (Sep 28, 2026) ──────────────────────────────
  // Greg: "can we only show pins within storm dates that are clicked. so if
  // we only choose 2 dates, the only pins showing on map are within those
  // storm pins." Every date in the picker is checked by default, so "the
  // dates that are clicked" = whatever storms are on the map: with the
  // overlay on, a manager sees ONLY the knock pins inside a drawn storm area,
  // and hiding dates in the picker narrows the pins with the storms. (The
  // first cut that morning only engaged once a date was hidden — Greg's
  // screenshot with every date shown still had pins outside the halos, so
  // the narrowing gate came out.) "Storm area" = the 1-mile halo around each
  // radar signature / NWS hail report / NWS wind report — only layers
  // currently ON count, and only the dates still visible. Overlay off, the
  // opt-out toggle, or a non-manager → no pin filtering, so canvassers and
  // RMs (not in MANAGER_ROLES) are untouched. Applied by every pin-render
  // path (DOM markers, cluster source, overview cells, distance hint) via
  // pinIdsInStorms / stormPinIndex so they stay in lockstep.
  const stormDatesHiddenCount = useMemo(
    () => stormDateOptions.reduce((n, o) => n + (excludedStormDates.has(o.date) ? 1 : 0), 0),
    [stormDateOptions, excludedStormDates],
  );
  const stormPinFilterActive = !!isManager && showStorms && pinsInStormsOnly;
  const stormPinIndex = useMemo<StormPointIndex | null>(() => {
    if (!stormPinFilterActive) return null;
    return buildStormPointIndex([
      showRadar ? stormGeoJsonDisplayed : null,
      showLsrHail ? lsrGeoJsonDisplayed : null,
      showLsrWind ? lsrWindGeoJsonDisplayed : null,
    ]);
  }, [stormPinFilterActive, showRadar, showLsrHail, showLsrWind, stormGeoJsonDisplayed, lsrGeoJsonDisplayed, lsrWindGeoJsonDisplayed]);
  // null = filter inactive (every pin shows). Otherwise the ids of the fetched
  // pins that sit inside at least one drawn storm area.
  const pinIdsInStorms = useMemo<Set<string> | null>(() => {
    if (!stormPinIndex) return null;
    const ids = new Set<string>();
    for (const p of pins) {
      if (stormIndexHasWithin(stormPinIndex, p.lat, p.lon, STORM_BUFFER_M)) ids.add(p.id);
    }
    return ids;
  }, [stormPinIndex, pins]);

  // ── Load active canvas_area_assignments (RLS scopes: canvasser sees their own,
  //    ASM/TL/manager sees all). Refetched after each new assignment is created.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_active_canvas_areas_geojson");
      if (cancelled) return;
      if (error) {
        // Soft-fail: log but don't block the map
        console.warn("get_active_canvas_areas_geojson:", error.message);
        return;
      }
      setAssignedAreasGeoJson(data ?? { type: "FeatureCollection", features: [] });
    })();
    return () => { cancelled = true; };
  // savingAssignment as a dep so a freshly-created area appears on the map immediately
  }, [savingAssignment]);

  // ?area=<id> — fit the map to that area once its outline has loaded. RLS
  // decides what's there: a rep only gets their own areas, a manager every
  // area. Someone else's area lives in the toggleable "others" layer, so that
  // layer is switched on for it.
  const areaFitDoneRef = useRef(false);
  useEffect(() => {
    if (!areaLink.area || areaFitDoneRef.current) return;
    const map = mapRef.current;
    const feats = ((assignedAreasGeoJson?.features ?? []) as any[])
      .filter((f) => f?.properties?.area_group_id === areaLink.area);
    if (!map || feats.length === 0) return;
    const coords: Array<[number, number]> = [];
    for (const f of feats) {
      const g = f.geometry;
      const polys = g?.type === "Polygon" ? [g.coordinates] : g?.type === "MultiPolygon" ? g.coordinates : [];
      for (const poly of polys) for (const ring of poly) for (const c of ring) coords.push([c[0], c[1]]);
    }
    if (coords.length === 0) return;
    areaFitDoneRef.current = true;
    startedOnDefaultViewRef.current = false;
    const bounds = coords.reduce((b: any, c) => b.extend(c), new mapboxgl.LngLatBounds(coords[0], coords[0]));
    map.fitBounds(bounds, { padding: 60, maxZoom: 16.5, duration: 0 });
    if (feats.some((f) => f?.properties?.assigned_to !== member?.id)) setShowAreas(true);
  }, [areaLink.area, assignedAreasGeoJson, member?.id]);

  // ?assign=1&rep=<id> — open draw-an-area mode with that person pre-picked.
  const assignLinkDoneRef = useRef(false);
  useEffect(() => {
    if (!areaLink.assign || assignLinkDoneRef.current || !isManager) return;
    assignLinkDoneRef.current = true;
    enterAssignMode();
    if (areaLink.rep) setSelectedReps(new Set([areaLink.rep]));
    if (areaLink.label) setAreaLabel(areaLink.label);
    setAreaForDate(areaLink.forDate);
    setAreaAnchor(areaLink.anchor ? { dealId: areaLink.anchor, kind: areaLink.kind ?? "other" } : null);
  }, [areaLink.assign, areaLink.rep, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist toggle state to sessionStorage on every change (Fix #2+#3) ────
  // mapStyle / showStorms / showRadar / showLsrHail / radarHighConf /
  // stormIncludeSub1 all hydrate from sessionStorage in their useState
  // initializers above; mirror writes here so future remounts see the
  // current values. Each effect is intentionally narrow so unrelated state
  // changes don't trigger writes.
  useEffect(() => { writeSession("bardin.canvas.mapStyle", mapStyle); }, [mapStyle]);
  useEffect(() => { writeSession("bardin.canvas.showStorms", showStorms); }, [showStorms]);
  useEffect(() => { writeSession("bardin.canvas.showRadar", showRadar); }, [showRadar]);
  useEffect(() => { writeSession("bardin.canvas.showLsrHail", showLsrHail); }, [showLsrHail]);
  useEffect(() => { writeSession("bardin.canvas.showLsrWind", showLsrWind); }, [showLsrWind]);
  useEffect(() => { writeSession("bardin.canvas.windMinMph", String(windMinMph)); }, [windMinMph]);
  useEffect(() => { writeSession("bardin.canvas.radarHighConf", radarHighConf); }, [radarHighConf]);
  useEffect(() => { writeSession("bardin.canvas.stormIncludeSub1", stormIncludeSub1); }, [stormIncludeSub1]);
  useEffect(() => { writeSession("bardin.canvas.showAreas", showAreas); }, [showAreas]);

  // ── Init Mapbox map ────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) {
      setErr("Mapbox token not configured. Set VITE_MAPBOX_TOKEN in .env.local.");
      return;
    }
    if (typeof mapboxgl === "undefined") {
      setErr("Mapbox GL didn't load. Check the CDN script tag in index.html.");
      return;
    }
    mapboxgl.accessToken = MAPBOX_TOKEN;
    // Fix #2 (May 11, 2026): hydrate map view from sessionStorage so the
    // user keeps their neighborhood when /canvas/appt unmounts Canvas and
    // remounts it on submit. Falls back to the default north-Atlanta view.
    const initialView = readMapViewFromSession();
    startedOnDefaultViewRef.current = !areaLink.center && !areaLink.area && !stormLink.center && !initialView;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: mapStyle === "satellite"
        ? "mapbox://styles/mapbox/satellite-streets-v12"
        : "mapbox://styles/mapbox/streets-v12",
      center: areaLink.center ?? stormLink.center ?? initialView?.center ?? [-84.5, 33.95],   // north Atlanta default
      zoom: areaLink.center ? areaLink.zoom : stormLink.center ? 11 : (initialView?.zoom ?? 10),
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;
    // Once the rep moves the map themselves, the first GPS fix leaves it alone.
    map.on("movestart", (e: any) => { if (e?.originalEvent) startedOnDefaultViewRef.current = false; });

    // Persist view on every meaningful move/zoom so the next mount picks up
    // exactly where we left off. moveend fires for both pan and zoom.
    const onMoveEnd = () => {
      try {
        const c = map.getCenter();
        const z = map.getZoom();
        sessionStorage.setItem("bardin.canvas.mapView", JSON.stringify({
          center: [c.lng, c.lat], zoom: z,
        }));
      } catch { /* ignore quota / private-mode errors */ }
    };
    map.on("moveend", onMoveEnd);

    // Signal to the bbox-fetch effect (declared above this one but
    // gated on mapInited) that mapRef.current is valid and it's safe to
    // attach idle/moveend listeners.
    setMapInited(true);

    return () => {
      map.off("moveend", onMoveEnd);
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
      setMapInited(false);
    };
  }, []);  // intentionally only on mount; style toggles handled below

  // ── Restyle when toggle flips ──────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const target = mapStyle === "satellite"
      ? "mapbox://styles/mapbox/satellite-streets-v12"
      : "mapbox://styles/mapbox/streets-v12";
    map.setStyle(target);
  }, [mapStyle]);

  // ── Storm zone overlay: GeoJSON source + cluster layers ────────────────
  // Mapbox setStyle() wipes all sources/layers, so we re-add on every "style.load" event.
  // NOTE: passes stormGeoJsonDisplayed / lsrGeoJsonDisplayed (not the raw fetches)
  // so the manager's excluded-date filter is honored at the source level —
  // critical for cluster_count which is computed at source aggregation time
  // and ignores any setFilter() on the cluster layer.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !stormGeoJsonDisplayed) return;

    function addStormLayers() {
      // Source 1: clustered (drives the bubble + count layers at low zooms)
      if (!map.getSource("storm-sigs")) {
        map.addSource("storm-sigs", {
          type: "geojson",
          data: stormGeoJsonDisplayed,
          cluster: true,
          clusterRadius: 50,
          clusterMaxZoom: 14,
          clusterProperties: { max_hail: ["max", ["get", "max_size_in"]] },
        });
      } else {
        (map.getSource("storm-sigs") as any).setData(stormGeoJsonDisplayed);
      }

      // Source 2: NOT clustered — drives the 1-mile geographic-radius buffer fills.
      // Same data, separate source so the buffers render at every zoom regardless
      // of clustering state.
      if (!map.getSource("storm-buffers")) {
        map.addSource("storm-buffers", { type: "geojson", data: stormGeoJsonDisplayed, cluster: false });
      } else {
        (map.getSource("storm-buffers") as any).setData(stormGeoJsonDisplayed);
      }

      // 1-mile radius buffer fills (bottom of the stack). Mapbox interpolate base-2
      // makes circle-radius double per zoom level, exactly tracking mercator's
      // pixels-per-meter. Calibrated for lat ~34° (Georgia) using Mapbox GL JS v2
      // 512px tile formula (z+9): 1 mi ≈ 6.4 px @ z8, 406 px @ z14, 1626 px @ z16.
      // Low alpha so overlap visually sums into a corridor — multiple radar signatures
      // in the same area = darker = worse storm.
      if (!map.getLayer("storm-buffer-fills")) {
        map.addLayer({
          id: "storm-buffer-fills",
          type: "circle",
          source: "storm-buffers",
          paint: {
            "circle-color": [
              // Sub-1.0" cells (NEXRAD low-confidence tier) render slate so they
              // don't visually masquerade as confident hail damage.
              "step", ["get", "max_size_in"],
              "#94a3b8", 1.0, "#fbbf24", 1.5, "#f46530", 2.0, "#dc2626",
            ],
            "circle-radius": [
              "interpolate", ["exponential", 2], ["zoom"],
              8, 6.4,  10, 25.4,  12, 101.4,  14, 406,  16, 1626,
            ],
            "circle-opacity": 0.28,
            "circle-pitch-alignment": "map",
            "circle-stroke-width": 0,
          },
        });
      }

      // Cluster bubbles — sit on top of the buffer fills
      if (!map.getLayer("storm-clusters")) {
        map.addLayer({
          id: "storm-clusters",
          type: "circle",
          source: "storm-sigs",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": [
              "step", ["coalesce", ["get", "max_hail"], 1.0],
              "#94a3b8", 1.0, "#fbbf24", 1.5, "#f46530", 2.0, "#dc2626",
            ],
            "circle-radius": [
              "step", ["get", "point_count"],
              14, 5, 18, 20, 22, 50, 26, 100, 30,
            ],
            "circle-opacity": 0.55,
            "circle-stroke-width": 1.5,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-opacity": 0.8,
          },
        });
      }
      if (!map.getLayer("storm-cluster-count")) {
        map.addLayer({
          id: "storm-cluster-count",
          type: "symbol",
          source: "storm-sigs",
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["concat", ["get", "point_count_abbreviated"]],
            "text-size": 12,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          },
          paint: { "text-color": "#1f2937" },
        });
      }
      if (!map.getLayer("storm-points")) {
        map.addLayer({
          id: "storm-points",
          type: "circle",
          source: "storm-sigs",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": [
              "step", ["get", "max_size_in"],
              "#94a3b8", 1.0, "#fbbf24", 1.5, "#f46530", 2.0, "#dc2626",
            ],
            "circle-radius": [
              "step", ["get", "max_size_in"],
              3, 1.0, 4, 1.5, 6, 2.0, 8,
            ],
            "circle-opacity": [
              // Sub-1.0" dots are more transparent so they recede visually.
              "step", ["get", "max_size_in"],
              0.55, 1.0, 0.95,
            ],
            "circle-stroke-width": 1.5,
            "circle-stroke-color": "#ffffff",
          },
        });

        // Click handlers — add once when storm-points layer is first added
        map.on("click", "storm-clusters", (e: any) => {
          const features = map.queryRenderedFeatures(e.point, { layers: ["storm-clusters"] });
          if (!features.length) return;
          const clusterId = features[0].properties.cluster_id;
          map.getSource("storm-sigs").getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
            if (err) return;
            map.easeTo({ center: features[0].geometry.coordinates, zoom });
          });
        });
        map.on("click", "storm-points", (e: any) => {
          const f = e.features?.[0];
          if (!f) return;
          const size = Number(f.properties.max_size_in);
          const sizeIn = size.toFixed(2);
          const date = new Date(f.properties.occurred_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
          const lowConfidence = size < 1.0;
          const sizeColor = lowConfidence ? "#64748b" : "#f46530";
          const lowConfidenceNote = lowConfidence
            ? `<div style="color:#b45309;background:#fef3c7;border:1px solid #fde68a;border-radius:4px;padding:4px 6px;margin-top:6px;font-size:11px;">⚠ NEXRAD low-confidence tier. Often pairs with 1.0–1.25″ on the ground.</div>`
            : "";
          new mapboxgl.Popup({ closeButton: true })
            .setLngLat(f.geometry.coordinates)
            .setHTML(`<div style="font:13px -apple-system,sans-serif;color:#2a2e30;">
              <div style="font-weight:700;font-size:15px;color:${sizeColor};">${sizeIn}″ hail</div>
              <div style="color:#7a7e83;font-size:11px;margin-top:2px;">NEXRAD · ${date}</div>
              ${lowConfidenceNote}
            </div>`)
            .addTo(map);
        });
        map.on("mouseenter", "storm-clusters", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "storm-clusters", () => { map.getCanvas().style.cursor = ""; });
        map.on("mouseenter", "storm-points", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "storm-points", () => { map.getCanvas().style.cursor = ""; });
      }

      // ── NWS LSR hail layer (ground-truth spotter reports) ─────────────────
      // Rendered as hollow rings (white fill + thick colored stroke) so they
      // remain visually distinct from radar's solid dots. No clustering — at
      // ~70 hail LSRs / 365d statewide the volume is fine without it.
      const emptyFc = { type: "FeatureCollection", features: [] };
      if (!map.getSource("storm-lsr-hail")) {
        map.addSource("storm-lsr-hail", { type: "geojson", data: lsrGeoJsonDisplayed ?? emptyFc });
      } else {
        (map.getSource("storm-lsr-hail") as any).setData(lsrGeoJsonDisplayed ?? emptyFc);
      }
      // 1-mile radius buffer around each NWS hail report. Same Mercator-tracking
      // radius interpolation as storm-buffer-fills (radar buffers above) — see
      // that block's comment for the math. Distinguished from radar buffers via
      // higher base opacity (0.30 vs 0.28) + visible 1.5px stroke so canvassers
      // can see the perimeter of the ground-truth report's coverage zone.
      // Inserted BELOW the LSR ring marker (storm-lsr-hail-points) so the dot
      // sits on top of its own halo.
      if (!map.getLayer("storm-lsr-hail-buffers")) {
        map.addLayer({
          id: "storm-lsr-hail-buffers",
          type: "circle",
          source: "storm-lsr-hail",
          paint: {
            "circle-color": [
              "step", ["get", "max_size_in"],
              "#94a3b8", 1.0, "#fbbf24", 1.5, "#f46530", 2.0, "#dc2626",
            ],
            "circle-radius": [
              "interpolate", ["exponential", 2], ["zoom"],
              8, 6.4,  10, 25.4,  12, 101.4,  14, 406,  16, 1626,
            ],
            "circle-opacity": 0.18,
            "circle-pitch-alignment": "map",
            "circle-stroke-width": 1.5,
            "circle-stroke-color": [
              "step", ["get", "max_size_in"],
              "#94a3b8", 1.0, "#fbbf24", 1.5, "#f46530", 2.0, "#dc2626",
            ],
            "circle-stroke-opacity": 0.7,
          },
        });
      }
      if (!map.getLayer("storm-lsr-hail-points")) {
        map.addLayer({
          id: "storm-lsr-hail-points",
          type: "circle",
          source: "storm-lsr-hail",
          paint: {
            "circle-color": "#ffffff",
            "circle-radius": [
              "step", ["get", "max_size_in"],
              4, 1.0, 5, 1.5, 7, 2.0, 9,
            ],
            "circle-stroke-width": 2.5,
            "circle-stroke-color": [
              "step", ["get", "max_size_in"],
              "#94a3b8", 1.0, "#fbbf24", 1.5, "#f46530", 2.0, "#dc2626",
            ],
            "circle-opacity": 1.0,
          },
        });

        map.on("click", "storm-lsr-hail-points", (e: any) => {
          const f = e.features?.[0];
          if (!f) return;
          // Full HTML-escape since this popup interpolates NWS-sourced strings
          // into setHTML. NWS data is well-controlled but any change at IEM
          // shouldn't be able to inject script tags or close the wrapper div.
          const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => (
            { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string
          ));
          const size = Number(f.properties.max_size_in);
          const sizeIn = size.toFixed(2);
          const date = new Date(f.properties.occurred_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
          const loc = esc(f.properties.location ?? "—");
          const county = esc(f.properties.county ?? "");
          const state = esc(f.properties.state ?? "");
          const sourceKind = esc(f.properties.source_kind ?? "");
          const remarks = esc(f.properties.remarks ?? "");
          // Source badge — small chip showing who filed the report. Icon by
          // reliability tier: trained / official / LE / Cocorahs = ✅,
          // emergency mgr / fire / 911 / amateur radio / broadcast = 📡,
          // public = 📞 (most common but lowest reliability).
          const sourceLower = sourceKind.toLowerCase();
          let sourceIcon = "📋";
          if (sourceLower.includes("trained") || sourceLower.includes("official") || sourceLower.includes("law") || sourceLower.includes("cocorahs")) sourceIcon = "✅";
          else if (sourceLower.includes("emergency") || sourceLower.includes("fire") || sourceLower.includes("911") || sourceLower.includes("amateur") || sourceLower.includes("broadcast")) sourceIcon = "📡";
          else if (sourceLower.includes("public")) sourceIcon = "📞";
          const sourceHtml = sourceKind
            ? `<div style="display:inline-block;margin-top:4px;padding:1px 6px;border-radius:10px;background:#f1f5f9;color:#475569;font-size:10px;font-weight:600;">${sourceIcon} ${sourceKind}</div>`
            : "";
          const remarksHtml = remarks
            ? `<div style="color:#475569;font-size:11px;margin-top:6px;padding-top:6px;border-top:1px solid #e2e8f0;line-height:1.4;">${remarks}</div>`
            : "";
          new mapboxgl.Popup({ closeButton: true, maxWidth: "280px" })
            .setLngLat(f.geometry.coordinates)
            .setHTML(`<div style="font:13px -apple-system,sans-serif;color:#2a2e30;">
              <div style="font-weight:700;font-size:15px;color:#f46530;">${sizeIn}″ hail · NWS report</div>
              <div style="color:#7a7e83;font-size:11px;margin-top:2px;">${loc}${county ? ` · ${county} Co.` : ""}${state ? `, ${state}` : ""}</div>
              <div style="color:#7a7e83;font-size:11px;">${date}</div>
              ${sourceHtml}
              ${remarksHtml}
            </div>`)
            .addTo(map);
        });
        map.on("mouseenter", "storm-lsr-hail-points", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "storm-lsr-hail-points", () => { map.getCanvas().style.cursor = ""; });
      }

      // ── NWS LSR wind layer (measured/estimated gust reports, Jul 12, 2026) ─
      // Blue → violet palette (WIND_STEP_EXPR) so wind never reads as hail.
      // Same hollow-ring-over-1-mile-halo construction as the hail LSR layer;
      // no clustering (~150 features/yr at the 30 mph server floor).
      if (!map.getSource("storm-lsr-wind")) {
        map.addSource("storm-lsr-wind", { type: "geojson", data: lsrWindGeoJsonDisplayed ?? emptyFc });
      } else {
        (map.getSource("storm-lsr-wind") as any).setData(lsrWindGeoJsonDisplayed ?? emptyFc);
      }
      // 1-mile radius halo — identical Mercator-tracking interpolation as the
      // hail buffers (see storm-buffer-fills comment for the math).
      if (!map.getLayer("storm-lsr-wind-buffers")) {
        map.addLayer({
          id: "storm-lsr-wind-buffers",
          type: "circle",
          source: "storm-lsr-wind",
          paint: {
            "circle-color": WIND_STEP_EXPR,
            "circle-radius": [
              "interpolate", ["exponential", 2], ["zoom"],
              8, 6.4,  10, 25.4,  12, 101.4,  14, 406,  16, 1626,
            ],
            "circle-opacity": 0.18,
            "circle-pitch-alignment": "map",
            "circle-stroke-width": 1.5,
            "circle-stroke-color": WIND_STEP_EXPR,
            "circle-stroke-opacity": 0.7,
          },
        });
      }
      if (!map.getLayer("storm-lsr-wind-points")) {
        map.addLayer({
          id: "storm-lsr-wind-points",
          type: "circle",
          source: "storm-lsr-wind",
          paint: {
            "circle-color": "#ffffff",
            "circle-radius": [
              "step", ["get", "speed_mph"],
              4, 50, 5, 58, 7, 70, 9,
            ],
            "circle-stroke-width": 2.5,
            "circle-stroke-color": WIND_STEP_EXPR,
            "circle-opacity": 1.0,
          },
        });

        map.on("click", "storm-lsr-wind-points", (e: any) => {
          const f = e.features?.[0];
          if (!f) return;
          // Same full HTML-escape rationale as the hail LSR popup — NWS strings
          // go through setHTML and must never be able to inject markup.
          const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => (
            { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string
          ));
          const mph = Number(f.properties.speed_mph);
          const date = new Date(f.properties.occurred_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
          const loc = esc(f.properties.location ?? "—");
          const county = esc(f.properties.county ?? "");
          const state = esc(f.properties.state ?? "");
          const sourceKind = esc(f.properties.source_kind ?? "");
          const remarks = esc(f.properties.remarks ?? "");
          const sourceLower = sourceKind.toLowerCase();
          let sourceIcon = "📋";
          if (sourceLower.includes("trained") || sourceLower.includes("official") || sourceLower.includes("law") || sourceLower.includes("cocorahs") || sourceLower.includes("survey") || sourceLower.includes("asos") || sourceLower.includes("awos") || sourceLower.includes("mesonet")) sourceIcon = "✅";
          else if (sourceLower.includes("emergency") || sourceLower.includes("fire") || sourceLower.includes("911") || sourceLower.includes("amateur") || sourceLower.includes("broadcast")) sourceIcon = "📡";
          else if (sourceLower.includes("public")) sourceIcon = "📞";
          const sourceHtml = sourceKind
            ? `<div style="display:inline-block;margin-top:4px;padding:1px 6px;border-radius:10px;background:#f1f5f9;color:#475569;font-size:10px;font-weight:600;">${sourceIcon} ${sourceKind}</div>`
            : "";
          const remarksHtml = remarks
            ? `<div style="color:#475569;font-size:11px;margin-top:6px;padding-top:6px;border-top:1px solid #e2e8f0;line-height:1.4;">${remarks}</div>`
            : "";
          new mapboxgl.Popup({ closeButton: true, maxWidth: "280px" })
            .setLngLat(f.geometry.coordinates)
            .setHTML(`<div style="font:13px -apple-system,sans-serif;color:#2a2e30;">
              <div style="font-weight:700;font-size:15px;color:${windColor(mph)};">💨 ${Math.round(mph)} mph wind · NWS report</div>
              <div style="color:#7a7e83;font-size:11px;margin-top:2px;">${loc}${county ? ` · ${county} Co.` : ""}${state ? `, ${state}` : ""}</div>
              <div style="color:#7a7e83;font-size:11px;">${date}</div>
              ${sourceHtml}
              ${remarksHtml}
            </div>`)
            .addTo(map);
        });
        map.on("mouseenter", "storm-lsr-wind-points", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "storm-lsr-wind-points", () => { map.getCanvas().style.cursor = ""; });
      }

      // Apply current visibility to all storm layers (per-layer toggle)
      const radarVis = (showStorms && showRadar) ? "visible" : "none";
      const lsrVis   = (showStorms && showLsrHail) ? "visible" : "none";
      const windVis  = (showStorms && showLsrWind) ? "visible" : "none";
      ["storm-buffer-fills", "storm-clusters", "storm-cluster-count", "storm-points"].forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", radarVis);
      });
      ["storm-lsr-hail-buffers", "storm-lsr-hail-points"].forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", lsrVis);
      });
      ["storm-lsr-wind-buffers", "storm-lsr-wind-points"].forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", windVis);
      });
    }

    if (map.isStyleLoaded()) {
      addStormLayers();
    } else {
      map.once("style.load", addStormLayers);
    }
    // Re-add layers whenever the basemap style flips (setStyle nukes them)
    map.on("style.load", addStormLayers);
    return () => { map.off("style.load", addStormLayers); };
    // showStorms/showRadar/showLsrHail/showLsrWind included so the style.load
    // listener always re-registers with a fresh closure — otherwise a basemap
    // toggle after the user flipped a sub-toggle would recreate layers using
    // stale visibility values captured at the last render-effect run.
  }, [stormGeoJsonDisplayed, lsrGeoJsonDisplayed, lsrWindGeoJsonDisplayed, mapStyle, showStorms, showRadar, showLsrHail, showLsrWind]);

  // Toggle storm overlay visibility without re-adding layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const radarVis = (showStorms && showRadar) ? "visible" : "none";
    const lsrVis   = (showStorms && showLsrHail) ? "visible" : "none";
    const windVis  = (showStorms && showLsrWind) ? "visible" : "none";
    ["storm-buffer-fills", "storm-clusters", "storm-cluster-count", "storm-points"].forEach((id) => {
      if (map.getLayer && map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", radarVis);
      }
    });
    ["storm-lsr-hail-buffers", "storm-lsr-hail-points"].forEach((id) => {
      if (map.getLayer && map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", lsrVis);
      }
    });
    ["storm-lsr-wind-buffers", "storm-lsr-wind-points"].forEach((id) => {
      if (map.getLayer && map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", windVis);
      }
    });
  }, [showStorms, showRadar, showLsrHail, showLsrWind]);

  // ── Split assigned areas into "mine" (always visible) and "others" (toggleable)
  const myAreasGeoJson = useMemo(() => {
    if (!assignedAreasGeoJson || !member) return { type: "FeatureCollection", features: [] };
    return {
      type: "FeatureCollection",
      features: (assignedAreasGeoJson.features ?? []).filter((f: any) => f.properties?.assigned_to === member.id),
    };
  }, [assignedAreasGeoJson, member]);

  const otherAreasGeoJson = useMemo(() => {
    if (!assignedAreasGeoJson || !member) return assignedAreasGeoJson ?? { type: "FeatureCollection", features: [] };
    return {
      type: "FeatureCollection",
      features: (assignedAreasGeoJson.features ?? []).filter((f: any) => f.properties?.assigned_to !== member.id),
    };
  }, [assignedAreasGeoJson, member]);

  // Quick win #5 — count pins inside each polygon and stamp pin_count onto
  // each feature's properties so the symbol layer can render "N pins" in
  // the label. Client-side point-in-polygon (ray-casting) over the local
  // pins array. Cheap: O(pins × areas × ring_vertices).
  const myAreasGeoJsonWithCounts = useMemo(
    () => enrichAreasWithPinCount(myAreasGeoJson, pins),
    [myAreasGeoJson, pins]
  );
  const otherAreasGeoJsonWithCounts = useMemo(
    () => enrichAreasWithPinCount(otherAreasGeoJson, pins),
    [otherAreasGeoJson, pins]
  );

  // ── Render the two layers. "Mine" stronger (peach tint) + always visible;
  //    "Others" (teal) controlled by the 📐 toggle so a TL/ASM/manager can
  //    flip it on to see the broader picture (and off again to declutter).
  //    May 13, 2026 fix: visibility is reapplied on every render-effect run
  //    so basemap toggles / pin-driven data refreshes don't quietly reset
  //    others-areas back to "visible" while the user has the toggle off.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const othersVis: "visible" | "none" = showAreas ? "visible" : "none";

    function setOrUpdateAreas() {
      // MY areas — peach gradient, always visible
      if (!map.getSource("my-areas")) {
        map.addSource("my-areas", { type: "geojson", data: myAreasGeoJsonWithCounts });
        map.addLayer({
          id: "my-areas-fill",
          type: "fill",
          source: "my-areas",
          layout: { visibility: "visible" },
          paint: { "fill-color": "#ff9e7a", "fill-opacity": 0.22 },
        });
        map.addLayer({
          id: "my-areas-line",
          type: "line",
          source: "my-areas",
          layout: { visibility: "visible" },
          paint: { "line-color": "#f46530", "line-width": 3, "line-opacity": 1 },
        });
        map.addLayer({
          id: "my-areas-label",
          type: "symbol",
          source: "my-areas",
          layout: {
            visibility: "visible",
            // "★ <area_label> · YOUR AREA · N PINS" — pin count appended when
            // present. Count is computed client-side from the row-level pins,
            // which are only fetched at detail zoom (>= DETAIL_MIN_ZOOM), so
            // below z12 the label drops the count instead of lying "0 PINS".
            // (Layout camera expressions evaluate at integer zooms — 12 is the
            // first integer step at/above DETAIL_MIN_ZOOM.)
            "text-field": ["step", ["zoom"],
              ["concat",
                ["case",
                  ["has", "area_label"], ["concat", "★ ", ["get", "area_label"], " · "],
                  "★ ",
                ],
                "YOUR AREA",
              ],
              12,
              ["concat",
                ["case",
                  ["has", "area_label"], ["concat", "★ ", ["get", "area_label"], " · "],
                  "★ ",
                ],
                "YOUR AREA · ",
                ["case",
                  ["has", "pin_count"], ["concat", ["to-string", ["get", "pin_count"]], " PINS"],
                  "0 PINS",
                ],
              ],
            ],
            "text-size": 11,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-offset": [0, 0.6],
            "text-anchor": "top",
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#9a3412",
            "text-halo-color": "#ffffff",
            "text-halo-width": 2,
          },
        });
      } else {
        (map.getSource("my-areas") as any).setData(myAreasGeoJsonWithCounts);
      }

      // OTHERS' areas — teal, dimmer, controlled by the 📐 toggle
      if (!map.getSource("others-areas")) {
        map.addSource("others-areas", { type: "geojson", data: otherAreasGeoJsonWithCounts });
        map.addLayer({
          id: "others-areas-fill",
          type: "fill",
          source: "others-areas",
          layout: { visibility: othersVis },
          paint: { "fill-color": "#0d9488", "fill-opacity": 0.13 },
        });
        map.addLayer({
          id: "others-areas-line",
          type: "line",
          source: "others-areas",
          layout: { visibility: othersVis },
          paint: { "line-color": "#0d9488", "line-width": 2, "line-opacity": 0.8 },
        });
        map.addLayer({
          id: "others-areas-label",
          type: "symbol",
          source: "others-areas",
          layout: {
            visibility: othersVis,
            // Same zoom-gating as my-areas-label: counts only at detail zoom.
            "text-field": ["step", ["zoom"],
              ["concat",
                ["case",
                  ["has", "area_label"], ["concat", ["get", "area_label"], " · "],
                  "",
                ],
                ["get", "assigned_to_name"],
              ],
              12,
              ["concat",
                ["case",
                  ["has", "area_label"], ["concat", ["get", "area_label"], " · "],
                  "",
                ],
                ["get", "assigned_to_name"],
                ["case",
                  ["has", "pin_count"], ["concat", " · ", ["to-string", ["get", "pin_count"]], " pins"],
                  "",
                ],
              ],
            ],
            "text-size": 11,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-offset": [0, 0.5],
            "text-anchor": "top",
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#134e4a",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5,
          },
        });
      } else {
        (map.getSource("others-areas") as any).setData(otherAreasGeoJsonWithCounts);
      }

      // Re-apply current visibility every run — protects against the case
      // where this effect re-runs (data refresh, mapStyle change) and would
      // otherwise leave the just-re-created others-areas-* layers at their
      // default "visible" state while the user has the 📐 toggle off.
      ["my-areas-fill","my-areas-line","my-areas-label"].forEach((id) => {
        if (map.getLayer && map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", "visible");
        }
      });
      ["others-areas-fill","others-areas-line","others-areas-label"].forEach((id) => {
        if (map.getLayer && map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", othersVis);
        }
      });
    }

    if (map.isStyleLoaded()) setOrUpdateAreas();
    else map.once("style.load", setOrUpdateAreas);
    map.on("style.load", setOrUpdateAreas);
    return () => { map.off("style.load", setOrUpdateAreas); };
  }, [myAreasGeoJsonWithCounts, otherAreasGeoJsonWithCounts, mapStyle, showAreas]);

  // 📐 toggle — flip others-areas visibility on demand. my-areas stay on
  // always (the assignee should always see their own zone).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const vis = showAreas ? "visible" : "none";
    ["others-areas-fill","others-areas-line","others-areas-label"].forEach((id) => {
      if (map.getLayer && map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", vis);
      }
    });
    ["my-areas-fill","my-areas-line","my-areas-label"].forEach((id) => {
      if (map.getLayer && map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", "visible");
      }
    });
  }, [showAreas]);

  // ── Ship 10 v2: freehand polygon draw — raw DOM events on the canvas ──
  // We bypass Mapbox's high-level event system because it debounces/suppresses
  // touchmove frames thinking they're pan gestures. Direct DOM listeners on the
  // canvas element capture every frame. Points accumulate in a ref (no React
  // re-render per move); a single requestAnimationFrame ticker pushes the
  // current ring to state at ~60fps. Result: line tracks the finger 1:1.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!assignMode) return;

    // Disable EVERY interaction handler so the gesture goes only to our draw.
    map.boxZoom?.disable();
    map.scrollZoom?.disable();
    map.dragPan?.disable();
    map.dragRotate?.disable();
    map.keyboard?.disable();
    map.doubleClickZoom?.disable();
    map.touchZoomRotate?.disable();
    map.touchPitch?.disable();

    const canvas: HTMLCanvasElement = map.getCanvas();
    canvas.style.cursor = "crosshair";
    canvas.style.touchAction = "none";   // tells iOS not to intercept touches as scroll/zoom

    let drawing = false;
    const pts: Array<[number, number]> = [];
    let rafQueued = false;

    function flushToState() {
      rafQueued = false;
      setPolygonPoints(pts.slice());
    }
    function queueFlush() {
      if (rafQueued) return;
      rafQueued = true;
      requestAnimationFrame(flushToState);
    }

    function pointFromXY(clientX: number, clientY: number): [number, number] | null {
      const rect = canvas.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      try {
        const ll = map.unproject([px, py]);
        return [ll.lng, ll.lat];
      } catch {
        return null;
      }
    }

    function getXY(e: TouchEvent | MouseEvent): { x: number; y: number } | null {
      if ((e as TouchEvent).touches && (e as TouchEvent).touches.length > 0) {
        const t = (e as TouchEvent).touches[0];
        return { x: t.clientX, y: t.clientY };
      }
      if ((e as TouchEvent).changedTouches && (e as TouchEvent).changedTouches.length > 0) {
        const t = (e as TouchEvent).changedTouches[0];
        return { x: t.clientX, y: t.clientY };
      }
      const me = e as MouseEvent;
      if (typeof me.clientX === "number") return { x: me.clientX, y: me.clientY };
      return null;
    }

    function onDown(e: TouchEvent | MouseEvent) {
      // Single-touch only (ignore pinch). For mouse, only respond to the primary button.
      if ("touches" in e && e.touches.length > 1) return;
      if (!("touches" in e) && (e as MouseEvent).button !== 0) return;
      const xy = getXY(e);
      if (!xy) return;
      const p = pointFromXY(xy.x, xy.y);
      if (!p) return;
      e.preventDefault();
      drawing = true;
      pts.length = 0;
      pts.push(p);
      setIsDrawing(true);
      queueFlush();
    }

    function onMove(e: TouchEvent | MouseEvent) {
      if (!drawing) return;
      // Reject multi-touch interruption (e.g. user starts pinching mid-draw)
      if ("touches" in e && e.touches.length > 1) return;
      const xy = getXY(e);
      if (!xy) return;
      const p = pointFromXY(xy.x, xy.y);
      if (!p) return;
      e.preventDefault();
      // Skip points closer than ~1px to the previous (no-ops are wasted work)
      const last = pts[pts.length - 1];
      if (last) {
        const rect = canvas.getBoundingClientRect();
        const lastPx = map.project(last);
        const dx = xy.x - rect.left - lastPx.x;
        const dy = xy.y - rect.top - lastPx.y;
        if (dx * dx + dy * dy < 1) return;
      }
      pts.push(p);
      queueFlush();
    }

    function onUp(e: TouchEvent | MouseEvent) {
      if (!drawing) return;
      drawing = false;
      e.preventDefault();
      setIsDrawing(false);
      if (pts.length < 3) {
        pts.length = 0;
        setPolygonPoints([]);
        return;
      }
      // Auto-close
      const first = pts[0];
      const last = pts[pts.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) pts.push(first);
      setPolygonPoints(pts.slice());
    }

    // Touch events MUST be {passive: false} so preventDefault works (iOS Safari)
    canvas.addEventListener("touchstart", onDown, { passive: false });
    canvas.addEventListener("touchmove",  onMove, { passive: false });
    canvas.addEventListener("touchend",   onUp,   { passive: false });
    canvas.addEventListener("touchcancel", onUp,  { passive: false });
    canvas.addEventListener("mousedown",  onDown);
    canvas.addEventListener("mousemove",  onMove);
    canvas.addEventListener("mouseup",    onUp);
    canvas.addEventListener("mouseleave", onUp);

    return () => {
      map.boxZoom?.enable();
      map.scrollZoom?.enable();
      map.dragPan?.enable();
      map.dragRotate?.enable();
      map.keyboard?.enable();
      map.doubleClickZoom?.enable();
      map.touchZoomRotate?.enable();
      map.touchPitch?.enable();
      canvas.style.cursor = "";
      canvas.style.touchAction = "";
      canvas.removeEventListener("touchstart", onDown);
      canvas.removeEventListener("touchmove",  onMove);
      canvas.removeEventListener("touchend",   onUp);
      canvas.removeEventListener("touchcancel", onUp);
      canvas.removeEventListener("mousedown",  onDown);
      canvas.removeEventListener("mousemove",  onMove);
      canvas.removeEventListener("mouseup",    onUp);
      canvas.removeEventListener("mouseleave", onUp);
    };
  }, [assignMode]);

  // ── Render the drawn polygon as a GeoJSON layer ──────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    function setOrUpdatePolygon() {
      const data = polygonPoints.length >= 2
        ? polygonToGeoJson(polygonPoints, isDrawing)
        : { type: "FeatureCollection" as const, features: [] };

      if (!map.getSource("assign-polygon")) {
        map.addSource("assign-polygon", { type: "geojson", data });
        map.addLayer({
          id: "assign-polygon-fill",
          type: "fill",
          source: "assign-polygon",
          // Only fill closed polygons (skip the in-progress LineString)
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: { "fill-color": "#f46530", "fill-opacity": 0.18 },
        });
        map.addLayer({
          id: "assign-polygon-line",
          type: "line",
          source: "assign-polygon",
          paint: {
            "line-color": "#f46530",
            "line-width": 2.5,
            "line-dasharray": isDrawing ? [1, 0] : [3, 2],   // solid while drawing, dashed when closed
          },
        });
      } else {
        (map.getSource("assign-polygon") as any).setData(data);
        // Update line style if drawing state flipped
        if (map.getLayer("assign-polygon-line")) {
          map.setPaintProperty("assign-polygon-line", "line-dasharray", isDrawing ? [1, 0] : [3, 2]);
        }
      }
    }

    if (map.isStyleLoaded()) setOrUpdatePolygon();
    else map.once("style.load", setOrUpdatePolygon);
    map.on("style.load", setOrUpdatePolygon);
    return () => { map.off("style.load", setOrUpdatePolygon); };
  }, [polygonPoints, isDrawing, mapStyle]);

  // ── Load active roster when entering assign mode ─────────────────────
  useEffect(() => {
    if (!assignMode) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_roster")
        .select("id, full_name, role")
        .eq("is_active", true)
        .order("full_name");
      if (cancelled) return;
      setActiveRoster((data ?? []) as any);
    })();
    return () => { cancelled = true; };
  }, [assignMode]);

  // Pins inside the drawn polygon — only counts when polygon is closed (drawing finished).
  const pinsInside = useMemo(() => {
    if (isDrawing || polygonPoints.length < 4) return [];
    return pins.filter((p) => pointInPolygon([p.lon, p.lat], polygonPoints));
  }, [pins, polygonPoints, isDrawing]);

  // ── Render pins as DOM markers (so we can color + tap natively) ────────
  // Perf (May 20, 2026): DOM markers are materialized ONLY at street zoom
  // (>= CLUSTER_BREAK_ZOOM) and ONLY for pins inside the current viewport.
  // Below that zoom the clustered GeoJSON layer (the effect below) owns the
  // visuals, so we hold ZERO DOM markers. Previously a marker was created for
  // EVERY pin in the fetched bbox regardless of zoom (just hidden via
  // display:none). After the May 18 viewport-bbox fetch — which pulls the
  // viewport + a 50% buffer, thousands of pins in dense Regrid areas — that
  // meant thousands of hidden DOM nodes: the main-thread freeze on zoom-in, and
  // the mass display-toggle at the cluster-break boundary made pins blink out
  // and back in. We re-sync on `moveend` so panning/zooming at street zoom
  // reveals newly-visible pins without waiting for a refetch.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const CLUSTER_BREAK_ZOOM = 14; // keep in lockstep with the cluster effect

    const syncMarkers = () => {
      // Shift gate: off-shift field reps see no pins. Tear down any existing
      // markers and hold none until they go on shift.
      if (pinsHiddenOffShift) {
        if (markersRef.current.size) {
          for (const [, m] of markersRef.current) m.remove();
          markersRef.current.clear();
        }
        return;
      }
      // Below street zoom the cluster layer owns the visuals — hold no markers.
      if (map.getZoom() < CLUSTER_BREAK_ZOOM) {
        if (markersRef.current.size) {
          for (const [, m] of markersRef.current) m.remove();
          markersRef.current.clear();
        }
        return;
      }

      // Viewport (+15% pad so a small pan doesn't pop) — only pins inside this
      // box get a DOM node. Off-screen pins stay in `pins` state for the
      // cluster source + counts, but cost nothing in the DOM.
      const vb = map.getBounds?.();
      if (!vb) return;
      const vsw = vb.getSouthWest();
      const vne = vb.getNorthEast();
      const vLatPad = (vne.lat - vsw.lat) * 0.15;
      const vLonPad = (vne.lng - vsw.lng) * 0.15;
      const vS = vsw.lat - vLatPad, vN = vne.lat + vLatPad;
      const vW = vsw.lng - vLonPad, vE = vne.lng + vLonPad;

      const seen = new Set<string>();
      for (const pin of pins) {
        if (!filterStates.has(pin.current_state)) continue;
        // Owner-scope filter: when active, hide pins whose deal isn't in scope.
        // Pins with no deal_id (e.g. fresh canvasser drops not yet tied to a deal)
        // are always shown — they're "yours" by virtue of being on your map.
        if (scopedDealIds !== null && pin.deal_id && !scopedDealIds.has(pin.deal_id)) continue;
        // Storm-area filter (manager narrowed the storm dates): only pins
        // inside a drawn storm halo get a marker.
        if (pinIdsInStorms !== null && !pinIdsInStorms.has(pin.id)) continue;
        // Off-screen pins get no DOM node — materializing the whole buffered
        // bbox is what froze the map. The cluster source still sees them.
        if (pin.lat < vS || pin.lat > vN || pin.lon < vW || pin.lon > vE) continue;
        seen.add(pin.id);

        let marker = markersRef.current.get(pin.id);
        const style = STATE_STYLE[pin.current_state] ?? STATE_STYLE.open;
        // Contacted pins get a small clock badge in the corner — at-a-glance hint
        // that someone has spoken with the homeowner. The 72h cooldown that used
        // to dim these pins was removed May 11, 2026 — pins stay full opacity and
        // the bottom sheet shows full contact history.
        const showContactBadge = pin.current_state === "contacted";

        const inSession = !!dropSession && pin.drop_session_id === dropSession.id;
        const sessionGlow = inSession
          ? ", 0 0 0 3px rgba(255,158,122,0.85), 0 0 0 6px rgba(244,101,48,0.35)"
          : "";

        // Warm-lead halo: gold ring around pins that were once in appt_scheduled
        // but got recycled after 3mo without converting. Receptive homeowner, worth
        // re-approaching. Stacks behind the session glow when both apply.
        const warmHalo = (pin.prior_appointment_count ?? 0) > 0
          ? ", 0 0 0 2px #facc15, 0 0 6px 2px rgba(250,204,21,0.55)"
          : "";

        if (!marker) {
          const el = document.createElement("div");
          el.className = "br-knock-pin";
          // NOTE: do NOT set position here. Mapbox's `.mapboxgl-marker` class
          // pins the element with `position: absolute; top: 0; left: 0` and
          // then drives placement via `transform: translate3d(...)`. An inline
          // `position: relative` overrides that class rule and the marker
          // falls into normal document flow + transform, which manifests as
          // "pins shifted off the houses" at low zoom and pins disappearing
          // off-viewport at street zoom. The contact badge below uses
          // `position: absolute` and anchors to this element fine — any
          // positioned ancestor (absolute included) is a valid anchor.
          el.style.cssText = `
            width: 18px; height: 18px; border-radius: 50%;
            background: ${style.color};
            border: 2px solid ${style.outline ?? "#fff"};
            box-shadow: 0 1px 3px rgba(0,0,0,0.4)${warmHalo}${sessionGlow};
            cursor: pointer;
          `;
          if (showContactBadge) {
            const badge = document.createElement("span");
            badge.className = "br-knock-pin__contact-badge";
            badge.title = "Contacted previously";
            badge.style.cssText = `
              position: absolute;
              top: -4px; right: -4px;
              width: 10px; height: 10px; border-radius: 50%;
              background: #fff;
              border: 1.5px solid ${style.color};
              box-shadow: 0 0 0 1px rgba(0,0,0,0.15), 0 1px 2px rgba(0,0,0,0.25);
              pointer-events: none;
            `;
            el.appendChild(badge);
          }
          // Markers only exist at >= CLUSTER_BREAK_ZOOM now, so they're always
          // visible. (The cluster effect's applyMarkerVisibility still toggles
          // display defensively when crossing the boundary mid-animation.)
          marker = new mapboxgl.Marker({ element: el })
            .setLngLat([pin.lon, pin.lat])
            .addTo(map);
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            setActivePin(pin);
          });
          markersRef.current.set(pin.id, marker);
        } else {
          // Update existing marker (state may have changed via realtime later)
          const el = marker.getElement() as HTMLDivElement;
          el.style.background = style.color;
          el.style.borderColor = style.outline ?? "#fff";
          el.style.boxShadow = `0 1px 3px rgba(0,0,0,0.4)${warmHalo}${sessionGlow}`;
          marker.setLngLat([pin.lon, pin.lat]);
          // Sync the contact badge with the pin's current state (realtime UPDATEs
          // can flip 'open' → 'contacted' or 'contacted' → 'appt_scheduled').
          const existingBadge = el.querySelector(".br-knock-pin__contact-badge") as HTMLSpanElement | null;
          if (showContactBadge && !existingBadge) {
            const badge = document.createElement("span");
            badge.className = "br-knock-pin__contact-badge";
            badge.title = "Contacted previously";
            badge.style.cssText = `
              position: absolute;
              top: -4px; right: -4px;
              width: 10px; height: 10px; border-radius: 50%;
              background: #fff;
              border: 1.5px solid ${style.color};
              box-shadow: 0 0 0 1px rgba(0,0,0,0.15), 0 1px 2px rgba(0,0,0,0.25);
              pointer-events: none;
            `;
            el.appendChild(badge);
          } else if (!showContactBadge && existingBadge) {
            existingBadge.remove();
          } else if (showContactBadge && existingBadge) {
            // Keep border in sync if the state-color shifts within contacted (no-op today)
            existingBadge.style.borderColor = style.color;
          }
        }
      }

      // Remove markers for pins that aren't visible / filtered out anymore.
      for (const [pinId, marker] of markersRef.current) {
        if (!seen.has(pinId)) {
          marker.remove();
          markersRef.current.delete(pinId);
        }
      }
    };

    // Sync now (deps changed: pins/filters/scope/session) and on every map
    // settle so panning at street zoom reveals newly-visible pins. moveend
    // fires after both pan and zoom animations finish.
    syncMarkers();
    map.on("moveend", syncMarkers);
    return () => {
      try { map.off("moveend", syncMarkers); } catch { /* map may be torn down */ }
    };
  }, [pins, filterStates, scopedDealIds, dropSession?.id, pinsHiddenOffShift, pinIdsInStorms]);

  // ── Cluster source + layers for knock pins ────────────────────────────
  // At zooms <= 13 Mapbox groups overlapping pins into colored bubbles with
  // a count badge. At zoom 14+ clusters break apart and the DOM markers
  // (handled by the effect above) take over — that path keeps the rich
  // per-pin styling (contact badge, session glow, gold-ring tile-click).
  // Cluster bubble color reflects the most-advanced lifecycle state present
  // in the cluster (built > claim_called > lead > appt_scheduled > contacted >
  // knocked > open), so a quick visual scan shows where the best work is.
  //
  // Hybrid rationale: keeping DOM markers for high-zoom preserves all the
  // subtle visuals (peach-orange session halo, gold-ring scale-up on
  // tile-click) without porting them to Mapbox feature-state expressions.
  // The Mapbox source owns clusters + low-zoom singleton stubs only.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const SRC = "pins-cluster";
    const LYR_BUBBLES = "pins-cluster-bubbles";
    const LYR_COUNT = "pins-cluster-count";
    const LYR_UNCL = "pins-unclustered-stub";
    const CLUSTER_BREAK_ZOOM = 14; // DOM markers take over at this zoom

    // Build a filtered GeoJSON FeatureCollection from the same predicates
    // the DOM-marker effect uses, so the two stay in lockstep. When pins are
    // shift-gated off (off-shift field rep), feed an empty collection so the
    // cluster bubbles + low-zoom stubs disappear too.
    const features = pinsHiddenOffShift ? [] : pins
      .filter((p) => filterStates.has(p.current_state))
      .filter((p) => !(scopedDealIds !== null && p.deal_id && !scopedDealIds.has(p.deal_id)))
      .filter((p) => pinIdsInStorms === null || pinIdsInStorms.has(p.id))
      .map((p) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [p.lon, p.lat] },
        properties: { id: p.id, state: p.current_state },
      }));
    const geojson = { type: "FeatureCollection" as const, features };

    // Stable handler refs — extracted out of ensureLayers so we can bind
    // them ONCE per effect run and `off()` them in cleanup. Binding inside
    // ensureLayers (the previous design) caused listener accumulation: every
    // basemap toggle wipes layers, fires style.load, re-enters the !getLayer
    // branch, and re-binds another copy of each click/hover handler.
    const onClusterClick = (e: any) => {
      const fts = map.queryRenderedFeatures(e.point, { layers: [LYR_BUBBLES] });
      if (!fts.length) return;
      const clusterId = fts[0].properties.cluster_id;
      const src = map.getSource(SRC) as any;
      if (!src?.getClusterExpansionZoom) return;
      src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (err) return;
        map.easeTo({ center: fts[0].geometry.coordinates, zoom, duration: 500 });
      });
    };
    const onClusterEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onClusterLeave = () => { map.getCanvas().style.cursor = ""; };

    const ensureLayers = () => {
      const tune = bubbleTuning(map);
      if (!map.getSource(SRC)) {
        map.addSource(SRC, {
          type: "geojson",
          data: geojson,
          cluster: true,
          clusterMaxZoom: CLUSTER_BREAK_ZOOM - 1, // = 13, clusters break apart at z14
          clusterRadius: tune.clusterRadius, // = min bubble spacing in px (phone-aware)
          // Per-state counts inside each cluster — drive dominant-state color.
          clusterProperties: {
            n_built:     ["+", ["case", ["==", ["get", "state"], "built"], 1, 0]],
            n_claim:     ["+", ["case", ["==", ["get", "state"], "claim_called"], 1, 0]],
            n_lead:      ["+", ["case", ["==", ["get", "state"], "lead"], 1, 0]],
            n_appt:      ["+", ["case", ["==", ["get", "state"], "appt_scheduled"], 1, 0]],
            n_contacted: ["+", ["case", ["==", ["get", "state"], "contacted"], 1, 0]],
            n_knocked:   ["+", ["case", ["==", ["get", "state"], "knocked"], 1, 0]],
          },
        });
      } else {
        // Defensive — during a basemap toggle the source can briefly be
        // present-but-not-yet-ready. Skip silently; the next dep change
        // (or style.load) will retry.
        const src = map.getSource(SRC) as any;
        if (src?.setData) src.setData(geojson);
      }

      if (!map.getLayer(LYR_BUBBLES)) {
        map.addLayer({
          id: LYR_BUBBLES,
          type: "circle",
          source: SRC,
          filter: ["has", "point_count"],
          // Below DETAIL_MIN_ZOOM the server-aggregated pins-overview layers
          // own the visuals — this source only holds the last-fetched bbox at
          // wide zoom, so letting it render there would show a stale subset.
          minzoom: DETAIL_MIN_ZOOM,
          paint: {
            "circle-color": BUBBLE_COLOR_EXPR,
            "circle-radius": tune.radiusSteps(["get", "point_count"]),
            "circle-opacity": 0.85,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }

      if (!map.getLayer(LYR_COUNT)) {
        map.addLayer({
          id: LYR_COUNT,
          type: "symbol",
          source: SRC,
          filter: ["has", "point_count"],
          minzoom: DETAIL_MIN_ZOOM, // see LYR_BUBBLES note
          layout: {
            "text-field": ["coalesce", ["get", "point_count_abbreviated"], ["get", "point_count"]],
            "text-size": tune.textSize,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "rgba(0,0,0,0.35)",
            "text-halo-width": 1.5,
          },
        });
      }

      // Singleton-pin stubs at low zoom: a small state-colored dot for any
      // pin that has no neighbors within clusterRadius. Capped at
      // CLUSTER_BREAK_ZOOM via `maxzoom` so it doesn't compete with DOM
      // markers at street level. No click handler — at z<14 the user should
      // zoom in (via the cluster bubbles) to reach the DOM marker.
      if (!map.getLayer(LYR_UNCL)) {
        map.addLayer({
          id: LYR_UNCL,
          type: "circle",
          source: SRC,
          filter: ["!", ["has", "point_count"]],
          minzoom: DETAIL_MIN_ZOOM, // see LYR_BUBBLES note
          maxzoom: CLUSTER_BREAK_ZOOM,
          paint: {
            "circle-color": [
              "match", ["get", "state"],
              "open",            STATE_STYLE.open.color,
              "knocked",         STATE_STYLE.knocked.color,
              "contacted",       STATE_STYLE.contacted.color,
              "rent",            STATE_STYLE.rent.color,
              "appt_scheduled",  STATE_STYLE.appt_scheduled.color,
              "lead",            STATE_STYLE.lead.color,
              "claim_called",    STATE_STYLE.claim_called.color,
              "built",           STATE_STYLE.built.color,
              "legacy_client",   STATE_STYLE.legacy_client.color,
              "do_not_knock",    STATE_STYLE.do_not_knock.color,
              "wrong_address",   STATE_STYLE.wrong_address.color,
              "not_home_3x",     STATE_STYLE.not_home_3x.color,
              STATE_STYLE.open.color,
            ],
            "circle-radius": 6,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }
    };

    // Hide DOM markers when clusters own the visual (z < CLUSTER_BREAK_ZOOM);
    // show them again at street level. Mapbox fires `zoom` continuously
    // during animations (~30 ticks per zoom). With 1000s of markers that's
    // 30k DOM mutations per zoom — wasteful. Cache last state and bail when
    // the threshold isn't crossed; only iterate markersRef on the transition.
    let lastVisible: boolean | null = null;
    const applyMarkerVisibility = () => {
      const visible = map.getZoom() >= CLUSTER_BREAK_ZOOM;
      if (visible === lastVisible) return;
      lastVisible = visible;
      for (const [, marker] of markersRef.current) {
        const el = marker.getElement() as HTMLElement | null;
        if (el) el.style.display = visible ? "" : "none";
      }
    };

    // Bind cluster click + hover handlers ONCE per effect run, idempotent via
    // off()+on() so a stale binding (e.g., from a prior effect run that
    // hasn't cleaned up yet) is replaced not stacked. These are attached to
    // the map itself, not the layer — they survive setStyle() so we don't
    // need to re-bind on style.load.
    map.off("click", LYR_BUBBLES, onClusterClick);
    map.on("click", LYR_BUBBLES, onClusterClick);
    map.off("mouseenter", LYR_BUBBLES, onClusterEnter);
    map.on("mouseenter", LYR_BUBBLES, onClusterEnter);
    map.off("mouseleave", LYR_BUBBLES, onClusterLeave);
    map.on("mouseleave", LYR_BUBBLES, onClusterLeave);

    if (map.isStyleLoaded()) ensureLayers();
    // Permanent listener (not `once`) so the basemap toggle — which calls
    // map.setStyle() and wipes all custom sources/layers — gets our cluster
    // source re-added. Same pattern as the storm-sigs effect above.
    map.on("style.load", ensureLayers);
    applyMarkerVisibility();
    map.on("zoom", applyMarkerVisibility);

    return () => {
      try { map.off("zoom", applyMarkerVisibility); } catch { /* ignore */ }
      try { map.off("style.load", ensureLayers); } catch { /* ignore */ }
      try { map.off("click", LYR_BUBBLES, onClusterClick); } catch { /* ignore */ }
      try { map.off("mouseenter", LYR_BUBBLES, onClusterEnter); } catch { /* ignore */ }
      try { map.off("mouseleave", LYR_BUBBLES, onClusterLeave); } catch { /* ignore */ }
    };
  }, [pins, filterStates, scopedDealIds, pinsHiddenOffShift, pinIdsInStorms]);

  // ── Overview bubbles (zoom < DETAIL_MIN_ZOOM) — Sep 9, 2026 ────────────
  // Server-aggregated grid cells rendered to LOOK like the client cluster
  // bubbles (same size steps, same most-advanced-state color rule) so the
  // handoff at DETAIL_MIN_ZOOM is visually seamless. Cells carry per-state
  // counts, so the manager's state-filter chips apply here WITHOUT a refetch.
  // Oct 12, 2026: the cells are fed through Mapbox clustering (see
  // bubbleTuning) so bubble spacing is a screen-pixel radius, not the degree
  // grid — on a phone the grid put cells 17pt apart under 60pt bubbles.
  // Known gap, accepted: scopedDealIds can't be applied (aggregates carry no
  // deal ids) — at metro zoom a deal-scoped view shows unscoped totals.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapInited) return;

    const SRC = "pins-overview";
    const LYR_B = "pins-overview-bubbles";
    const LYR_C = "pins-overview-count";

    const features = (pinsHiddenOffShift || !overviewCells) ? [] : overviewCells
      .filter((cell) => {
        if (!stormPinIndex) return true;
        // Storm-area filter at wide zoom. Cells are aggregates (no per-pin
        // coords), so "inside a storm" is approximated as: any drawn storm
        // feature within 1 mile + the cell's diagonal of the cell centroid.
        // Deliberately over-inclusive — a cell straddling a halo edge keeps
        // its full count — so bubbles never vanish from a corridor that has
        // pins in it; exact per-pin filtering takes over at DETAIL_MIN_ZOOM.
        const lat = Number(cell.lat);
        const lon = Number(cell.lon);
        const cellDeg = overviewCellDegRef.current;
        const cosLat = Math.cos((lat * Math.PI) / 180);
        const diagM = Math.hypot(cellDeg * M_PER_DEG_LAT, cellDeg * M_PER_DEG_LAT * cosLat);
        return stormIndexHasWithin(stormPinIndex, lat, lon, STORM_BUFFER_M + diagM);
      })
      .map((cell) => {
        const states = cell.states ?? {};
        // Counts narrowed to the ACTIVE filter chips, keyed exactly like the
        // pins-cluster source's clusterProperties so BUBBLE_COLOR_EXPR and
        // the clusterProperties sums below work on cells and clusters alike.
        const n = (st: string) => (filterStates.has(st) ? Number(states[st]) || 0 : 0);
        let count = 0;
        for (const st of Object.keys(states)) count += n(st);
        if (count <= 0) return null;
        return {
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [Number(cell.lon), Number(cell.lat)] },
          properties: {
            count,
            n_built:     n("built"),
            n_claim:     n("claim_called"),
            n_lead:      n("lead"),
            n_appt:      n("appt_scheduled"),
            n_contacted: n("contacted"),
            n_knocked:   n("knocked"),
          },
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);
    const geojson = { type: "FeatureCollection" as const, features };

    const onBubbleClick = (e: any) => {
      const fts = map.queryRenderedFeatures(e.point, { layers: [LYR_B] });
      if (!fts.length) return;
      map.easeTo({
        center: fts[0].geometry.coordinates,
        zoom: Math.min(map.getZoom() + 2.2, DETAIL_MIN_ZOOM + 0.5),
        duration: 500,
      });
    };
    const onBubbleEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onBubbleLeave = () => { map.getCanvas().style.cursor = ""; };

    const ensureLayers = () => {
      const tune = bubbleTuning(map);
      if (!map.getSource(SRC)) {
        map.addSource(SRC, {
          type: "geojson",
          data: geojson,
          // Screen-space bunching: Mapbox merges cells closer than
          // overviewRadius px into one bubble whose properties are the sums
          // below, so spacing follows the screen instead of the degree grid.
          // clusterMaxZoom covers every zoom this layer can show (the layer
          // is capped at DETAIL_MIN_ZOOM).
          cluster: true,
          clusterRadius: tune.overviewRadius,
          clusterMaxZoom: Math.ceil(DETAIL_MIN_ZOOM),
          clusterProperties: {
            count:       ["+", ["get", "count"]],
            n_built:     ["+", ["get", "n_built"]],
            n_claim:     ["+", ["get", "n_claim"]],
            n_lead:      ["+", ["get", "n_lead"]],
            n_appt:      ["+", ["get", "n_appt"]],
            n_contacted: ["+", ["get", "n_contacted"]],
            n_knocked:   ["+", ["get", "n_knocked"]],
          },
        });
      } else {
        const src = map.getSource(SRC) as any;
        if (src?.setData) src.setData(geojson);
      }
      // A merged cluster and a lone cell both carry `count` + n_*, so one
      // layer styles both — deliberately no `has point_count` filter here.
      if (!map.getLayer(LYR_B)) {
        map.addLayer({
          id: LYR_B,
          type: "circle",
          source: SRC,
          maxzoom: DETAIL_MIN_ZOOM,
          paint: {
            "circle-color": BUBBLE_COLOR_EXPR,
            "circle-radius": tune.radiusSteps(["get", "count"]),
            "circle-opacity": 0.85,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }
      if (!map.getLayer(LYR_C)) {
        map.addLayer({
          id: LYR_C,
          type: "symbol",
          source: SRC,
          maxzoom: DETAIL_MIN_ZOOM,
          layout: {
            "text-field": bubbleCountLabel(["get", "count"]),
            "text-size": tune.textSize,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "rgba(0,0,0,0.35)",
            "text-halo-width": 1.5,
          },
        });
      }
    };

    map.off("click", LYR_B, onBubbleClick);
    map.on("click", LYR_B, onBubbleClick);
    map.off("mouseenter", LYR_B, onBubbleEnter);
    map.on("mouseenter", LYR_B, onBubbleEnter);
    map.off("mouseleave", LYR_B, onBubbleLeave);
    map.on("mouseleave", LYR_B, onBubbleLeave);

    if (map.isStyleLoaded()) ensureLayers();
    map.on("style.load", ensureLayers);

    return () => {
      try { map.off("style.load", ensureLayers); } catch { /* ignore */ }
      try { map.off("click", LYR_B, onBubbleClick); } catch { /* ignore */ }
      try { map.off("mouseenter", LYR_B, onBubbleEnter); } catch { /* ignore */ }
      try { map.off("mouseleave", LYR_B, onBubbleLeave); } catch { /* ignore */ }
    };
  }, [overviewCells, filterStates, pinsHiddenOffShift, mapInited, stormPinIndex]);

  // ── Quick win #4: render grey-blue dots for other active reps ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set<string>();
    for (const rep of activeReps) {
      // Hide self — you don't need a dot for yourself
      if (member && rep.user_id === member.id) continue;
      if (rep.lat == null || rep.lon == null) continue;
      seen.add(rep.user_id);

      let marker = repMarkersRef.current.get(rep.user_id);
      if (!marker) {
        const el = document.createElement("div");
        el.className = "br-rep-dot";
        el.title = `${rep.full_name} — ${rep.visits} knocks · ${rep.appts} appts`;
        el.style.cssText = `
          width: 22px; height: 22px; border-radius: 50%;
          background: #64748b;
          border: 2px solid white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.5);
          color: white;
          font-size: 9px;
          font-weight: 800;
          display: flex; align-items: center; justify-content: center;
          opacity: 0.85;
        `;
        el.textContent = rep.initials || "?";
        marker = new mapboxgl.Marker({ element: el })
          .setLngLat([rep.lon, rep.lat])
          .addTo(map);
        repMarkersRef.current.set(rep.user_id, marker);
      } else {
        marker.setLngLat([rep.lon, rep.lat]);
        const el = marker.getElement() as HTMLDivElement;
        if (el) el.title = `${rep.full_name} — ${rep.visits} knocks · ${rep.appts} appts`;
      }
    }

    // Remove dots for reps no longer active
    for (const [userId, marker] of repMarkersRef.current) {
      if (!seen.has(userId)) {
        marker.remove();
        repMarkersRef.current.delete(userId);
      }
    }
  }, [activeReps, member?.id]);

  // ── "You are here" dot (Oct 27, 2026) ──
  // Drawn from the same watch as the distance hint, so it shows without
  // tapping the locate button. The first fix centers the map on the rep when
  // the canvas opened on the default view (no saved view, area or storm link).
  const meMarkerRef = useRef<any>(null);
  const centeredOnMeRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !myPos) {
      if (meMarkerRef.current) { meMarkerRef.current.remove(); meMarkerRef.current = null; }
      return;
    }
    if (!meMarkerRef.current) {
      const el = document.createElement("div");
      el.className = "br-me-dot";
      el.title = "You are here";
      el.style.cssText = `
        width: 18px; height: 18px; border-radius: 50%;
        background: #2563eb;
        border: 3px solid white;
        box-shadow: 0 0 0 6px rgba(37,99,235,0.25), 0 1px 4px rgba(0,0,0,0.45);
        pointer-events: none;
      `;
      meMarkerRef.current = new mapboxgl.Marker({ element: el })
        .setLngLat([myPos.lon, myPos.lat])
        .addTo(map);
    } else {
      meMarkerRef.current.setLngLat([myPos.lon, myPos.lat]);
    }
    if (!centeredOnMeRef.current) {
      centeredOnMeRef.current = true;
      if (startedOnDefaultViewRef.current) map.flyTo({ center: [myPos.lon, myPos.lat], zoom: 16, speed: 1.4 });
    }
  }, [myPos, mapInited]);
  useEffect(() => () => { meMarkerRef.current?.remove(); meMarkerRef.current = null; }, []);

  // ── Ship 6 v2: tile-click → identify matching pins on map + fit bounds ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Always remove any prior breadcrumb layer/source
    try {
      if (map.getLayer("shift-breadcrumb-line")) map.removeLayer("shift-breadcrumb-line");
      if (map.getSource("shift-breadcrumb")) map.removeSource("shift-breadcrumb");
    } catch { /* style not ready, ignore */ }

    if (!tileSel || !shift || !member) {
      // Clear highlight class from all markers
      for (const [, marker] of markersRef.current) {
        const el = marker.getElement() as HTMLDivElement | null;
        if (el) {
          el.style.boxShadow = "0 1px 3px rgba(0,0,0,0.4)";
          el.style.transform = el.style.transform.replace(/scale\([^)]*\)/, "");
        }
      }
      return;
    }

    let cancelled = false;

    (async () => {
      if (tileSel === "miles") {
        // Fetch shift breadcrumb pings → fit bounds + draw line layer
        const { data, error } = await supabase
          .from("canvas_gps_pings")
          .select("lat, lon, pinged_at")
          .eq("shift_id", shift.id)
          .order("pinged_at", { ascending: true });
        if (cancelled) return;
        if (error || !data || data.length < 2) return;

        const coords: [number, number][] = data.map((p: any) => [Number(p.lon), Number(p.lat)]);
        const lineGeoJson = {
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: coords },
        };

        try {
          map.addSource("shift-breadcrumb", { type: "geojson", data: lineGeoJson });
          map.addLayer({
            id: "shift-breadcrumb-line",
            type: "line",
            source: "shift-breadcrumb",
            layout: { "line-cap": "round", "line-join": "round" },
            paint: {
              "line-color": "#f46530",
              "line-width": 4,
              "line-opacity": 0.85,
            },
          });
        } catch { /* style not ready or duplicate, ignore */ }

        // Fit bounds to the breadcrumb
        const bounds = coords.reduce(
          (b, c) => b.extend(c),
          new mapboxgl.LngLatBounds(coords[0], coords[0]),
        );
        map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 16 });
        return;
      }

      // Pin-based tiles: query canvas_visits for the shift, filter to outcome,
      // collect knock_pin_ids, look up coords from local pins state.
      const outcomeFilter: Record<TileKind, string[]> =
        ({ knocks: [], contacts: ["contact_made","follow_up","appt_set","rent"], appts: ["appt_set"], claims: ["appt_set"], miles: [] } as any);
      let q = supabase
        .from("canvas_visits")
        .select("knock_pin_id")
        .eq("visited_by", member.id)
        .gte("visited_at", shift.started_at);
      if (outcomeFilter[tileSel].length > 0) {
        q = q.in("outcome", outcomeFilter[tileSel]);
      }
      const { data, error } = await q;
      if (cancelled) return;
      if (error || !data) return;

      const ids = new Set<string>(data.map((r: any) => r.knock_pin_id).filter(Boolean));

      // Apply gold ring to matching markers, plain to non-matching.
      // matchingCoords is derived from `pins` directly (not from materialized
      // markers) so fitBounds still frames off-screen matches — DOM markers are
      // now viewport-gated and a matched pin may not have a marker yet. The
      // fitBounds below brings it on-screen, where the next syncMarkers run
      // (on moveend) materializes it.
      const matchingCoords: [number, number][] = [];
      for (const pin of pins) {
        const isMatch = ids.has(pin.id);
        if (isMatch) matchingCoords.push([pin.lon, pin.lat]);
        const marker = markersRef.current.get(pin.id);
        if (!marker) continue;
        const el = marker.getElement() as HTMLDivElement | null;
        if (!el) continue;
        if (isMatch) {
          el.style.boxShadow = "0 0 0 3px #f46530, 0 0 0 5px rgba(244,101,48,0.4), 0 1px 3px rgba(0,0,0,0.4)";
          el.style.transform = (el.style.transform || "") + " scale(1.25)";
        } else {
          el.style.boxShadow = "0 1px 3px rgba(0,0,0,0.4)";
          el.style.transform = el.style.transform.replace(/\s*scale\([^)]*\)/, "");
        }
      }

      if (matchingCoords.length > 0) {
        const bounds = matchingCoords.reduce(
          (b, c) => b.extend(c),
          new mapboxgl.LngLatBounds(matchingCoords[0], matchingCoords[0]),
        );
        // padding 80px so the highlighted pins aren't kissing the screen edges;
        // maxZoom 17 so a single pin doesn't zoom in too far.
        map.fitBounds(bounds, { padding: 80, duration: 600, maxZoom: 17 });
      }
    })();

    return () => { cancelled = true; };
  }, [tileSel, shift?.id, shift?.visit_count, shift?.contact_count, shift?.appointments_set, shift?.ping_count, shift?.miles_walked, pins, member?.id]);

  // ── Pending-drop markers — render an instant peach circle for each
  // queued drop in session mode so the manager sees their click landed
  // even though the DB write is deferred until End.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Build a set of tempIds we still need to show
    const wanted = new Set(pendingDrops.map((d) => d.tempId));
    // Add markers for new tempIds
    for (const drop of pendingDrops) {
      if (pendingMarkersRef.current.has(drop.tempId)) continue;
      const el = document.createElement("div");
      el.style.cssText = `
        width: 16px; height: 16px; border-radius: 50%;
        background: #ff9e7a; border: 2px solid white;
        box-shadow: 0 0 0 3px rgba(255,158,122,0.6), 0 0 0 6px rgba(244,101,48,0.25), 0 1px 3px rgba(0,0,0,0.4);
        opacity: 0.95;
      `;
      const m = new mapboxgl.Marker({ element: el })
        .setLngLat([drop.lon, drop.lat])
        .addTo(map);
      pendingMarkersRef.current.set(drop.tempId, m);
    }
    // Remove markers whose tempIds are no longer pending (cleared on End)
    for (const [tid, m] of pendingMarkersRef.current) {
      if (!wanted.has(tid)) {
        try { m.remove(); } catch { /* ignore */ }
        pendingMarkersRef.current.delete(tid);
      }
    }
  }, [pendingDrops]);

  // ── Batch-process pending drops when the manager taps End ───────────────
  // Sep 9, 2026 rewrite (fallout from the Sep 8 "pins didn't save" incident —
  // the pins HAD saved, but per-drop parcel RPCs fired 8-wide each carrying
  // their own 8s statement-timeout, freezing End for 25-40s under DB load and
  // silently disabling the dedupe guard):
  //  · Parcel resolution is ONE batched canvas_parcels_for_drops call per 25
  //    drops (sequential), which also returns "already pinned" flags — the
  //    separate dedupe SELECT is gone.
  //  · The resolved parcel_id ships in the INSERT payload, so the DB trigger
  //    (knock_pins_attach_parcel_tg) skips its own per-row lookup.
  //  · INSERTs are chunked (25/statement). A failed chunk no longer strands
  //    the whole session: saved + skipped drops leave the pending queue, the
  //    rest stay queued (markers + localStorage intact) and the session stays
  //    OPEN so tapping End again retries just the remainder.
  //  · This function now owns the end-of-run state transitions (review modal
  //    vs keep-session-open) so both End buttons behave identically — the old
  //    callers closed the session UI even when the insert had failed.
  //  Mapbox geocoding is unchanged: parallel chunks of 8 against Mapbox (not
  //  our DB); placeholder addresses fall through and the trigger overwrites
  //  them from parcel data when a parcel matched.
  async function processDropSessionEnd(): Promise<void> {
    if (!dropSession || !member) return;
    const sessionId = dropSession.id;
    const drops = [...pendingDrops];
    const total = drops.length;
    if (total === 0) {
      // Nothing pending — just close the session row
      const { data } = await supabase.rpc("canvas_end_drop_session", { p_label: null, p_notes: null });
      const row = (data && (data as any).id) ? data : null;
      setDropSession(null);
      setReviewSession({
        id: sessionId,
        pin_count: row?.pin_count ?? dropSession.pin_count,
        savedNow: 0,
        skipped: 0,
      });
      return;
    }
    setProcessing({ done: 0, total });

    try {
      // Phase 1a: batched parcel + already-pinned resolution. On a chunk
      // failure (e.g. DB under load) the drops still insert — the trigger
      // backfills parcels — we just lose the dedupe screen for that chunk
      // and tell the manager so in the review modal.
      const parcelByTempId = new Map<string, { parcelId: string | null; pinned: boolean }>();
      let dedupeUnavailable = false;
      const PARCEL_BATCH = 25;
      for (let i = 0; i < drops.length; i += PARCEL_BATCH) {
        const chunk = drops.slice(i, i + PARCEL_BATCH);
        try {
          const { data, error } = await supabase.rpc("canvas_parcels_for_drops", {
            p_points: chunk.map((d) => ({ lat: d.lat, lon: d.lon })),
            p_max_meters: 100,
          });
          if (error || !Array.isArray(data) || data.length !== chunk.length) {
            dedupeUnavailable = true;
            continue;
          }
          chunk.forEach((d, idx) => {
            const row = (data as Array<{ parcel_id: string | null; pinned: boolean }>)[idx];
            parcelByTempId.set(d.tempId, {
              parcelId: row?.parcel_id ?? null,
              pinned: !!row?.pinned,
            });
          });
        } catch {
          dedupeUnavailable = true;
        }
      }

      // Phase 1b: parallel Mapbox geocoding in chunks of 8.
      // We could skip this entirely and let the placeholder fall through, but
      // for cross-county drops Mapbox is the ONLY source of a real address.
      const enriched: Array<{
        tempId: string; lat: number; lon: number;
        street: string | null; city: string | null; zip: string | null;
        parcelId: string | null; pinned: boolean;
      }> = [];
      const CONCURRENCY = 8;
      let done = 0;
      for (let i = 0; i < drops.length; i += CONCURRENCY) {
        const chunk = drops.slice(i, i + CONCURRENCY);
        const results = await Promise.all(chunk.map(async (d) => {
          let street: string | null = null;
          let city: string | null = null;
          let zip: string | null = null;
          if (MAPBOX_TOKEN) {
            try {
              const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${d.lon},${d.lat}.json` +
                `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}&types=address&limit=1`;
              const resp = await fetch(url);
              if (resp.ok) {
                const json = await resp.json();
                const feat = json.features?.[0];
                if (feat) {
                  const num = feat.address ? `${feat.address} ` : "";
                  street = (num + (feat.text ?? "")).trim() || null;
                  const ctx = (feat.context ?? []) as Array<{ id: string; text: string }>;
                  for (const c of ctx) {
                    if (c.id?.startsWith("place")    && !city) city = c.text;
                    if (c.id?.startsWith("postcode") && !zip)  zip  = c.text;
                  }
                }
              }
            } catch { /* fall through to placeholder */ }
          }
          const hit = parcelByTempId.get(d.tempId);
          return {
            tempId: d.tempId, lat: d.lat, lon: d.lon,
            // Placeholder — trigger will overwrite from parcel data when matched.
            street: street ?? `Pending · ${d.lat.toFixed(5)}, ${d.lon.toFixed(5)}`,
            city: city ?? "Pending",
            zip,
            parcelId: hit?.parcelId ?? null,
            pinned: hit?.pinned ?? false,
          };
        }));
        for (const r of results) enriched.push(r);
        done += chunk.length;
        setProcessing({ done, total });
      }

      // Phase 1c: dedupe guard. Skip drops whose parcel already has a pin (so
      // a session doesn't restack on top of existing/SalesRabbit pins) AND
      // collapse any intra-batch dups (rep double-tapped the same house).
      // Parcel-less drops (unloaded counties, failed lookups) pass through.
      const seenThisBatch = new Set<string>();
      const skippedTempIds = new Set<string>();
      const toInsert: typeof enriched = [];
      for (const e of enriched) {
        const dup = !!e.parcelId && (e.pinned || seenThisBatch.has(e.parcelId));
        if (dup) { skippedTempIds.add(e.tempId); continue; }
        if (e.parcelId) seenThisBatch.add(e.parcelId);
        toInsert.push(e);
      }
      const skipped = skippedTempIds.size;

      // Phase 2: chunked INSERTs. parcel_id rides the payload when resolved,
      // so the trigger only does its own (slow-under-load) lookup for the
      // parcel-less leftovers. Chunking bounds each statement's trigger work
      // AND limits the blast radius of one timeout to 25 drops.
      const INSERT_CHUNK = 25;
      const insertedPins: KnockPin[] = [];
      const savedTempIds = new Set<string>();
      let insertError: string | null = null;
      for (let i = 0; i < toInsert.length; i += INSERT_CHUNK) {
        const chunk = toInsert.slice(i, i + INSERT_CHUNK);
        const payload = chunk.map((e) => ({
          property_street: e.street,
          property_city:   e.city,
          property_state:  "GA",
          property_zip:    e.zip,
          lat: e.lat,
          lon: e.lon,
          parcel_id:       e.parcelId,
          dropped_by:      member.id,
          drop_session_id: sessionId,
        }));
        const { data, error } = await supabase
          .from("knock_pins")
          .insert(payload)
          .select("id, lat, lon, property_street, property_city, property_state, property_zip, homeowner_name, current_state, attempt_count, cooldown_until, notes, deal_id, parcel_id, drop_session_id, prior_appointment_count, last_appointment_at");
        if (error) {
          insertError = error.message;
          break; // leave the rest queued — don't hammer a struggling DB
        }
        insertedPins.push(...((data ?? []) as KnockPin[]));
        for (const e of chunk) savedTempIds.add(e.tempId);
      }

      // Add whatever saved to local state (realtime INSERT de-dupes a race).
      if (insertedPins.length > 0) setPins((prev) => [...insertedPins, ...prev]);

      if (insertError) {
        // Partial save: remove saved + skipped drops from the queue, keep the
        // session (server row included) OPEN so End can be re-tapped to retry
        // just the remainder. Their pending markers stay on the map.
        const doneIds = new Set<string>([...savedTempIds, ...skippedTempIds]);
        setPendingDrops((prev) => prev.filter((d) => !doneIds.has(d.tempId)));
        const remaining = toInsert.length - savedTempIds.size;
        setErr(
          `Saved ${savedTempIds.size} of ${toInsert.length} pins — ${remaining} couldn't save ` +
          `(${insertError}). They're still queued: tap End again to retry.`
        );
        return;
      }

      if (skipped > 0) {
        setErr(`Skipped ${skipped} drop${skipped === 1 ? "" : "s"} — ${skipped === 1 ? "that house" : "those houses"} already had a pin.`);
      }

      // Phase 3: close the session row + clear pending state, then open the
      // review modal with explicit save feedback (a slow End must never read
      // as "didn't save" again).
      const { data: endRow } = await supabase.rpc("canvas_end_drop_session", { p_label: null, p_notes: null });
      const ended = (endRow && (endRow as any).id) ? endRow : null;

      setPendingDrops([]);
      try { window.localStorage.removeItem(`bardin.canvas.pendingDrops.${sessionId}`); } catch { /* ignore */ }
      setDropSession(null);
      setReviewSession({
        id: sessionId,
        pin_count: ended?.pin_count ?? dropSession.pin_count,
        savedNow: insertedPins.length,
        skipped,
        dedupeUnavailable,
      });
    } finally {
      // Always clear the progress pill — the old code left "Processing…"
      // stuck (End button disabled forever) when the insert errored.
      setProcessing(null);
    }
  }

  // ── One-off pin drop at explicit coordinates ───────────────────────────
  // Shared by the map-click "Drop one pin" mode AND the address-search
  // action card. Does parcel auto-fill, the dedupe guard, Mapbox
  // reverse-geocode fallback, and the manual prompt fallback.
  // `preset` (from a search result that already knows the address) is used
  // before falling back to window.prompt.
  async function dropOneOffPinAt(
    lat: number,
    lng: number,
    preset?: { street: string | null; city: string | null; state: string | null; zip: string | null },
  ): Promise<{ status: "dropped" | "declined" | "cancelled" | "error"; pin?: KnockPin }> {
    if (!member) return { status: "error" };

    // ── Step 1: try to auto-populate from Regrid parcel data.
    // canvas_nearest_parcel_for returns the nearest parcel id within range.
    // If found, we read the parcel's address + owner and skip the prompts.
    let street: string | null = null;
    let city: string | null = null;
    let state: string = "GA";
    let zip: string | null = null;
    let homeowner: string | null = null;
    let parcelMatched = false;
    let matchedParcelId: string | null = null;

    try {
      const { data: parcelId } = await supabase.rpc("canvas_nearest_parcel_for", {
        p_lat: lat, p_lon: lng, p_max_meters: 100,
      });
      if (parcelId) {
        matchedParcelId = parcelId as string;
        const { data: parcel } = await supabase
          .from("parcels")
          .select("street, city, state, zip, owner_name")
          .eq("id", parcelId)
          .single();
        if (parcel) {
          street    = parcel.street ?? null;
          city      = parcel.city ?? null;
          state     = parcel.state ?? "GA";
          zip       = parcel.zip ?? null;
          homeowner = parcel.owner_name ?? null;
          parcelMatched = !!street;
        }
      }
    } catch { /* silent — fall through to manual prompts */ }

    // ── Dedupe guard: if this house (parcel) already has a pin, don't
    // silently stack a second one. Bulk imports + the deal backfill left
    // ~1,400 stacked dups on the map (cleaned up May 20, 2026); this stops
    // a manual drop from recreating the same clutter. Parcel-scoped only —
    // drops with no parcel match (unloaded counties) fall through as before.
    if (matchedParcelId) {
      const { data: existingOnParcel } = await supabase
        .from("knock_pins")
        .select("id, current_state")
        .eq("parcel_id", matchedParcelId)
        .limit(1);
      if (existingOnParcel && existingOnParcel.length > 0) {
        const exState = (existingOnParcel[0] as any).current_state as string;
        const exLabel = STATE_STYLE[exState]?.label ?? exState;
        const proceed = window.confirm(
          `This house already has a pin (${exLabel}). Drop another one anyway?`,
        );
        if (!proceed) return { status: "declined" };
      }
    }

    // ── Step 2: if parcel didn't resolve a street, try Mapbox reverse-geocoding.
    // Covers all of GA (and beyond) — handy for counties we haven't loaded
    // Regrid parcels for. We won't get owner_name this way, but we'll
    // get a real street address with no manager typing.
    if (!street && MAPBOX_TOKEN) {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
          `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}&types=address&limit=1`;
        const resp = await fetch(url);
        if (resp.ok) {
          const json = await resp.json();
          const feat = json.features?.[0];
          if (feat) {
            // place_name like "1247 Mt Airy Loop, Cornelia, Georgia 30531, United States"
            // Mapbox provides "address" + "text" + "context" — assemble from parts
            const num = feat.address ? `${feat.address} ` : "";
            const streetName = feat.text ?? "";
            street = (num + streetName).trim() || null;
            const ctx = (feat.context ?? []) as Array<{ id: string; text: string }>;
            for (const c of ctx) {
              if (c.id?.startsWith("place")    && !city) city = c.text;
              if (c.id?.startsWith("postcode") && !zip)  zip  = c.text;
              if (c.id?.startsWith("region")) {
                const short = (c as any).short_code; // e.g. "us-ga"
                if (short && short.toUpperCase().endsWith("-GA")) state = "GA";
              }
            }
          }
        }
      } catch { /* silent — fall through to manual prompt */ }
    }

    // ── Step 2b: search results already carry the address the user picked —
    // use it before bothering the manager with a prompt.
    if (!street && preset?.street) {
      street = preset.street;
      city   = city || preset.city || null;
      state  = preset.state || state;
      zip    = zip || preset.zip || null;
    }

    // ── Step 3: if all auto-lookups failed, prompt.
    if (!street) {
      const promptStreet = window.prompt("No address found — enter the street address:");
      if (!promptStreet) return { status: "cancelled" };
      street = promptStreet;
      city = window.prompt("City:") || city || "Cornelia";
    }

    // ── Step 4: insert. Trigger also re-fills any NULLs from parcel as a
    // belt-and-suspenders, and stamps parcel_id automatically.
    const { data, error } = await supabase
      .from("knock_pins")
      .insert({
        property_street: street,
        property_city: city,
        property_state: state,
        property_zip: zip,
        homeowner_name: homeowner,
        lat,
        lon: lng,
        dropped_by: member.id,
        drop_session_id: null,
      })
      .select("id, lat, lon, property_street, property_city, property_state, property_zip, homeowner_name, current_state, attempt_count, cooldown_until, notes, deal_id, parcel_id, drop_session_id, prior_appointment_count, last_appointment_at")
      .single();
    if (error) {
      setErr(error.message);
      return { status: "error" };
    }
    setPins((prev) => [data as KnockPin, ...prev]);

    if (parcelMatched) {
      const previewLabel = `${street}${city ? `, ${city}` : ""}`;
      setUndoToast({
        clientUuid: "",
        pinId: (data as any).id,
        label: `Pin dropped at ${previewLabel}`,
      });
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => setUndoToast(null), 4_000);
    }
    return { status: "dropped", pin: data as KnockPin };
  }

  // ── Map click in drop-pin mode → drop a pin ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handler = async (e: any) => {
      // Active in EITHER one-off drop mode OR a continuous drop-session
      const sessionActive = !!dropSession;
      if (!dropPinMode && !sessionActive) return;
      if (!isManager || !member) return;
      const { lng, lat } = e.lngLat;

      // ── Fast path: in session mode, defer ALL network work until End.
      // Just queue the lat/lon + render an instant peach marker. The batch
      // processor on End does parcel auto-fill (server trigger) + Mapbox
      // fallback in parallel for all pending drops at once.
      if (sessionActive) {
        const tempId = (typeof crypto !== "undefined" && (crypto as any).randomUUID)
          ? (crypto as any).randomUUID()
          : `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const drop: PendingDrop = {
          tempId,
          lat, lon: lng,
          droppedAt: new Date().toISOString(),
        };
        setPendingDrops((prev) => [...prev, drop]);
        // Optimistic counter bump on the ribbon
        setDropSession((s) => s ? { ...s, pin_count: s.pin_count + 1 } : s);
        // No toast — visual marker (rendered by useEffect on pendingDrops)
        // is the feedback. Keeps drops at literally 1-frame latency.
        return;
      }

      // ── One-off Drop pin mode: shared auto-fill flow.
      // "dropped" and dedupe-"declined" both exit drop mode (matches the
      // pre-refactor behavior); a cancelled address prompt or an insert
      // error keeps the mode active so the manager can try again.
      const res = await dropOneOffPinAt(lat, lng);
      if (res.status === "dropped" || res.status === "declined") {
        setDropPinMode(false);
      }
    };
    map.on("click", handler);
    return () => { map.off("click", handler); };
  }, [dropPinMode, isManager, member, dropSession]);

  // ── Off-shift map tap → nudge field reps to go on shift ────────────────
  // When a field rep (RM/canvasser) whose pins are gated taps anywhere on the
  // map, flash a reminder. Their pins are hidden, so a tap means they're
  // trying to work — point them at the shift toggle.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pinsHiddenOffShift) return;
    const nudge = () => {
      setUndoToast({ clientUuid: "", pinId: "", label: "You're off shift — go on shift to see and work your pins." });
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => setUndoToast(null), 4_000);
    };
    map.on("click", nudge);
    return () => { try { map.off("click", nudge); } catch { /* map torn down */ } };
  }, [pinsHiddenOffShift, mapInited]);

  // ── Load visits + most recent storm_report for the active pin ──────────
  useEffect(() => {
    if (!activePin) { setVisits([]); setPinStormReport(null); return; }
    setLoadingVisits(true);
    (async () => {
      try {
        const [visitsRes, reportRes] = await Promise.all([
          supabase
            .from("canvas_visits")
            .select("id, outcome, notes, visited_at, visited_by, attempt_number, voice_note_url, photo_storage_path")
            .eq("knock_pin_id", activePin.id)
            .order("visited_at", { ascending: false }),
          supabase
            .from("storm_reports")
            .select("id, short_id, pdf_storage_path, storm_date, property_max_in, data_snapshot")
            .eq("knock_pin_id", activePin.id)
            .order("generated_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);
        if (visitsRes.data) setVisits(visitsRes.data as CanvasVisit[]);
        else if (visitsRes.error && !isNetworkErr(visitsRes.error)) setErr(visitsRes.error.message);
        if (reportRes.data) setPinStormReport(reportRes.data as any);
        // (network errors silently leave state as-is — sheet still works for logging visits)
      } catch (e) {
        if (!isNetworkErr(e)) setErr(String((e as any)?.message ?? e));
      } finally {
        setLoadingVisits(false);
      }
    })();
  }, [activePin]);

  async function logOutcome(outcome: "no_answer" | "contact_made" | "rent" | "follow_up", notes?: string, appointmentAt?: string) {
    if (!activePin || !member) return;
    setSubmitting(true);
    setErr(null);

    // Try to capture the canvasser's actual GPS at the moment of logging (audit)
    let lat: number | undefined, lon: number | undefined;
    if ("geolocation" in navigator) {
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000, enableHighAccuracy: true })
        );
        lat = pos.coords.latitude;
        lon = pos.coords.longitude;
      } catch { /* fail-open: log without GPS */ }
    }

    // Quick win #1 — upload any staged voice/photo media. Storage path is
    // saved on the visit so signed URLs can be regenerated on read.
    //
    // Offline-loss fix (May 2): if uploads fail (e.g. airplane mode),
    // we DON'T clear the staged blob/file — keeps it in pendingMedia so
    // the canvasser sees the chip + a retry hint and can re-tap once
    // online. Visit still logs without the media (we don't block the
    // canvasser from logging the outcome).
    const visitUuid = newClientUuid();
    let voiceUrl: string | null = null;
    let photoPath: string | null = null;
    let voiceUploadFailed = false;
    let photoUploadFailed = false;

    if (pendingMedia.voiceBlob) {
      try {
        const ext = extForBlob(pendingMedia.voiceBlob);
        const path = `canvas-media/${member.id}/${visitUuid}.${ext}`;
        const up = await supabase.storage
          .from("deal-documents")
          .upload(path, pendingMedia.voiceBlob, {
            contentType: pendingMedia.voiceBlob.type || "audio/webm",
            upsert: false,
          });
        if (!up.error) voiceUrl = path;
        else voiceUploadFailed = true;
      } catch {
        voiceUploadFailed = true;
      }
    }
    if (pendingMedia.photoFile) {
      try {
        // Compress to 1280px / JPEG q=0.82 — saves ~80-90% bytes vs raw
        // iPhone shot. Falls open to original on any failure.
        const compressed = await compressImage(pendingMedia.photoFile);
        const ext = compressed.type?.includes("jpeg")
          ? "jpg"
          : (pendingMedia.photoFile.name.split(".").pop() || "jpg").toLowerCase();
        const path = `canvas-media/${member.id}/${visitUuid}.${ext}`;
        const up = await supabase.storage
          .from("deal-documents")
          .upload(path, compressed, {
            contentType: compressed.type || pendingMedia.photoFile.type || "image/jpeg",
            upsert: false,
          });
        if (!up.error) photoPath = path;
        else photoUploadFailed = true;
      } catch {
        photoUploadFailed = true;
      }
    }

    // Submit through the offline queue. If online → upserts immediately; if
    // network is dead or hiccups → queues to IndexedDB, returns success anyway,
    // background sync flushes when online comes back.
    const result = await submitVisit(supabase, {
      client_uuid: visitUuid,
      knock_pin_id: activePin.id,
      visited_by: member.id,
      outcome,
      notes: notes?.trim() || null,
      lat,
      lon,
      appointment_at: appointmentAt ?? null,
      voice_note_url: voiceUrl,
      photo_storage_path: photoPath,
    });

    setSubmitting(false);

    // Selective clear — only drop blobs/files whose upload landed. If a
    // file upload failed (e.g. airplane mode), keep it staged so the
    // canvasser sees the chip + a retry hint. They can tap another
    // outcome once online to re-attach.
    setPendingMedia((prev) => ({
      voiceBlob: voiceUploadFailed ? prev.voiceBlob : null,
      voiceMs:   voiceUploadFailed ? prev.voiceMs   : 0,
      photoFile: photoUploadFailed ? prev.photoFile : null,
    }));
    if (voiceUploadFailed || photoUploadFailed) {
      const which = [
        voiceUploadFailed ? "voice note" : null,
        photoUploadFailed ? "photo" : null,
      ].filter(Boolean).join(" + ");
      setErr(`Visit logged but ${which} couldn't upload — re-tap an outcome once back online to attach.`);
    }

    // Quick win #3 — show 5-second Undo toast
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoToast({ clientUuid: visitUuid, pinId: activePin.id, label: labelForOutcome(outcome) });
    undoTimerRef.current = setTimeout(() => setUndoToast(null), 5_000);

    if (result.queued) {
      // Bump local count so the pill renders immediately, even before the
      // background-sync onChange fires.
      setQueuedCount((n) => n + 1);
      // Optimistic close — visit will land when network returns
      setActivePin(null);
      return;
    }

    // Optimistic: refresh just this pin from DB so UI shows new state immediately
    const { data: updated } = await supabase
      .from("knock_pins")
      .select("id, lat, lon, property_street, property_city, property_state, property_zip, homeowner_name, current_state, attempt_count, cooldown_until, notes, deal_id, parcel_id, drop_session_id, prior_appointment_count, last_appointment_at")
      .eq("id", activePin.id)
      .single();
    if (updated) {
      setPins((prev) => prev.map((p) => p.id === updated.id ? updated as KnockPin : p));
      setActivePin(updated as KnockPin);
    }
    // Refresh visits log
    const { data: vs } = await supabase
      .from("canvas_visits")
      .select("id, outcome, notes, visited_at, visited_by, attempt_number, voice_note_url, photo_storage_path")
      .eq("knock_pin_id", activePin.id)
      .order("visited_at", { ascending: false });
    setVisits((vs ?? []) as CanvasVisit[]);
  }

  // Renders the PDF client-side via pdf-lib, uploads to deal-documents storage,
  // updates storm_reports.pdf_storage_path. Mirrors PropertyTab.tsx flow.
  async function renderAndUploadPdf(reportId: string, dataSnapshot: any, shortId: string): Promise<string | null> {
    try {
      const { renderStormReportPDF } = await import("../lib/stormReportPDF");
      const data = { ...dataSnapshot, short_id: shortId };
      const bytes = await renderStormReportPDF(data);
      const path = `storm-reports/canvas/${activePin!.id}/${Date.now()}.pdf`;
      const { error: upErr } = await supabase.storage
        .from("deal-documents")
        .upload(path, bytes, { contentType: "application/pdf", upsert: true });
      if (upErr) throw upErr;
      const { error: updErr } = await supabase
        .from("storm_reports")
        .update({ pdf_storage_path: path })
        .eq("id", reportId);
      if (updErr) throw updErr;
      return path;
    } catch (e: any) {
      setErr(`PDF render failed: ${e.message ?? e}`);
      return null;
    }
  }

  async function openSignedPdf(path: string) {
    // Open about:blank synchronously inside the click handler so iOS Safari
    // doesn't block the popup, then redirect to the signed URL once it resolves.
    const win = window.open("about:blank", "_blank");
    const { data, error } = await supabase.storage
      .from("deal-documents")
      .createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) {
      setErr(error?.message ?? "Could not open PDF");
      if (win) win.close();
      return;
    }
    if (win) win.location.href = data.signedUrl;
    else window.location.href = data.signedUrl;
  }

  async function generateWeatherReport(opts: { regenerate?: boolean } = {}) {
    if (!activePin) return;

    // Fast-path: existing PDF already on file → just open it
    if (!opts.regenerate && pinStormReport?.pdf_storage_path) {
      await openSignedPdf(pinStormReport.pdf_storage_path);
      return;
    }

    setReportRendering(true);
    setErr(null);
    try {
      // 1. Prepare data via edge function
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const resp = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/storm-report-prepare`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ knock_pin_id: activePin.id, regenerate: !!opts.regenerate }),
        },
      );
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error ?? `HTTP ${resp.status}`);
      if (!body.generated) {
        alert(body.message ?? body.reason ?? "No storm in window for this property.");
        return;
      }

      // 2. Pull the row that was just created/updated so we have data_snapshot
      const { data: row, error: rowErr } = await supabase
        .from("storm_reports")
        .select("id, short_id, pdf_storage_path, storm_date, property_max_in, data_snapshot")
        .eq("id", body.storm_report_id)
        .single();
      if (rowErr || !row) throw new Error("Could not load storm_reports row");

      // 3. Render PDF client-side + upload + persist path
      let pdfPath = row.pdf_storage_path as string | null;
      if (!pdfPath) {
        pdfPath = await renderAndUploadPdf(row.id as string, row.data_snapshot, row.short_id as string);
      }

      // 4. Update local state so the sheet flips to "View report"
      setPinStormReport({
        short_id: row.short_id as string,
        pdf_storage_path: pdfPath,
        storm_date: row.storm_date as string,
        property_max_in: row.property_max_in as number | null,
        data_snapshot: row.data_snapshot,
      });

      // 5. Open it for the canvasser to show the homeowner
      if (pdfPath) await openSignedPdf(pdfPath);
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setReportRendering(false);
    }
  }

  // ── Address search — debounced, parcels + Mapbox geocoder ───────────
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) { setSearchResults([]); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      const results: Array<any> = [];

      // 1. Internal: parcels by street/city (case-insensitive)
      const { data: parcels } = await supabase
        .from("parcels")
        .select("id, street, city, state, zip, lat, lon")
        .or(`street.ilike.%${q}%,city.ilike.%${q}%`)
        .not("lat", "is", null)
        .limit(5);
      for (const p of parcels ?? []) {
        results.push({
          label: p.street ?? "(no street)",
          sublabel: [p.city, p.state, p.zip].filter(Boolean).join(", ") + " · parcel",
          lat: Number(p.lat),
          lon: Number(p.lon),
          source: "parcel",
          parcelId: p.id,
          street: p.street, city: p.city, state: p.state, zip: p.zip,
        });
      }

      // 2. Internal: existing knock_pins by street
      const { data: pinHits } = await supabase
        .from("knock_pins")
        .select("id, property_street, property_city, property_state, property_zip, lat, lon, current_state, attempt_count")
        .or(`property_street.ilike.%${q}%,property_city.ilike.%${q}%`)
        .limit(3);
      for (const p of pinHits ?? []) {
        results.push({
          label: p.property_street,
          sublabel: `${p.property_city}, ${p.property_state} · existing pin (${p.current_state} · ${p.attempt_count}× knocks)`,
          lat: Number(p.lat),
          lon: Number(p.lon),
          source: "pin",
          pinId: p.id,
        });
      }

      // 3. External: Mapbox geocoder fallback (only if we have <5 internal hits)
      if (results.length < 5 && MAPBOX_TOKEN) {
        try {
          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=us&proximity=-84.55,33.95&limit=5&types=address,place`;
          const resp = await fetch(url);
          if (resp.ok) {
            const json = await resp.json();
            for (const f of json.features ?? []) {
              if (!f.center || f.center.length !== 2) continue;
              // Structured parts (Oct 27, 2026) so "Log a lead here" can
              // prefill the address: house number + street, city, state, ZIP.
              // Only for a house (an "address" feature with a number): a city
              // result keeps the old behavior (Drop pin asks for the address).
              const ctx: Array<{ id?: string; text?: string; short_code?: string }> = f.context ?? [];
              const part = (kind: string) => ctx.find((c) => c.id?.startsWith(kind + "."));
              const isHouse = Array.isArray(f.place_type) && f.place_type.includes("address") && !!f.address;
              const streetLine = isHouse ? `${f.address} ${f.text}` : null;
              results.push({
                label: streetLine ?? f.text ?? f.place_name,
                sublabel: f.place_name,
                lon: f.center[0],
                lat: f.center[1],
                source: "mapbox",
                ...(isHouse ? {
                  street: streetLine,
                  city: part("place")?.text ?? null,
                  state: part("region")?.short_code?.split("-")[1] ?? null,
                  zip: part("postcode")?.text ?? null,
                } : {}),
              });
            }
          }
        } catch (e) { /* fail-soft on network errors */ }
      }

      if (cancelled) return;
      setSearchResults(results.slice(0, 10));
    }, 200);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [searchQuery]);

  const SEARCH_PIN_COLS = "id, lat, lon, property_street, property_city, property_state, property_zip, homeowner_name, current_state, attempt_count, cooldown_until, notes, deal_id, parcel_id, drop_session_id, prior_appointment_count, last_appointment_at";

  async function selectSearchResult(r: {
    lat: number; lon: number; label: string; sublabel: string;
    pinId?: string; parcelId?: string;
    street?: string | null; city?: string | null; state?: string | null; zip?: string | null;
  }) {
    const map = mapRef.current;
    if (!map) return;
    startedOnDefaultViewRef.current = false;
    map.easeTo({ center: [r.lon, r.lat], zoom: 17, duration: 600 });
    // Drop a purple "SEARCHED" pin (replaces any prior one)
    if (searchedMarkerRef.current) {
      try { searchedMarkerRef.current.remove(); } catch { /* */ }
      searchedMarkerRef.current = null;
    }
    const el = document.createElement("div");
    el.style.cssText = `
      width: 22px; height: 22px; border-radius: 50%;
      background: #9333ea;
      border: 3px solid #fff;
      box-shadow: 0 2px 6px rgba(147,51,234,0.5);
      cursor: pointer;
    `;
    el.title = `${r.label} — ${r.sublabel}`;
    // Tapping the purple marker: open the existing pin's sheet directly if
    // one was found (the marker can sit right on top of the real pin and
    // block its tap target), otherwise re-open the action card.
    el.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const last = lastSearchActionRef.current;
      if (!last) return;
      if (last.existingPin) openSearchPin(last.existingPin);
      else setSearchAction(last);
    });
    searchedMarkerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat([r.lon, r.lat])
      .addTo(map);
    setShowSearch(false);
    setSearchResults([]);
    setSearchQuery("");

    // Show the action card right away, then resolve whether a knock pin
    // already exists at this house (by pin id > parcel > nearby coords).
    const base = {
      lat: r.lat, lon: r.lon, label: r.label, sublabel: r.sublabel,
      resolving: true, existingPin: null as KnockPin | null,
      street: r.street, city: r.city, state: r.state, zip: r.zip,
    };
    lastSearchActionRef.current = base;
    setSearchAction(base);

    let existing: KnockPin | null = null;
    try {
      if (r.pinId) {
        const { data } = await supabase
          .from("knock_pins").select(SEARCH_PIN_COLS).eq("id", r.pinId).single();
        existing = (data as KnockPin) ?? null;
      } else {
        let parcelId: string | null = r.parcelId ?? null;
        if (!parcelId) {
          const { data: pid } = await supabase.rpc("canvas_nearest_parcel_for", {
            p_lat: r.lat, p_lon: r.lon, p_max_meters: 80,
          });
          parcelId = (pid as string) ?? null;
        }
        if (parcelId) {
          const { data } = await supabase
            .from("knock_pins").select(SEARCH_PIN_COLS)
            .eq("parcel_id", parcelId)
            .order("created_at", { ascending: false })
            .limit(1);
          existing = (data?.[0] as KnockPin) ?? null;
        }
        if (!existing) {
          // Coordinate fallback (~40 m box) for counties without parcel data
          const d = 0.00035;
          const { data } = await supabase
            .from("knock_pins").select(SEARCH_PIN_COLS)
            .gte("lat", r.lat - d).lte("lat", r.lat + d)
            .gte("lon", r.lon - d).lte("lon", r.lon + d)
            .limit(1);
          existing = (data?.[0] as KnockPin) ?? null;
        }
      }
    } catch { /* fail-soft — card just offers Drop pin */ }

    const resolved = { ...base, resolving: false, existingPin: existing };
    lastSearchActionRef.current = resolved;
    // Only update the visible card if it's still showing this search
    setSearchAction((cur) =>
      cur && cur.lat === base.lat && cur.lon === base.lon ? resolved : cur,
    );
  }

  // Open an existing pin found via address search — make sure it's in local
  // state (its marker may not be materialized if it was outside the fetched
  // bbox) and pop the sheet.
  function openSearchPin(pin: KnockPin) {
    setPins((prev) => prev.some((p) => p.id === pin.id)
      ? prev.map((p) => (p.id === pin.id ? pin : p))
      : [pin, ...prev]);
    setActivePin(pin);
    setSearchAction(null);
  }

  // "Drop pin here" from the search action card — reuses the one-off drop
  // flow (parcel auto-fill, dedupe guard, reverse-geocode fallback) at the
  // searched coordinates, then opens the fresh pin's sheet.
  async function dropPinFromSearch() {
    const action = searchAction;
    if (!action || !isManager || !member) return;
    setSearchAction((cur) => cur ? { ...cur, resolving: true } : cur);
    const res = await dropOneOffPinAt(action.lat, action.lon, {
      street: action.street ?? null,
      city: action.city ?? null,
      state: action.state ?? null,
      zip: action.zip ?? null,
    });
    if (res.status === "dropped" && res.pin) {
      // Real pin now marks the house — retire the purple search marker
      if (searchedMarkerRef.current) {
        try { searchedMarkerRef.current.remove(); } catch { /* */ }
        searchedMarkerRef.current = null;
      }
      lastSearchActionRef.current = null;
      setSearchAction(null);
      setActivePin(res.pin);
    } else {
      setSearchAction((cur) => cur ? { ...cur, resolving: false } : cur);
    }
  }

  async function manualFlushQueue() {
    setFlushing(true);
    setErr(null);
    try {
      const result = await flushQueue(supabase);
      setQueuedCount(result.remaining);
      if (result.errors.length > 0 && result.flushed === 0) {
        setErr(`Couldn't flush: ${result.errors[0]}`);
      }
    } catch (e: any) {
      setErr(`Flush failed: ${e?.message ?? e}`);
    } finally {
      setFlushing(false);
    }
  }

  function toggleFilter(state: string) {
    setFilterStates((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  }

  // Ship 10: enter / exit assign mode + submit assignment
  function enterAssignMode() {
    setAssignMode(true);
    setPolygonPoints([]);
    setIsDrawing(false);
    setSelectedReps(new Set());
    setAssignNote("");
    setAreaLabel("");
    setShareMode("share");
    setShowAssignSheet(false);
  }
  function exitAssignMode() {
    setAssignMode(false);
    setPolygonPoints([]);
    setIsDrawing(false);
    setShowAssignSheet(false);
    setSelectedReps(new Set());
    setAreaForDate(null);
    setAreaAnchor(null);
  }
  function toggleRepSelected(id: string) {
    setSelectedReps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  async function submitAssignment() {
    if (polygonPoints.length < 4 || selectedReps.size === 0 || !member) return;
    setSavingAssignment(true);
    setErr(null);
    try {
      const polygon = {
        type: "Polygon" as const,
        coordinates: [polygonPoints],
      };
      const groupId = crypto.randomUUID();   // ties all rep rows to the same logical area
      const assignedToList = Array.from(selectedReps);
      const insideIds = pinsInside.map((p) => p.id);

      const rows = assignedToList.map((rep_id) => ({
        area_group_id: groupId,
        assigned_to: rep_id,
        assigned_by: member.id,
        boundary_geom: polygon,
        pin_ids: insideIds,
        share_mode: shareMode,
        area_label: areaLabel.trim() || null,
        notes: assignNote.trim() || null,
        for_date: areaForDate,
        anchor_deal_id: areaAnchor?.dealId ?? null,
        anchor_kind: areaAnchor?.kind ?? null,
      }));

      const { error } = await supabase.from("canvas_area_assignments").insert(rows);
      if (error) throw error;

      // Toast-style feedback via err state slot (re-purpose for success)
      window.alert(`Area assigned to ${assignedToList.length} rep${assignedToList.length === 1 ? "" : "s"} · ${insideIds.length} pin${insideIds.length === 1 ? "" : "s"} inside.`);
      exitAssignMode();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setSavingAssignment(false);
    }
  }

  // Quick win #3 — undo just-logged visit. Deletes the canvas_visits row by
  // its client_uuid and refreshes the pin from DB so its state recomputes.
  async function undoLastVisit() {
    if (!undoToast) return;
    const { clientUuid, pinId } = undoToast;
    setUndoToast(null);
    if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
    try {
      // Note: BEFORE INSERT trigger on canvas_visits stamped pin state when
      // the row was inserted. Deleting the row doesn't auto-rewind the pin's
      // state — we need to manually re-derive it. The trigger already runs on
      // INSERT, not DELETE. So we just refetch the pin and trust that the
      // visit-row delete + a possible later visit will keep things consistent.
      // For v1 this is acceptable: undo within 5s should almost always be
      // before any subsequent visit on the same pin.
      const { error } = await supabase
        .from("canvas_visits")
        .delete()
        .eq("client_uuid", clientUuid);
      if (error) {
        setErr(`Undo failed: ${error.message}`);
        return;
      }
      // Re-derive the pin's state from its remaining visits. The BEFORE
      // INSERT trigger that set current_state / attempt_count / cooldown_until
      // / no_answer_streak doesn't auto-rewind on delete, so we explicitly
      // call canvas_recompute_pin_state_for_pin which replays each remaining
      // visit through the same state-transition logic.
      const { error: recErr } = await supabase.rpc("canvas_recompute_pin_state_for_pin", { p_pin_id: pinId });
      if (recErr && !isNetworkErr(recErr)) {
        setErr(`Undo recompute failed: ${recErr.message}`);
      }
      // Refresh the pin to update its state in the UI
      const { data: updated } = await supabase
        .from("knock_pins")
        .select("id, lat, lon, property_street, property_city, property_state, property_zip, homeowner_name, current_state, attempt_count, cooldown_until, notes, deal_id, parcel_id, drop_session_id, prior_appointment_count, last_appointment_at")
        .eq("id", pinId)
        .single();
      if (updated) {
        setPins((prev) => prev.map((p) => p.id === pinId ? (updated as any) : p));
      }
    } catch (e: any) {
      if (!isNetworkErr(e)) setErr(`Undo failed: ${String(e?.message ?? e)}`);
    }
  }

  // Quick win #2 — nearest open pin to my current location, for the
  // "↑ 23 ft to Carter — 1247 Mt Airy Loop" hint.
  const nearestPin = useMemo(() => {
    if (!myPos || pins.length === 0) return null;
    let best: { pin: KnockPin; ft: number; bearing: number } | null = null;
    for (const p of pins) {
      // Only suggest pins worth knocking next: skip retired states
      if (["do_not_knock","wrong_address","not_home_3x","built","legacy_client"].includes(p.current_state)) continue;
      // Don't point at a pin the storm-area filter is hiding.
      if (pinIdsInStorms !== null && !pinIdsInStorms.has(p.id)) continue;
      const ft = haversineFeet(myPos.lat, myPos.lon, p.lat, p.lon);
      if (best == null || ft < best.ft) {
        best = { pin: p, ft, bearing: bearingDeg(myPos.lat, myPos.lon, p.lat, p.lon) };
      }
    }
    return best;
  }, [myPos, pins, pinIdsInStorms]);

  // ── Render ─────────────────────────────────────────────────────────────
  // z-50: the Layout header is `sm:sticky sm:z-40` on desktop, which used to
  // paint OVER this full-screen tool's own top bar (hiding the 🔎/⛈/📐/⋯
  // toolbar entirely on desktop — mobile was fine because the header isn't
  // sticky there). Canvas is a full-screen takeover with its own ← back
  // button, so it should cover the app header on every screen size. The
  // commission-addendum gate (z-[200]) still deliberately covers canvas.
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900">
      {/* Top bar — compact icon-only buttons; manager actions in ⋯ menu.
          ref + useVisualViewportPin keep this anchored to the visible
          viewport even if iOS Safari decides to pinch-zoom the page. */}
      <div ref={topBarRef} className="relative z-30 flex items-center justify-between border-b border-slate-800 bg-slate-900 px-2 py-2 text-white">
        <button
          onClick={() => navigate("/")}
          className="flex h-9 w-9 items-center justify-center rounded-md text-xl text-slate-300 hover:bg-slate-800"
          title="Back to Home"
          aria-label="Back to Home"
        >
          ←
        </button>
        {/* Center cluster — shift toggle (replaces static "Canvas" title).
            Day stats render below the top bar in their own row when on shift. */}
        <div className="flex flex-1 items-center justify-center px-2">
          <button
            onClick={() => {
              if (starting || ending) return;
              if (onShift) {
                if (window.confirm("End your shift? Your GPS trail will stop and today's totals will lock in.")) {
                  void endShift();
                }
              } else {
                void startShift();
              }
            }}
            disabled={starting || ending}
            className={[
              "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow transition disabled:opacity-60",
              onShift
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-slate-700 text-slate-200 hover:bg-slate-600",
            ].join(" ")}
            title={onShift ? "Tap to end shift" : "Tap to go on shift (starts GPS audit trail)"}
            aria-label={onShift ? "End shift" : "Start shift"}
          >
            <span className={[
              "h-2 w-2 rounded-full",
              onShift ? "bg-white animate-pulse" : "bg-slate-400",
            ].join(" ")}/>
            <span>{starting ? "Starting…" : ending ? "Ending…" : onShift ? "On shift" : "Off shift"}</span>
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Canvassers: the bottom nav is hidden here, so My Pay gets its own
              button (Oct 26, 2026). Since Oct 27, ← goes to their own home
              page (sign out, areas, Log a lead) instead of bouncing back. */}
          {member?.role === "canvasser" && (
            <IconButton
              active={false}
              onClick={() => navigate("/my-pay")}
              label="My Pay"
              icon="💰"
            />
          )}
          <IconButton
            active={showSearch}
            onClick={() => setShowSearch((s) => !s)}
            label="Search address"
            icon="🔎"
          />
          <IconButton
            active={showStorms}
            onClick={() => setShowStorms((s) => !s)}
            label="Toggle storms"
            icon="⛈"
          />
          <IconButton
            active={showAreas}
            onClick={() => setShowAreas((s) => !s)}
            label="Toggle assigned areas"
            icon="📐"
          />
          <IconButton
            active={false}
            onClick={() => setMapStyle((s) => (s === "satellite" ? "streets" : "satellite"))}
            label="Toggle map style"
            icon={mapStyle === "satellite" ? "🛰" : "🗺"}
          />
          {isManager ? (
            <div className="relative">
              <IconButton
                active={dropPinMode || assignMode || showActionsMenu}
                onClick={() => setShowActionsMenu((s) => !s)}
                label="Manager actions"
                icon="⋯"
              />
              {showActionsMenu ? (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowActionsMenu(false)}
                  />
                  <div className="absolute right-0 top-11 z-40 w-48 overflow-hidden rounded-lg bg-white text-slate-900 shadow-2xl">
                    {/* Drop session — continuous instant-drop mode */}
                    <button
                      onClick={async () => {
                        setShowActionsMenu(false);
                        if (assignMode) exitAssignMode();
                        if (dropSession) {
                          // End: batch-process all queued drops. The function
                          // owns the wrap-up itself — review modal on success,
                          // session kept open (with the unsaved drops still
                          // queued) on a partial failure.
                          await processDropSessionEnd();
                        } else {
                          // Start a new session
                          const { data, error } = await supabase.rpc("canvas_start_drop_session", { p_label: null });
                          if (error) { setErr(error.message); return; }
                          const row = (data && (data as any).id) ? data : null;
                          if (row) setDropSession({ id: row.id, pin_count: row.pin_count, started_at: row.started_at, label: row.label });
                          setDropPinMode(false); // mutually exclusive with one-off mode
                        }
                      }}
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-sm font-semibold ${
                        dropSession ? "bg-orange-50 text-orange-700" : "hover:bg-slate-100"
                      }`}
                    >
                      <span className="text-base">{dropSession ? "🛑" : "🎯"}</span>
                      {dropSession ? `End drop session (${dropSession.pin_count})` : "Start drop session"}
                    </button>

                    {/* One-off drop (legacy, single-pin) */}
                    <button
                      onClick={() => {
                        setShowActionsMenu(false);
                        if (assignMode) exitAssignMode();
                        if (dropSession) return;  // suppressed when session active
                        setDropPinMode((d) => !d);
                      }}
                      className={`flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-sm font-semibold ${
                        dropPinMode ? "bg-orange-50 text-orange-700" : "hover:bg-slate-100"
                      } ${dropSession ? "pointer-events-none opacity-40" : ""}`}
                    >
                      <span className="text-base">📍</span>
                      {dropPinMode ? "Cancel drop pin" : "Drop one pin"}
                    </button>
                    <button
                      onClick={() => {
                        setShowActionsMenu(false);
                        setDropPinMode(false);
                        if (assignMode) exitAssignMode();
                        else enterAssignMode();
                      }}
                      className={`flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-sm font-semibold ${
                        assignMode ? "bg-orange-50 text-orange-700" : "hover:bg-slate-100"
                      }`}
                    >
                      <span className="text-base">✏️</span>
                      {assignMode ? "Exit assign mode" : "Assign area"}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Day-stats row — appears under the top bar when on shift. Mirrors the
          prototype's KNOCKS · CONTACTS · APPS · MI WALKED tile strip. Tap a
          tile → matching pins are gold-ringed on the map and the camera
          fits them. Tap again (or another tile) to swap / clear. */}
      {onShift && shift ? (
        <div className="z-20 flex items-stretch gap-1.5 border-b border-slate-800 bg-slate-900 px-2 py-1.5 text-white">
          <ShiftStatTile value={shift.visit_count} label="Knocks"
            active={tileSel === "knocks"}
            onClick={() => setTileSel((s) => s === "knocks" ? null : "knocks")} />
          <ShiftStatTile value={shift.contact_count} label="Contacts"
            active={tileSel === "contacts"}
            onClick={() => setTileSel((s) => s === "contacts" ? null : "contacts")} />
          <ShiftStatTile value={shift.appointments_set} label="Appts"
            active={tileSel === "appts"}
            onClick={() => setTileSel((s) => s === "appts" ? null : "appts")} />
          <ShiftStatTile value={shift.claim_count} label="Claims"
            active={tileSel === "claims"}
            onClick={() => setTileSel((s) => s === "claims" ? null : "claims")} />
          <ShiftStatTile
            value={shift.miles_walked != null ? shift.miles_walked.toFixed(1) : "—"}
            label="Mi walked"
            active={tileSel === "miles"}
            onClick={() => setTileSel((s) => s === "miles" ? null : "miles")} />
        </div>
      ) : null}

      {/* Location banner (Oct 27, 2026) — the phone isn't giving a location:
          what's wrong, and the steps for this phone to turn it back on. */}
      {locProblem ? <LocationBanner key={locProblem} problem={locProblem} onRetry={myLoc.retry} /> : null}

      {/* Off-shift banner (Jun 8, 2026) — for field reps (RM/canvasser) whose
          pins are hidden until they go on shift. Explains the empty map and
          gives a one-tap way to start the shift. Assigned areas still show. */}
      {pinsHiddenOffShift ? (
        <div className="z-20 flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="flex items-center gap-2">
            <span className="text-base">📍</span>
            <span>You're <strong>off shift</strong> — go on shift to load your pins.</span>
          </span>
          <button
            type="button"
            onClick={() => { if (!starting) startShift(); }}
            disabled={starting}
            className="shrink-0 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow hover:bg-emerald-700 disabled:opacity-60"
          >
            {starting ? "Starting…" : "Go on shift"}
          </button>
        </div>
      ) : null}

      {/* Distance hint — nearest still-knockable pin. Only renders when on
          shift AND we have a fresh GPS fix AND there's an open pin within
          a useful range (≤ 1000 ft). */}
      {onShift && nearestPin && nearestPin.ft <= 1000 ? (
        <button
          type="button"
          onClick={() => {
            const m = mapRef.current;
            if (m) m.flyTo({ center: [nearestPin.pin.lon, nearestPin.pin.lat], zoom: 18, speed: 1.4 });
          }}
          className="z-10 flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-1.5 text-left text-xs text-slate-700 shadow-sm hover:bg-slate-50"
        >
          <span className="text-base text-orange-500">{arrowForBearing(nearestPin.bearing)}</span>
          <span><strong className="font-bold tabular-nums">{Math.round(nearestPin.ft)} ft</strong> to {nearestPin.pin.homeowner_name?.split(" ").slice(-1)[0] ?? nearestPin.pin.property_street?.split(" ").slice(0, 2).join(" ") ?? "next pin"}{nearestPin.pin.homeowner_name ? ` — ${nearestPin.pin.property_street}` : ""}</span>
          <span className="ml-auto text-slate-400">→</span>
        </button>
      ) : null}

      {/* Map */}
      <div ref={mapDiv} className="flex-1 relative">
        {/* Address search panel — May 11, 2026 redesign.
            Mobile: full-width drop-down across the top of the map with a
            large (text-base = 16px so iOS doesn't zoom on focus) input.
            Desktop: keeps the compact floating panel.
            Results capped at 55dvh so the iOS keyboard never covers them. */}
        {showSearch ? (
          <div
            className="absolute left-0 right-0 top-0 z-20 w-full rounded-none bg-white p-2 shadow-2xl sm:left-3 sm:right-auto sm:top-3 sm:w-[400px] sm:max-w-[94vw] sm:rounded-lg"
            style={{ touchAction: "manipulation" }}
          >
            <div className="flex items-center gap-2">
              <span className="pl-1 text-lg">🔎</span>
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search any GA address, street, or city…"
                enterKeyHint="search"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                /* text-base (16px) prevents iOS from zooming on focus.
                   Bigger py for a comfier mobile tap target. Desktop
                   keeps the compact text-sm. */
                className="flex-1 rounded-md border border-slate-200 px-3 py-2.5 text-base focus:border-orange-500 focus:outline-none sm:py-1.5 sm:text-sm"
              />
              <button
                onClick={() => { setShowSearch(false); setSearchQuery(""); setSearchResults([]); }}
                className="px-2 text-2xl leading-none text-slate-400 hover:text-slate-700"
                aria-label="Close search"
              >
                ×
              </button>
            </div>
            {searchResults.length > 0 ? (
              <div className="mt-2 overflow-y-auto" style={{ maxHeight: "55dvh" }}>
                {searchResults.map((r, i) => (
                  <button
                    key={`${r.source}-${i}-${r.lat}-${r.lon}`}
                    onClick={() => selectSearchResult(r)}
                    className="block w-full rounded-md px-2 py-2 text-left text-sm hover:bg-slate-100 sm:py-1.5 sm:text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold ${
                        r.source === "pin"   ? "bg-orange-100 text-orange-700" :
                        r.source === "deal"  ? "bg-cyan-100 text-cyan-700" :
                        r.source === "parcel" ? "bg-slate-100 text-slate-600" :
                        "bg-purple-100 text-purple-700"
                      }`}>
                        {r.source === "pin" ? "📍" : r.source === "deal" ? "💼" : r.source === "parcel" ? "🏠" : "🌐"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-slate-900">{r.label}</div>
                        <div className="truncate text-[11px] text-slate-500 sm:text-[10px]">{r.sublabel}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : searchQuery.trim().length >= 3 ? (
              <div className="mt-2 px-2 py-1.5 text-xs text-slate-500">No matches yet…</div>
            ) : null}
          </div>
        ) : null}

        {/* Storm deep-link banner — one storm date in focus (from the audit board). */}
        {focusStormDate ? (
          <div className="absolute left-1/2 top-14 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-900/90 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur">
            <span>⛈ Storm {new Date(focusStormDate + "T12:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} only</span>
            <span className="font-mono text-[10px] opacity-75">
              {(showRadar ? (stormGeoJsonDisplayed?.features?.length ?? 0) : 0).toLocaleString()} sigs
            </span>
            <button
              onClick={() => {
                setFocusStormDate(null);
                try {
                  const u = new URL(window.location.href);
                  ["storm", "lat", "lon"].forEach((k) => u.searchParams.delete(k));
                  window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
                } catch { /* ignore */ }
              }}
              className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] hover:bg-white/30"
              title="Show every storm date again"
            >
              ✕ Show all
            </button>
          </div>
        ) : null}

        {/* Drop session ribbon — appears whenever a session is open. Manager
            taps a roof anywhere on the map to drop pins instantly. */}
        {dropSession ? (
          <div className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
            <span>🎯 Drop session</span>
            <span className="rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-bold">{dropSession.pin_count} pin{dropSession.pin_count === 1 ? "" : "s"}</span>
            {processing ? (
              <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                Processing {processing.done}/{processing.total}…
              </span>
            ) : null}
            <button
              onClick={async () => {
                // processDropSessionEnd owns the wrap-up: review modal on
                // success, session kept open on a partial failure.
                await processDropSessionEnd();
              }}
              disabled={!!processing}
              className="rounded-full bg-slate-900/90 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-50"
            >End</button>
          </div>
        ) : null}

        {dropPinMode && !dropSession ? (
          <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
            🎯 Tap a roof to drop a knock pin
          </div>
        ) : null}

        {/* Ship 10: draw-mode instruction toast (shown until a polygon is drawn) */}
        {assignMode && polygonPoints.length === 0 ? (
          <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
            ✏️ Drag to draw a shape — release to close
          </div>
        ) : null}

        {/* Mid-draw indicator — sits at top while user is dragging */}
        {assignMode && isDrawing ? (
          <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
            ✏️ Drawing… release to close
          </div>
        ) : null}

        {/* Polygon-closed confirm panel */}
        {assignMode && !isDrawing && polygonPoints.length >= 4 ? (
          <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 w-[min(94vw,420px)] rounded-lg bg-white p-3 shadow-2xl">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700">
                {pinsInside.length} pin{pinsInside.length === 1 ? "" : "s"} inside
              </span>
              <span className="font-mono text-slate-500">{polygonPoints.length - 1} pts</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => setPolygonPoints([])}
                className="rounded-md bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
              >
                ↺ Redraw
              </button>
              <button
                onClick={() => setShowAssignSheet(true)}
                disabled={pinsInside.length === 0}
                className="flex-1 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirm area · {pinsInside.length} pin{pinsInside.length === 1 ? "" : "s"} →
              </button>
            </div>
          </div>
        ) : null}

        {/* Bottom-left controls — stack vertically so the Offline / queue pill
            sits ABOVE Filters and never competes with the storm chip on the
            right side of the bottom row on phone widths. */}
        <div className="absolute bottom-3 left-3 z-10 flex flex-col items-start gap-2">
          {!isOnline ? (
            <div
              className="flex items-center gap-1.5 rounded-full bg-slate-700/95 px-3 py-2 text-xs font-bold text-white shadow-lg"
              title="No network — visits will queue and sync when connection returns"
            >
              <span>📵</span>
              <span>Offline</span>
              {queuedCount > 0 ? (
                <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] leading-none">
                  {queuedCount} pending
                </span>
              ) : null}
            </div>
          ) : queuedCount > 0 ? (
            <button
              onClick={manualFlushQueue}
              disabled={flushing}
              className="flex items-center gap-1.5 rounded-full bg-amber-500/95 px-3 py-2 text-xs font-bold text-white shadow-lg disabled:opacity-60"
              title="Tap to retry sending queued visits now"
              aria-label="Retry queued visits"
            >
              <span>{flushing ? "⟳" : "📡"}</span>
              <span>{flushing ? "Syncing…" : `${queuedCount} pending`}</span>
            </button>
          ) : null}

          <button
            onClick={() => setShowFiltersSheet(true)}
            className="flex items-center gap-2 rounded-full bg-slate-900/85 px-3 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur hover:bg-slate-800"
            aria-label="Pin filters"
          >
            <span>🔘</span>
            <span>Filters</span>
            {filterStates.size < Object.keys(STATE_STYLE).length ? (
              <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold leading-none">
                {filterStates.size}/{Object.keys(STATE_STYLE).length}
              </span>
            ) : null}
          </button>

          {/* Reps-live pill — counts other reps currently on shift (not you) */}
          {(() => {
            const otherReps = activeReps.filter((r) => !member || r.user_id !== member.id);
            if (otherReps.length === 0) return null;
            return (
              <div
                className="flex items-center gap-1.5 rounded-full bg-slate-700/85 px-2.5 py-2 text-[10px] font-bold text-white shadow-lg"
                title={otherReps.map((r) => `${r.full_name} (${r.visits}k · ${r.appts}a)`).join("\n")}
              >
                <span className="block h-2 w-2 animate-pulse rounded-full bg-emerald-400"/>
                <span>{otherReps.length} rep{otherReps.length === 1 ? "" : "s"} live</span>
              </div>
            );
          })()}
        </div>

        {/* Storm legend (collapsed chip OR full panel) — only when overlay is on */}
        {showStorms && !stormPanelOpen ? (
          <button
            onClick={() => setStormPanelOpen(true)}
            className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-slate-900/85 px-3 py-2 text-[11px] font-semibold text-white shadow-lg backdrop-blur hover:bg-slate-800"
            title="Tap to open storm filters"
          >
            <span>{stormExpiringWindow > 0 ? "🚨" : "⛈"}</span>
            <span>
              {stormExpiringWindow > 0 ? `≤${stormExpiringWindow}d` : fmtDaysShort(stormDaysWindow)}
            </span>
            <span className="font-mono text-[9px] opacity-75">
              {(
                (showRadar ? (stormGeoJsonDisplayed?.features?.length ?? 0) : 0)
                + (showLsrHail ? (lsrGeoJsonDisplayed?.features?.length ?? 0) : 0)
                + (showLsrWind ? (lsrWindGeoJsonDisplayed?.features?.length ?? 0) : 0)
              ).toLocaleString()}
            </span>
            {stormPinFilterActive ? (
              <span title="Pins outside the selected storm dates are hidden">📍</span>
            ) : null}
          </button>
        ) : null}

        {showStorms && stormPanelOpen ? (
          <div className="absolute bottom-3 right-3 z-10 rounded-md bg-slate-900/85 p-2 text-[10px] text-white shadow-lg backdrop-blur">
            <div className="mb-1 flex items-center justify-between gap-3">
              <span className="font-semibold uppercase tracking-wider text-slate-400">
                {stormExpiringWindow > 0 ? `Storms · expiring ≤${stormExpiringWindow}d` : `Storms · ${fmtDaysLabel(stormDaysWindow)}`}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] text-slate-500">
                  {(stormGeoJsonDisplayed?.features?.length ?? 0).toLocaleString()} sigs
                  {excludedStormDates.size > 0 ? (
                    <span className="ml-1 text-slate-600">
                      ({((stormGeoJson?.features?.length ?? 0) - (stormGeoJsonDisplayed?.features?.length ?? 0)).toLocaleString()} hidden)
                    </span>
                  ) : null}
                  {stormPinFilterActive ? (
                    <span
                      className="ml-1 rounded-full bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-bold text-white"
                      title="Knock pins outside the selected storm dates are hidden (toggle in 📅 Storm dates)"
                    >
                      📍 pins
                    </span>
                  ) : null}
                </span>
                <button
                  onClick={() => setStormPanelOpen(false)}
                  className="-mr-1 -my-0.5 flex h-5 w-5 items-center justify-center rounded text-slate-300 hover:bg-slate-700 hover:text-white"
                  title="Collapse panel"
                  aria-label="Collapse panel"
                >
                  ▾
                </button>
              </div>
            </div>

            {/* Expiring-soon row — two buttons side by side, mutually exclusive */}
            <div className="mb-1.5 flex gap-1">
              <button
                onClick={() => setStormExpiringWindow((v) => v === 30 ? 0 : 30)}
                className={`flex-1 rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                  stormExpiringWindow === 30
                    ? "bg-red-600 text-white"
                    : "bg-slate-700 text-red-300 hover:bg-slate-600"
                }`}
                title="Storms 335–365 days old (≤30 days from 1-year claim deadline)"
              >
                🚨 ≤30d
              </button>
              <button
                onClick={() => setStormExpiringWindow((v) => v === 60 ? 0 : 60)}
                className={`flex-1 rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                  stormExpiringWindow === 60
                    ? "bg-red-500 text-white"
                    : "bg-slate-700 text-red-300 hover:bg-slate-600"
                }`}
                title="Storms 305–365 days old (≤60 days from 1-year claim deadline)"
              >
                🚨 ≤60d
              </button>
            </div>

            {/* Window pills (disabled / dimmed when expiring mode is on) */}
            <div className={`mb-1.5 flex flex-wrap gap-1 ${stormExpiringWindow > 0 ? "opacity-40" : ""}`}>
              {[7, 30, 90, 180, 365].map((d) => (
                <button
                  key={d}
                  onClick={() => { setStormExpiringWindow(0); setStormDaysWindow(d); }}
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                    stormExpiringWindow === 0 && stormDaysWindow === d ? "bg-orange-500 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                  }`}
                >
                  {fmtDaysShort(d)}
                </button>
              ))}
            </div>

            {/* Layer toggles — each independent, both can be on/off in any combo */}
            <div className="mb-1.5 grid grid-cols-2 gap-1">
              <button
                onClick={() => setShowRadar((v) => !v)}
                className={`flex items-center justify-between rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                  showRadar ? "bg-orange-500 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
                title="NEXRAD radar hail signatures (MEHS ≥ size floor). No probability/severity filter."
              >
                <span>📡 Radar</span>
                <span className="font-mono text-[9px] opacity-80">
                  {showRadar ? (stormGeoJsonDisplayed?.features?.length ?? 0).toLocaleString() : "OFF"}
                </span>
              </button>
              <button
                onClick={() => setShowLsrHail((v) => !v)}
                className={`flex items-center justify-between rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                  showLsrHail ? "bg-orange-500 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
                title="NWS Local Storm Reports — ground-truth hail observations from spotters / public / trained reporters."
              >
                <span>📋 NWS hail</span>
                <span className="font-mono text-[9px] opacity-80">
                  {showLsrHail ? (lsrGeoJsonDisplayed?.features?.length ?? 0).toLocaleString() : "OFF"}
                </span>
              </button>
            </div>

            {/* NWS wind layer — gust reports with a measured/estimated speed
                (Jul 12, 2026). Speed reports ONLY per Greg — the ~1,850
                no-speed damage reports are excluded server-side so the mph
                slider below governs everything on this layer. */}
            <button
              onClick={() => setShowLsrWind((v) => !v)}
              className={`mb-1.5 flex w-full items-center justify-between rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                showLsrWind ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
              title="NWS Local Storm Reports — wind gusts with a measured or estimated speed. Damage-only reports (no mph) are not shown. Filter by the mph slider."
            >
              <span>💨 NWS wind</span>
              <span className="font-mono text-[9px] opacity-80">
                {showLsrWind ? (lsrWindGeoJsonDisplayed?.features?.length ?? 0).toLocaleString() : "OFF"}
              </span>
            </button>

            {/* Wind mph floor slider — client-side filter, instant. 58 mph =
                NWS severe-thunderstorm criterion, marked on the scale. */}
            {showLsrWind ? (
              <div className="mb-1.5 rounded-md bg-slate-800/60 px-2 pb-1 pt-1.5">
                <div className="mb-0.5 flex items-center justify-between">
                  <span className="font-bold text-slate-200">
                    Wind ≥ <span style={{ color: windColor(windMinMph) }}>{windMinMph} mph</span>
                  </span>
                  <span className="font-mono text-[9px] text-slate-400">
                    {(lsrWindGeoJsonDisplayed?.features?.length ?? 0).toLocaleString()} shown
                  </span>
                </div>
                <input
                  type="range"
                  min={WIND_MPH_MIN}
                  max={WIND_MPH_MAX}
                  step={1}
                  value={windMinMph}
                  onChange={(e) => setWindMinMph(Number(e.target.value))}
                  className="w-full accent-blue-500"
                  aria-label="Minimum wind speed in mph"
                />
                <div className="flex justify-between font-mono text-[8px] text-slate-500">
                  <span>{WIND_MPH_MIN}</span>
                  <span title="NWS severe-thunderstorm wind criterion">58 = severe</span>
                  <span>{WIND_MPH_MAX}+</span>
                </div>
              </div>
            ) : null}

            {/* Radar high-confidence toggle — POH ≥ 50 AND POSH ≥ 50 when ON.
                When OFF, every nx3hail_all cell shows (includes low-confidence
                echoes / graupel / noise). Radar-only; no effect on NWS layer. */}
            <button
              onClick={() => setRadarHighConf((v) => !v)}
              className={`mb-1.5 flex w-full items-center justify-between rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                radarHighConf
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
              title="Radar high-confidence filter: keeps only cells where Probability of Hail ≥ 50% AND Probability of Severe Hail ≥ 30%. Catches NEXRAD-undercalled cells like the Mar 16, 2026 Milton storm. Turn OFF to see every NEXRAD signature including low-confidence echoes (graupel, noise, weak cells)."
            >
              <span>🎯 High-confidence radar only</span>
              <span className="font-mono text-[9px] opacity-80">
                {radarHighConf ? "ON" : "ALL"}
              </span>
            </button>

            {/* Sub-1.0" toggle — adds NEXRAD low-confidence tier to the map */}
            <button
              onClick={() => setStormIncludeSub1((v) => !v)}
              className={`mb-1.5 flex w-full items-center justify-between rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                stormIncludeSub1
                  ? "bg-slate-500 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
              title="Include NEXRAD radar cells with MEHS 0.75–0.99″ (radar layer only). Radar often undercalls small / brief / poorly-sampled cells. NWS reports are always shown regardless of this toggle."
            >
              <span className="flex items-center gap-1.5">
                <span className="block h-2 w-3 rounded" style={{ background: "#94a3b8" }} />
                <span>Show 0.75–1.0″</span>
              </span>
              <span className="font-mono text-[9px] opacity-80">
                {stormIncludeSub1 ? "ON" : "OFF"}
              </span>
            </button>

            {/* Storm-date picker — MANAGER ONLY (May 11, 2026).
                Lets a manager hide specific storm dates from BOTH layers, e.g.
                turn off May & June dates to focus on July–September. Persists
                per-device via localStorage. Pre-filters the GeoJSON before it
                hits Mapbox sources so cluster_count reflects the selection. */}
            {isManager && stormDateOptions.length > 0 ? (
              <div className="mb-1.5">
                <button
                  onClick={() => setStormDatePickerOpen((v) => !v)}
                  className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                    excludedStormDates.size > 0
                      ? "bg-amber-600 text-white"
                      : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                  }`}
                  title={
                    excludedStormDates.size > 0
                      ? `${excludedStormDates.size} storm date${excludedStormDates.size === 1 ? "" : "s"} hidden across radar + NWS layers.${stormPinFilterActive ? " Knock pins outside the remaining storm areas are hidden too." : ""}`
                      : `Manager-only: hide individual storm dates (e.g., turn off May/June to focus on July–September).${stormPinFilterActive ? " Knock pins outside the storm areas on the map are hidden (📍 toggle inside)." : ""}`
                  }
                >
                  <span className="flex items-center gap-1.5">
                    <span>📅 Storm dates</span>
                    {excludedStormDates.size > 0 ? (
                      <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[9px] font-mono">
                        {excludedStormDates.size} hidden
                      </span>
                    ) : (
                      <span className="text-slate-500 text-[9px] font-normal">manager</span>
                    )}
                    {stormPinFilterActive ? (
                      <span
                        className="rounded-full bg-white/20 px-1.5 py-0.5 text-[9px] font-mono"
                        title="Knock pins outside the selected storm dates are hidden"
                      >
                        📍 pins
                      </span>
                    ) : null}
                  </span>
                  <span className="font-mono text-[9px] opacity-80">
                    {stormDatePickerOpen ? "▴" : "▾"}
                  </span>
                </button>

                {stormDatePickerOpen ? (
                  <div className="mt-1 rounded-md bg-slate-800/60 p-1">
                    {/* Bulk actions — "Hide all" is gated on whether every
                        VISIBLE date is already hidden (not on Set size equality),
                        which matters when excludedStormDates has stale entries
                        from a prior time window that aren't in the current view. */}
                    <div className="mb-1 flex gap-1">
                      <button
                        onClick={() => setExcludedStormDates(new Set())}
                        disabled={excludedStormDates.size === 0}
                        className="flex-1 rounded bg-slate-700 px-1.5 py-1 text-[9px] font-bold text-slate-200 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Show every storm date — clears the full saved selection, including any dates from prior time windows."
                      >
                        ✓ Show all
                      </button>
                      <button
                        onClick={() => setExcludedStormDates((prev) => {
                          // Merge currently-visible dates into existing selection so we
                          // don't drop stale entries from prior windows that the user
                          // is implicitly carrying forward.
                          const next = new Set(prev);
                          for (const o of stormDateOptions) next.add(o.date);
                          return next;
                        })}
                        disabled={stormDateOptions.every((o) => excludedStormDates.has(o.date))}
                        className="flex-1 rounded bg-slate-700 px-1.5 py-1 text-[9px] font-bold text-slate-200 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Hide every storm date currently visible in this time window."
                      >
                        ✕ Hide all
                      </button>
                    </div>

                    {/* Pins-in-storms toggle (Sep 28, 2026). While ON, knock
                        pins outside the storm areas on the map (the 1-mile
                        halos of every date still checked) are hidden; hiding
                        dates narrows the pins with them. Default ON; this is
                        the opt-out. */}
                    <div className="mb-1 rounded bg-slate-900/40 p-1">
                      <button
                        onClick={() => setPinsInStormsOnly((v) => !v)}
                        className={`flex w-full items-center justify-between rounded px-1.5 py-1 text-[10px] font-bold transition-colors ${
                          pinsInStormsOnly
                            ? "bg-amber-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                        title="Hide every knock pin that isn't inside one of the storm areas on the map. Hiding dates narrows the pins with the storms."
                      >
                        <span>📍 Pins: selected storms only</span>
                        <span className="font-mono text-[9px] opacity-80">{pinsInStormsOnly ? "ON" : "OFF"}</span>
                      </button>
                      <div className="mt-0.5 px-1 text-[9px] leading-snug text-slate-400">
                        {!pinsInStormsOnly
                          ? "All pins show regardless of the storm areas."
                          : (stormPinIndex?.size ?? 0) === 0
                            ? "No storm areas on the map — every pin is hidden. Show a date, turn a layer on, or switch this off."
                            : `Showing only pins within 1 mi of a drawn storm — ${(stormDateOptions.length - stormDatesHiddenCount).toLocaleString()} of ${stormDateOptions.length.toLocaleString()} storm date${stormDateOptions.length === 1 ? "" : "s"} on the map.`}
                      </div>
                    </div>

                    {/* Scrollable date list — newest first */}
                    <div className="max-h-48 overflow-y-auto pr-0.5">
                      {stormDateOptions.map((opt) => {
                        const hidden = excludedStormDates.has(opt.date);
                        // Format YYYY-MM-DD as "Mon DD (Wkday)" using the same
                        // +T12:00:00 date-only-string trick the portal already
                        // uses elsewhere (avoids the EDT/UTC midnight off-by-one).
                        const dateObj = new Date(opt.date + "T12:00:00");
                        const label = dateObj.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          weekday: "short",
                          year: "numeric",
                        });
                        return (
                          <button
                            key={opt.date}
                            onClick={() => {
                              setExcludedStormDates((prev) => {
                                const next = new Set(prev);
                                if (next.has(opt.date)) next.delete(opt.date);
                                else next.add(opt.date);
                                return next;
                              });
                            }}
                            className={`flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-[10px] transition-colors ${
                              hidden
                                ? "bg-slate-900/40 text-slate-500 hover:bg-slate-700/60"
                                : "bg-transparent text-slate-200 hover:bg-slate-700/60"
                            }`}
                          >
                            <span className="flex items-center gap-1.5 truncate">
                              <span
                                className={`inline-flex h-3 w-3 flex-none items-center justify-center rounded-sm border ${
                                  hidden
                                    ? "border-slate-600 bg-slate-900/60"
                                    : "border-emerald-400 bg-emerald-500/80"
                                }`}
                                aria-hidden="true"
                              >
                                {hidden ? null : <span className="text-[8px] font-black leading-none text-slate-900">✓</span>}
                              </span>
                              <span className={hidden ? "line-through" : ""}>{label}</span>
                            </span>
                            <span className="flex flex-none items-center gap-1 font-mono text-[9px] text-slate-400">
                              {opt.radar > 0 ? (
                                <span title={`${opt.radar} NEXRAD radar signature${opt.radar === 1 ? "" : "s"}`}>📡 {opt.radar}</span>
                              ) : null}
                              {opt.lsr > 0 ? (
                                <span title={`${opt.lsr} NWS LSR hail report${opt.lsr === 1 ? "" : "s"}`}>📋 {opt.lsr}</span>
                              ) : null}
                              {opt.wind > 0 ? (
                                <span title={`${opt.wind} NWS wind report${opt.wind === 1 ? "" : "s"} ≥ ${windMinMph} mph`}>💨 {opt.wind}</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="border-t border-slate-700 pt-1">
              {stormIncludeSub1 ? (
                <div className="flex items-center gap-1.5">
                  <span className="block h-2 w-3 rounded" style={{ background: "#94a3b8" }} />
                  <span>0.75–1.0″ <span className="text-slate-500">(low conf.)</span></span>
                </div>
              ) : null}
              <div className="flex items-center gap-1.5">
                <span className="block h-2 w-3 rounded" style={{ background: "#fbbf24" }} />
                <span>1.0–1.5″</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="block h-2 w-3 rounded" style={{ background: "#f46530" }} />
                <span>1.5–2.0″</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="block h-2 w-3 rounded" style={{ background: "#dc2626" }} />
                <span>2.0″+</span>
              </div>
              {showLsrHail ? (
                <div className="mt-1 flex items-center gap-1.5 border-t border-slate-700 pt-1">
                  <span className="inline-block h-3 w-3 rounded-full border-[2px] border-orange-500 bg-white" />
                  <span>NWS report <span className="text-slate-500">(ring = ground truth)</span></span>
                </div>
              ) : null}
              {showLsrWind ? (
                <div className="mt-1 border-t border-slate-700 pt-1">
                  <div className="flex items-center gap-1.5">
                    <span className="block h-2 w-3 rounded" style={{ background: "#38bdf8" }} />
                    <span>30–50 mph</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="block h-2 w-3 rounded" style={{ background: "#3b82f6" }} />
                    <span>50–58 mph</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="block h-2 w-3 rounded" style={{ background: "#6366f1" }} />
                    <span>58–70 mph <span className="text-slate-500">(severe)</span></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="block h-2 w-3 rounded" style={{ background: "#7c3aed" }} />
                    <span>70+ mph</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {err || shiftErr ? (
          <div className="absolute right-3 top-3 z-10 max-w-xs rounded-md bg-red-600 px-3 py-2 text-xs font-medium text-white shadow-lg">
            ⚠ {err ?? shiftErr}
          </div>
        ) : null}

        {/* Address-search action card — open the existing pin at the
            searched house, or drop a fresh one there. */}
        {searchAction && !activePin ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-16 z-30 flex justify-center px-3">
            <div className="pointer-events-auto w-full max-w-sm rounded-xl bg-white p-3 shadow-2xl ring-1 ring-slate-200">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-sm">🔎</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{searchAction.label}</div>
                  <div className="truncate text-[11px] text-slate-500">{searchAction.sublabel}</div>
                </div>
                <button
                  onClick={() => setSearchAction(null)}
                  className="px-1 text-xl leading-none text-slate-400 hover:text-slate-700"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
              <div className="mt-2.5">
                {searchAction.resolving ? (
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs text-slate-500">
                    Checking this house…
                  </div>
                ) : searchAction.existingPin ? (
                  <button
                    onClick={() => openSearchPin(searchAction.existingPin!)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2.5 text-sm font-bold text-white hover:bg-orange-600"
                  >
                    <span>📍 Open pin</span>
                    <span className="rounded-full bg-white/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                      {STATE_STYLE[searchAction.existingPin.current_state]?.label ?? searchAction.existingPin.current_state}
                      {searchAction.existingPin.attempt_count > 0 ? ` · ${searchAction.existingPin.attempt_count}×` : ""}
                    </span>
                  </button>
                ) : isManager ? (
                  <button
                    onClick={() => void dropPinFromSearch()}
                    className="w-full rounded-lg bg-orange-500 px-3 py-2.5 text-sm font-bold text-white hover:bg-orange-600"
                  >
                    📌 Drop pin here
                  </button>
                ) : !(role && LEAD_BY_ADDRESS_ROLES.has(role)) ? (
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs text-slate-500">
                    No pin at this address yet.
                  </div>
                ) : (
                  // Oct 27, 2026: reps who can't drop pins log the lead by
                  // address instead — same approval and pay as an App.
                  <div className="space-y-1.5">
                    <div className="text-center text-xs text-slate-500">No pin at this address yet.</div>
                    <button
                      onClick={() => {
                        const q = new URLSearchParams({ manual: "1" });
                        const st = searchAction.street ?? searchAction.label;
                        if (st) q.set("street", st);
                        if (searchAction.city) q.set("city", searchAction.city);
                        if (searchAction.state) q.set("state", searchAction.state);
                        if (searchAction.zip) q.set("zip", searchAction.zip);
                        navigate(`/canvas/appt?${q.toString()}`);
                      }}
                      className="w-full rounded-lg bg-orange-500 px-3 py-2.5 text-sm font-bold text-white hover:bg-orange-600"
                    >
                      ✍️ Log a lead here
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Undo toast — bottom-center, above storm chip */}
        {undoToast ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-16 z-30 flex justify-center">
            <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-slate-900/95 px-4 py-2 text-sm text-white shadow-2xl backdrop-blur">
              {undoToast.clientUuid ? (
                <>
                  <span><strong>{undoToast.label}</strong> logged</span>
                  <button
                    onClick={undoLastVisit}
                    className="rounded-full bg-orange-500 px-3 py-0.5 text-xs font-bold uppercase tracking-wider hover:bg-orange-600"
                  >
                    Undo
                  </button>
                </>
              ) : (
                // Drop-pin or info-only toast — no undo path
                <span>{undoToast.label}</span>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Pin filter slide-up sheet */}
      {showFiltersSheet ? (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/30"
            onClick={() => setShowFiltersSheet(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-40 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pin filters</div>
                <div className="text-sm text-slate-700">{filterStates.size} of {Object.keys(STATE_STYLE).length} states visible</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setFilterStates(new Set(Object.keys(STATE_STYLE)))}
                  className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200"
                >
                  All
                </button>
                <button
                  onClick={() => setFilterStates(new Set())}
                  className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200"
                >
                  None
                </button>
                <button
                  onClick={() => setShowFiltersSheet(false)}
                  className="ml-1 text-2xl leading-none text-slate-400 hover:text-slate-700"
                >
                  ×
                </button>
              </div>
            </div>
            {/* Owner scope — RM/TL/ASM see only their own / team / area deals */}
            <div className="border-b border-slate-100 px-4 pt-4 pb-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Whose deals
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => { setScopeMode("all"); setPickedTlId(null); }}
                  className={`rounded-md border px-3 py-2 text-xs font-semibold ${
                    scopeMode === "all"
                      ? "border-orange-500 bg-orange-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  All deals
                </button>
                <button
                  onClick={() => { setScopeMode("mine"); setPickedTlId(null); }}
                  className={`rounded-md border px-3 py-2 text-xs font-semibold ${
                    scopeMode === "mine"
                      ? "border-orange-500 bg-orange-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  My deals
                </button>
                {(member?.role === "team_lead" || member?.role === "asm") ? (
                  <button
                    onClick={() => { setScopeMode("team"); setPickedTlId(null); }}
                    className={`rounded-md border px-3 py-2 text-xs font-semibold ${
                      scopeMode === "team"
                        ? "border-orange-500 bg-orange-50 text-slate-900"
                        : "border-slate-200 bg-white text-slate-500"
                    }`}
                  >
                    {member.role === "asm" ? "My area" : "My team"}
                  </button>
                ) : (
                  <span />
                )}
              </div>

              {/* ASM-only: drill into a specific TL's team */}
              {member?.role === "asm" && asmTlList.length > 0 ? (
                <div className="mt-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Or pick a team lead
                  </div>
                  <select
                    value={scopeMode === "tl" && pickedTlId ? pickedTlId : ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) { setScopeMode("all"); setPickedTlId(null); }
                      else { setScopeMode("tl"); setPickedTlId(val); }
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-xs font-semibold ${
                      scopeMode === "tl"
                        ? "border-orange-500 bg-orange-50 text-slate-900"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    <option value="">— Pick a TL to see their team —</option>
                    {asmTlList.map((tl) => (
                      <option key={tl.id} value={tl.id}>{tl.full_name}'s team</option>
                    ))}
                  </select>
                </div>
              ) : null}

              {scopeMode !== "all" ? (
                <div className="mt-2 text-[10px] text-slate-500">
                  Showing {scopedDealIds === null ? "…" : scopedDealIds.size.toLocaleString()} deal{scopedDealIds?.size === 1 ? "" : "s"} matching scope.
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-2 p-4">
              {Object.entries(STATE_STYLE).map(([state, s]) => {
                const active = filterStates.has(state);
                return (
                  <button
                    key={state}
                    onClick={() => toggleFilter(state)}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-xs font-semibold ${
                      active
                        ? "border-orange-500 bg-orange-50 text-slate-900"
                        : "border-slate-200 bg-white text-slate-500"
                    }`}
                  >
                    <span
                      className="block h-3 w-3 shrink-0 rounded-full"
                      style={{ background: s.color, border: `1.5px solid ${s.outline ?? "#fff"}` }}
                    />
                    <span className="flex-1">{s.label}</span>
                    {active ? <span className="text-orange-500">✓</span> : null}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] text-slate-500">
              Tap state pills to toggle visibility. Cooldown pins (purple in 72h window) are dimmed but still shown when Contacted is on.
            </div>
          </div>
        </>
      ) : null}

      {/* Ship 10: assignment rep-picker sheet */}
      {showAssignSheet && polygonPoints.length >= 4 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl">
          <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assign area to</div>
              <div className="text-sm text-slate-700">{pinsInside.length} pin{pinsInside.length === 1 ? "" : "s"} inside drawn shape</div>
            </div>
            <button onClick={() => setShowAssignSheet(false)} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
          </div>

          <div className="space-y-4 p-4">
            <div>
              <div className="mb-1 text-xs font-semibold text-slate-700">How long</div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setAreaForDate((d) => d ?? todayEastern())}
                    className={`rounded-full px-3 py-1 font-bold ${areaForDate ? "bg-orange-500 text-white" : "text-slate-600"}`}
                  >
                    {areaForDate ? (areaForDate === todayEastern() ? `Today · ${fmtDayShort(areaForDate)}` : `For ${fmtDayShort(areaForDate)}`) : "Today only"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAreaForDate(null)}
                    className={`rounded-full px-3 py-1 font-bold ${!areaForDate ? "bg-orange-500 text-white" : "text-slate-600"}`}
                  >
                    Standing
                  </button>
                </div>
                <span className="text-[10px] text-slate-500">
                  {areaForDate ? "A daily area: on the Today map until a manager expires it." : "Stays on the map until a manager removes it."}
                </span>
              </div>
              {areaAnchor ? (
                <div className="mt-1 text-[10px] text-slate-500">
                  Drawn around the {areaAnchor.kind === "other" ? "stop" : areaAnchor.kind === "build" ? "build" : `${areaAnchor.kind} meeting`} from the Today tab.
                </div>
              ) : null}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Area label (optional)</label>
              <input
                value={areaLabel}
                onChange={(e) => setAreaLabel(e.target.value)}
                placeholder='e.g. "Woodstock · Pioneer Dr" or "SW Cumming"'
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700">Reps ({selectedReps.size} selected)</label>
                {selectedReps.size > 1 ? (
                  <div className="flex items-center gap-1 text-[10px]">
                    <button
                      onClick={() => setShareMode("share")}
                      className={`rounded-full px-2 py-0.5 font-bold ${shareMode === "share" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600"}`}
                    >
                      Share (everyone sees all)
                    </button>
                    <button
                      onClick={() => setShareMode("split")}
                      className={`rounded-full px-2 py-0.5 font-bold ${shareMode === "split" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600"}`}
                    >
                      Split (divide pins)
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {activeRoster
                  .filter((r) => ["canvasser","restore_manager","team_lead","asm","roof_specialist"].includes(r.role))
                  .map((r) => {
                    const sel = selectedReps.has(r.id);
                    return (
                      <button
                        key={r.id}
                        onClick={() => toggleRepSelected(r.id)}
                        className={`rounded-md border px-2 py-1.5 text-left text-xs ${
                          sel ? "border-orange-500 bg-orange-50 text-orange-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <div className="font-semibold">{r.full_name}</div>
                        <div className="text-[10px] text-slate-500">{r.role}</div>
                      </button>
                    );
                  })}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Note for the rep (optional)</label>
              <textarea
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                rows={2}
                placeholder="e.g. focus on cul-de-sacs · 3 prior contacts here"
                className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            {err ? (
              <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">⚠ {err}</div>
            ) : null}
          </div>

          <div className="sticky bottom-0 flex items-center gap-2 border-t border-slate-200 bg-white p-3">
            <button
              onClick={() => setShowAssignSheet(false)}
              disabled={savingAssignment}
              className="rounded-md bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={submitAssignment}
              disabled={savingAssignment || selectedReps.size === 0}
              className="flex-1 rounded-md bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingAssignment ? "Assigning…" : `✅ Assign to ${selectedReps.size} rep${selectedReps.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      ) : null}

      {/* Bottom sheet */}
      {activePin ? (
        <PinSheet
          pin={activePin}
          visits={visits}
          loading={loadingVisits}
          submitting={submitting || reportRendering}
          stormReport={pinStormReport}
          reportRendering={reportRendering}
          onClose={() => setActivePin(null)}
          onLog={logOutcome}
          onGenerateReport={() => generateWeatherReport()}
          onRegenerateReport={() => generateWeatherReport({ regenerate: true })}
          onAppt={(notes?: string) => {
            // Tapping "App" logs an immediate contact_made (so the pin advances
            // through the lifecycle even if the canvasser bails on the form)
            // and routes to /canvas/appt with the door-side note tagged on so
            // it pre-fills the form. Notes ride the URL — small enough to fit
            // comfortably, and avoids extra plumbing.
            logOutcome("contact_made", notes).then(() => {
              const q = new URLSearchParams({ pin: activePin.id });
              if (notes) q.set("notes", notes);
              navigate(`/canvas/appt?${q.toString()}`);
            });
          }}
          pendingMedia={pendingMedia}
          onMediaChange={setPendingMedia}
        />
      ) : null}

      {/* Drop-session review modal — opens after the manager taps "End" */}
      {reviewSession ? (
        <DropSessionReviewModal
          sessionId={reviewSession.id}
          pinCount={reviewSession.pin_count}
          savedNow={reviewSession.savedNow}
          skipped={reviewSession.skipped}
          dedupeUnavailable={reviewSession.dedupeUnavailable}
          onClose={() => setReviewSession(null)}
          onJumpToPin={(p) => {
            const m = mapRef.current;
            if (m) m.flyTo({ center: [p.lon, p.lat], zoom: 18, speed: 1.5 });
            const pin = pins.find((x) => x.id === p.id);
            if (pin) setActivePin(pin);
          }}
          onPinDeleted={(pinId) => {
            setPins((prev) => prev.filter((x) => x.id !== pinId));
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function PinSheet({
  pin, visits, submitting, onClose, onLog, onGenerateReport, onRegenerateReport, onAppt,
  stormReport, reportRendering,
  pendingMedia, onMediaChange,
}: {
  pin: KnockPin;
  visits: CanvasVisit[];
  loading: boolean;
  submitting: boolean;
  stormReport: {
    short_id: string;
    pdf_storage_path: string | null;
    storm_date: string;
    property_max_in: number | null;
    data_snapshot: any;
  } | null;
  reportRendering: boolean;
  onClose: () => void;
  onLog: (outcome: "no_answer" | "contact_made" | "rent" | "follow_up", notes?: string) => Promise<void>;
  onGenerateReport: () => void;
  onRegenerateReport: () => void;
  onAppt: (notes?: string) => void;
  pendingMedia: CapturedMedia;
  onMediaChange: (next: CapturedMedia) => void;
}) {
  const style = STATE_STYLE[pin.current_state] ?? STATE_STYLE.open;
  // Most recent contact_made visit drives both the "Contacted previously" note
  // and the Follow Up button visibility. The 72h cooldown was removed May 11,
  // 2026 — we no longer gate the pin or dim the marker; the note is purely
  // informational so the next canvasser walks up with context.
  const lastContactedVisit = visits.find((v) => v.outcome === "contact_made");
  const hasContactHistory = pin.current_state === "contacted" || lastContactedVisit !== undefined;
  const lastContactRelative = lastContactedVisit
    ? formatRelativeAge(new Date(lastContactedVisit.visited_at))
    : null;

  // Fix #1 (May 11, 2026): outcome buttons hide when the pin is already past
  // the "knock the door" lifecycle stage. After Appt set, the canvasser
  // shouldn't be re-logging No Answer / Contact Made / Rent / App — the deal
  // has moved on. Same for lead / claim / built / past client / DNK. The
  // canvasser still sees the pin context + knock history + storm-report
  // controls in the sheet, just not the four-button grid. LOCKED_PIN_STATES
  // is module-scoped above so the Set isn't rebuilt every render.
  const isLocked = LOCKED_PIN_STATES.has(pin.current_state);

  // Fix #4 (May 11, 2026): notes box on the bottom sheet. Saves with the
  // next outcome log (passed through to logOutcome → canvas_visits.notes).
  // Cleared on pin change AND immediately on each submit click (the parent
  // re-sets activePin with the same id after a successful log, so the
  // pin.id-keyed effect alone wouldn't fire — we'd otherwise show "ghost"
  // text in the textarea after submit). The submitHandler below wraps each
  // outcome to clear-then-call, capturing noteDraft at click time.
  const [noteDraft, setNoteDraft] = useState("");
  useEffect(() => { setNoteDraft(""); }, [pin.id]);
  const handleOutcome = (outcome: "no_answer" | "contact_made" | "rent" | "follow_up") => {
    const n = noteDraft.trim() || undefined;
    setNoteDraft("");
    return onLog(outcome, n);
  };
  const handleAppt = () => {
    const n = noteDraft.trim() || undefined;
    setNoteDraft("");
    onAppt(n);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-20 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl">
      <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <span
            className="block h-3 w-3 rounded-full"
            style={{ background: style.color, border: `1.5px solid ${style.outline ?? "#fff"}` }}
          />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{style.label}</span>
          <span className="text-xs text-slate-400">· Attempt #{(pin.attempt_count ?? 0) + 1}</span>
          {(pin.prior_appointment_count ?? 0) > 0 ? (
            <span
              title="Previously scheduled an appointment — warm re-approach"
              className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-300"
            >
              ★ Warm
            </span>
          ) : null}
        </div>
        <button onClick={onClose} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
      </div>

      <div className="p-4">
        {pin.homeowner_name ? (
          <div className="text-base font-bold text-slate-900">{titleCaseOwner(pin.homeowner_name) ?? pin.homeowner_name}</div>
        ) : null}
        <div className={pin.homeowner_name ? "text-sm text-slate-700" : "text-base font-semibold text-slate-900"}>
          {pin.property_street}
        </div>
        <div className="text-sm text-slate-600">
          {[pin.property_city, pin.property_state, pin.property_zip].filter(Boolean).join(", ")}
        </div>

        {/* Property intel (parcel-derived, customizable via ⚙) */}
        <PinIntelSection parcelId={pin.parcel_id} />

        {/* Neighborhood insurance results (Oct 27, 2026): per carrier, how
            the claims near this house went at the first adjuster meeting and
            on reinspection. Counts only, no names. */}
        <PinNeighborhoodInsurance lat={pin.lat} lon={pin.lon} excludeDealId={pin.deal_id} />

        {hasContactHistory && lastContactedVisit ? (
          <div className="mt-3 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900">
            <div className="font-semibold">👋 Contacted previously</div>
            <div className="mt-0.5 text-purple-800">
              {lastContactRelative
                ? `Last contact ${lastContactRelative}.`
                : "Last contact recorded in knock history."}
              {lastContactedVisit.notes ? ` "${lastContactedVisit.notes}"` : ""}
              {" "}If you've come back on a scheduled callback, use <strong>Follow Up</strong>.
            </div>
          </div>
        ) : null}

        {(pin.prior_appointment_count ?? 0) > 0 ? (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="font-semibold">★ Warm lead — previously scheduled an appointment</div>
            <div className="mt-0.5 text-amber-800">
              {pin.last_appointment_at
                ? `Scheduled ${monthsAgoLabel(pin.last_appointment_at)} but didn't convert. `
                : "Scheduled previously but didn't convert. "}
              They were once receptive — re-approach with fresh context.
              {(pin.prior_appointment_count ?? 0) > 1
                ? ` (Scheduled ${pin.prior_appointment_count}× before.)`
                : ""}
            </div>
          </div>
        ) : null}

        {/* Knock history */}
        {visits.length > 0 ? (
          <div className="mt-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Knock history</div>
            <div className="mt-1 space-y-1.5">
              {visits.slice(0, 4).map((v) => (
                <div key={v.id} className="text-xs">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-mono text-slate-400">
                      {new Date(v.visited_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    </span>
                    <span className="font-semibold text-slate-700">{labelForOutcome(v.outcome)}</span>
                    {v.notes ? <span className="text-slate-500">— {v.notes}</span> : null}
                  </div>
                  {v.voice_note_url || v.photo_storage_path ? (
                    <VisitMediaInline
                      voicePath={v.voice_note_url}
                      photoPath={v.photo_storage_path}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Notes (Fix #4, May 11, 2026) — saves with the next outcome log */}
        {!isLocked ? (
          <div className="mt-4">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Notes (optional)
            </label>
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={2}
              placeholder="Anything worth remembering for next time…"
              className="w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              disabled={submitting}
            />
          </div>
        ) : null}

        {/* Status banner when locked (Fix #1, May 11, 2026) — appt set / lead /
            claim / built / past client / DNK all hide the outcome grid. The
            canvasser still sees pin context + knock history + weather report
            controls above; this just removes the now-redundant 4 buttons and
            tells them why. */}
        {isLocked ? (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
            <div className="flex items-start gap-2">
              <span className="text-lg leading-none">✓</span>
              <div>
                <div className="font-semibold text-slate-900">{style.label} — no further outcomes needed</div>
                <div className="mt-0.5 text-xs text-slate-600">
                  {pin.current_state === "appt_scheduled"
                    ? "Appointment is on the books. The RM will reach out to schedule. If something changed (rescheduled, cancelled), update on the linked deal."
                    : pin.current_state === "lead"
                    ? "Drone report approved — the RM is pursuing the homeowner to call their carrier. Updates happen on the deal."
                    : pin.current_state === "claim_called"
                    ? "Claim has been filed with the carrier. Updates flow through the deal page."
                    : pin.current_state === "built"
                    ? "Roof has been built. This will promote to a Past Client after 12 months."
                    : pin.current_state === "legacy_client"
                    ? "Past Bardin client — we did this roof. Use as a referral / warm intro for the neighborhood, not a fresh knock."
                    : pin.current_state === "do_not_knock"
                    ? "Homeowner has asked not to be contacted. Do not knock."
                    : "This pin has moved past the door-knock phase."}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* 4 outcome buttons — all use handleOutcome/handleAppt which
                capture noteDraft, clear the textarea, then call onLog. */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <OutcomeButton
                label="No Answer" icon="🚪"
                onClick={() => handleOutcome("no_answer")}
                disabled={submitting}
                tone="slate"
              />
              <OutcomeButton
                label="Contact Made" icon="👋"
                onClick={() => handleOutcome("contact_made")}
                disabled={submitting}
                tone="purple"
              />
              <OutcomeButton
                label="Rent" icon="🏠"
                onClick={() => handleOutcome("rent")}
                disabled={submitting}
                tone="slate"
              />
              <OutcomeButton
                label="App" icon="✅"
                onClick={() => handleAppt()}
                disabled={submitting}
                tone="orange"
              />
            </div>

            {/* Follow Up — one-tap contact_made for re-visits. Fix #5 (May 11,
                2026): now logs as `contact_made` directly so the canvasser
                doesn't have to tap Contact Made first then later tap Follow
                Up — the button IS Contact Made, just labeled for re-visit
                context. Still gated on hasContactHistory so it only shows on
                pins the team has talked to before. */}
            {hasContactHistory ? (
              <button
                onClick={() => handleOutcome("contact_made")}
                disabled={submitting}
                className="mt-3 w-full rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 px-3 py-2.5 text-left text-sm text-purple-900 hover:bg-purple-100 disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🔁</span>
                  <div className="flex-1">
                    <div className="font-semibold">Follow Up</div>
                    <div className="text-xs text-purple-700">
                      Coming back on a prior contact (scheduled callback, dropped-off info, etc.). Logs as Contact Made.
                    </div>
                  </div>
                </div>
              </button>
            ) : null}
          </>
        )}

        {/* Storm report row — three states based on what's saved on this pin */}
        {stormReport && stormReport.pdf_storage_path ? (
          // STATE A: report exists with PDF — show "View" + "Refresh"
          <div className="mt-3 flex gap-2">
            <button
              onClick={onGenerateReport}
              disabled={submitting}
              className="flex-1 rounded-lg border border-orange-200 bg-gradient-to-b from-orange-50 to-orange-100 px-3 py-2.5 text-left text-sm text-orange-900 hover:from-orange-100 hover:to-orange-200 disabled:opacity-50"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">⛈️</span>
                <div className="flex-1">
                  <div className="font-semibold">View weather report</div>
                  <div className="text-[10px] text-orange-700">
                    {stormReport.property_max_in ? `${Number(stormReport.property_max_in).toFixed(2)}″ at property` : "Saved on file"}
                    {" · "}{new Date(stormReport.storm_date + "T12:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    {" · "}<span className="font-mono">{stormReport.short_id}</span>
                  </div>
                </div>
                <span className="text-orange-500">↗</span>
              </div>
            </button>
            <button
              onClick={onRegenerateReport}
              disabled={submitting}
              title="Regenerate with latest data"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              ↻
            </button>
          </div>
        ) : reportRendering ? (
          // STATE B: rendering in progress
          <div className="mt-3 w-full rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-orange-900">
            <div className="flex items-center gap-2">
              <span className="text-lg">⏳</span>
              <div className="flex-1">
                <div className="font-semibold">Generating storm report…</div>
                <div className="text-[10px] text-orange-700">Pulling NEXRAD + NWS spotter data, rendering PDF.</div>
              </div>
            </div>
          </div>
        ) : (
          // STATE C: no report yet
          <button
            onClick={onGenerateReport}
            disabled={submitting}
            className="mt-3 w-full rounded-lg border border-orange-200 bg-gradient-to-b from-orange-50 to-orange-100 px-3 py-2.5 text-left text-sm text-orange-900 hover:from-orange-100 hover:to-orange-200 disabled:opacity-50"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">⛈️</span>
              <div className="flex-1">
                <div className="font-semibold">Generate weather report</div>
                <div className="text-xs text-orange-700">
                  Pulls 365d of NEXRAD + NWS spotter data, saves PDF to this pin so you can show the homeowner.
                </div>
              </div>
              <span className="text-orange-500">›</span>
            </div>
          </button>
        )}

        {/* Voice + photo extras row — staged to attach to the next outcome */}
        <MediaExtrasRow value={pendingMedia} onChange={onMediaChange} disabled={submitting} />

        {pin.deal_id ? (
          <button
            onClick={() => window.open(`/deal/${pin.deal_id}`, "_blank")}
            className="mt-3 w-full rounded-md bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
          >
            Open linked deal →
          </button>
        ) : null}
      </div>
    </div>
  );
}

function OutcomeButton({
  label, icon, onClick, disabled, tone,
}: {
  label: string; icon: string; onClick: () => void; disabled?: boolean;
  tone: "orange" | "purple" | "slate";
}) {
  const cls =
    tone === "orange" ? "bg-orange-500 text-white hover:bg-orange-600" :
    tone === "purple" ? "bg-purple-600 text-white hover:bg-purple-700" :
    "bg-slate-100 text-slate-700 hover:bg-slate-200";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-1 rounded-xl py-4 text-sm font-semibold ${cls} disabled:cursor-not-allowed disabled:opacity-50`}
    >
      <span className="text-2xl">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function labelForOutcome(o: string): string {
  switch (o) {
    case "no_answer":   return "No Answer";
    case "contact_made":return "Contact Made";
    case "rent":        return "Rent";
    case "appt_set":    return "Appt Set";
    case "follow_up":   return "Follow Up";
    case "wrong_address": return "Wrong Address";
    case "do_not_knock":  return "Do Not Knock";
    default: return o;
  }
}

// Human-friendly relative-age label for the "Contacted previously" note.
// Reads "12 minutes ago", "5 hours ago", "yesterday", "3 days ago",
// "2 weeks ago", or "Mar 14" once the timestamp is far enough back that
// week-counts stop being useful.
function formatRelativeAge(d: Date): string {
  const ms = Date.now() - d.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk} week${wk === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Human-friendly elapsed label for the prior-appointment callout.
// "3 months ago", "8 months ago", "1 year ago", "over a year ago".
function monthsAgoLabel(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "previously";
  const days = Math.max(0, (Date.now() - then) / 86_400_000);
  const months = Math.floor(days / 30);
  if (months < 1) return "less than a month ago";
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? "about a year ago" : `${years} years ago`;
}

// Quick win #5 — enrich each polygon feature with `pin_count`, the number
// of knock_pins inside the polygon. Mutates a shallow copy of the GeoJSON
// so React/Mapbox treats it as new data.
function enrichAreasWithPinCount(geo: any, pins: KnockPin[]) {
  if (!geo || !geo.features || geo.features.length === 0) return geo;
  const out = { ...geo, features: geo.features.map((f: any) => {
    const ring = (f.geometry?.coordinates ?? [])[0];
    if (!Array.isArray(ring) || ring.length < 3) return f;
    let count = 0;
    for (const p of pins) {
      if (pointInRing(p.lon, p.lat, ring)) count++;
    }
    return { ...f, properties: { ...(f.properties ?? {}), pin_count: count } };
  })};
  return out;
}

// Ray-casting point-in-polygon. ring = [[lon,lat], ...] in any winding.
// Renamed from pointInPolygon (May 2, 2026) to disambiguate from the tuple-
// based pointInPolygon defined further down — both signatures are in use.
function pointInRing(lon: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      (yi > lat) !== (yj > lat)
      && lon < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// Haversine distance in feet between two GPS coordinate pairs.
function haversineFeet(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000; // meters
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const sa = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(sa))) * 3.28084;
}

// Compass bearing 0-360 from point A to point B (0 = North, 90 = East).
function bearingDeg(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const φ1 = toRad(aLat), φ2 = toRad(bLat);
  const Δλ = toRad(bLon - aLon);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Compass arrow pointing from rep → pin. 8-way rounding so the user sees a
// stable indicator instead of one that twitches with every GPS update.
function arrowForBearing(deg: number): string {
  const idx = Math.round(deg / 45) % 8;
  return ["↑","↗","→","↘","↓","↙","←","↖"][idx];
}

// Day-stat tile used on the on-shift counter row. Mirrors the prototype's
// big-number / small-label format. Equal-flex so the four tiles split the
// available width cleanly on any phone. Tappable: the active tile filters
// the map to matching pins (or breadcrumb).
function ShiftStatTile({
  value, label, active, onClick,
}: {
  value: number | string;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const cls = active
    ? "bg-orange-500 text-white ring-1 ring-orange-300"
    : "bg-slate-800/70 text-white hover:bg-slate-700/80";
  return (
    <button
      onClick={onClick}
      type="button"
      className={`flex flex-1 flex-col items-center justify-center rounded-md px-1 py-1 text-center transition ${cls}`}
      aria-pressed={!!active}
    >
      <div className="text-base font-extrabold leading-none tabular-nums">{value}</div>
      <div className={`mt-0.5 text-[9px] font-semibold uppercase tracking-wider ${active ? "text-orange-100" : "text-slate-400"}`}>{label}</div>
    </button>
  );
}

// Compact icon-only button used in the top bar.
function IconButton({
  icon, label, active, onClick,
}: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`flex h-9 w-9 items-center justify-center rounded-md text-base transition-colors ${
        active ? "bg-orange-500 text-white shadow" : "bg-slate-800 text-slate-200 hover:bg-slate-700"
      }`}
    >
      {icon}
    </button>
  );
}

function fmtDaysLabel(d: number): string {
  if (d >= 365) return "1 year";
  if (d >= 180) return "6 months";
  if (d >= 90)  return "3 months";
  if (d >= 30)  return "30 days";
  return `${d} days`;
}
function fmtDaysShort(d: number): string {
  if (d >= 365) return "1y";
  if (d >= 180) return "6mo";
  if (d >= 30)  return `${Math.round(d/30)}mo`;
  return `${d}d`;
}

// ── Geometry helpers (Ship 10) ──────────────────────────────────────────

// Convert the user-drawn point list into a GeoJSON FeatureCollection.
// While the user is still dragging, we render as a LineString (no fill) so
// they see the trace. Once released, we have a closed Polygon (filled).
function polygonToGeoJson(points: Array<[number, number]>, drawing: boolean): any {
  if (drawing) {
    return {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        geometry: { type: "LineString", coordinates: points },
        properties: {},
      }],
    };
  }
  return {
    type: "FeatureCollection",
    features: [{
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [points] },
      properties: {},
    }],
  };
}

// Standard ray-casting point-in-polygon. polygon is [[lon, lat], …, first==last].
function pointInPolygon(p: [number, number], polygon: Array<[number, number]>): boolean {
  const [x, y] = p;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect = ((yi > y) !== (yj > y)) &&
                      (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
